import { getStudioAccessToken, getStudioUserId } from '@/services/auth-provider'
import { cogoPlatformApiUrl, withCogoDevProxyHeaders } from '@/services/cogo-platform-config'
import {
  dropBlockBaseline,
  dropProjectBlockBaselines,
  getBaselineGeneration,
  isHeaderSafeToken,
  refreshBlockBaseline,
  replaceProjectBlockBaselines,
} from '@/services/block-baselines'
import type { LoadedApp } from '@/features/page-editor/store/types'
import { usePreviewTestingStore } from '@/features/preview/store/preview-testing-store'
import { ThumbnailService } from '@/services/thumbnail-service'
import {
  chatGeneratedApplicationDraftMarkerForReceipt,
  chatGeneratedApplicationDraftMarkerMatchesReceipt,
  finalizeChatGeneratedApplicationDraft,
  type ChatGeneratedApplicationDraftPreparationReceipt,
} from '@/features/chat/services/chat-generated-application-draft-api'
import { hashCanonicalContent } from '@cogo/schema/canonical-content-hash'
import {
  preservesSelectedDesignSystemRelease,
  readStudioProjectPlan,
  type StudioProjectPlan,
} from '@/features/project/lib/studio-project-plan'
import {
  buildReportWorkspaceModel,
  readReportWorkspaceEvidence,
} from '@/features/report-workspace/report-workspace-model'
import {
  getRuntimeActionGrounding,
  type RuntimeActionGroundingKind,
} from '@cogo/action-registry'
import {
  parseOntologyReleaseSelection,
  type OntologyReleaseSelection,
} from '@cogo/ontology-lifecycle'
import { compileCogoDesignContract } from '@cogo/headless/cogo-design-compiler'
import { compileCogoRenderPack, encodeRenderPack } from '@cogo/runtime-v2'
import {
  evaluateApplicationSourceSufficiencyGate,
  type StudioApplicationSourceSufficiencyGate,
} from '@/features/source-assessment/source-assessment-contract'
import {
  evaluateApplicationOntologyV2Gate,
  type StudioApplicationOntologyV2Gate,
} from '@/features/application-ontology/application-ontology-v2-contract'
import {
  decodeRenderPackPublishPayload,
  type RenderPackBytesPayload,
} from '@cogo/iframe-protocol'
import {
  receiptBoundApplicationSupplement,
  receiptBoundBlockSupplement,
} from '@/services/receipt-bound-application-supplement'
import {
  studioBlocksArtifactHash,
  type StudioBlockRowInput,
} from '../lib/canonical-artifact-hash'
import {
  hasFailedGenerationFinalization,
  readProjectGenerationMarker,
  withFailedGenerationFinalization,
  withoutGenerationFinalization,
  withProjectGenerationMarker,
  type ProjectGenerationMarker,
} from '@/features/project/lib/project-generation-durability'
import {
  withLibraryStructureSelection,
  type LibraryStructureSelection,
} from '@/features/project/lib/library-structure-selection'
import {
  withDesignSystemPackReleaseLineage,
  type StudioDesignSystemPackReleaseLineage,
} from '@/features/design-system/lib/design-system-pack-release-binding'
import { APPLICATION_ONTOLOGY_GENERATION_BINDING_SETTINGS_KEY } from '@/features/application-ontology/application-generation-persistence'
import {
  readProjectDesignSystemGenerationLineage,
  withProjectDesignSystemGenerationLineage,
  type StudioProjectDesignSystemGenerationLineage,
} from '@/features/design-system/lib/project-design-system-generation-binding'
import {
  fingerprintAppGenerationSpec,
  isCanonicalAppGenerationSpecRef,
  type AppGenerationJobServerProbe,
  type AppSpecFull,
} from '@/services/block-api-service'
import type { StudioGenerationAuthorityEnvelope } from '@/features/generation-authority/generation-authority-envelope'
import { validateStudioGenerationAuthorityEnvelope } from '@/features/generation-authority/generation-authority-envelope'
import {
  beginStudioSettingsWriteOutcome,
  classifyStudioSettingsWriteFailure,
  publishStudioSettingsWriteOutcome,
} from '@/services/studio-settings-write-outcome'

function platformApiUrl(path: string): string {
  return cogoPlatformApiUrl(path)
}

function platformHeaders(init?: HeadersInit): Headers {
  return withCogoDevProxyHeaders(init)
}

async function getUserJwt(): Promise<string | null> {
  return getStudioAccessToken()
}

type ProjectWorkerSessionAccessTokenResolver = () => string | null | undefined

let projectWorkerSessionAccessTokenResolver: ProjectWorkerSessionAccessTokenResolver = () => null

/**
 * Installs the Studio store's live session reader without importing the store
 * into this module (project-api is already imported by project-slice).
 */
export function setProjectWorkerSessionAccessTokenResolver(
  resolver: ProjectWorkerSessionAccessTokenResolver,
): void {
  projectWorkerSessionAccessTokenResolver = resolver
}

async function requireUserJwt(): Promise<string> {
  // Keep the provider lookup first so its refresh path remains authoritative.
  // The Studio store can nevertheless already hold the authenticated session
  // while the provider's independent storage lookup returns null (the live
  // appliance project-open failure). The live store resolver is therefore a
  // central fallback for every project Worker operation, not a per-call token.
  const jwt = await getUserJwt()
  if (jwt) return jwt
  const liveSessionJwt = projectWorkerSessionAccessTokenResolver()?.trim() || null
  if (liveSessionJwt) return liveSessionJwt
  throw new Error('Not authenticated')
}

function decodeJwtSub(jwt: string): string | null {
  try {
    const [, payload] = jwt.split('.')
    if (!payload) return null
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=')
    const parsed = JSON.parse(atob(padded)) as { sub?: string }
    return parsed.sub ?? null
  } catch {
    return null
  }
}

async function getUserIdForPublish(): Promise<string> {
  const jwt = await requireUserJwt()
  const sub = decodeJwtSub(jwt)
  if (sub) return sub

  const userId = await getStudioUserId()
  if (!userId) throw new Error('Not authenticated')
  return userId
}

/**
 * Worker transport error preserving the HTTP status + parsed body (F11).
 * Before this, every non-OK response was flattened into a bare Error — a
 * 409 conflict was indistinguishable from a 500. The MESSAGE is built with
 * the exact same precedence as the old bare Error, and the class extends
 * Error, so every existing caller's observable behavior (instanceof Error,
 * .message reads) is unchanged.
 */
export class WorkerRequestError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, body: unknown) {
    const parsed = (body ?? {}) as { error?: string; message?: string }
    super(parsed.error ?? parsed.message ?? `Worker request failed: ${status}`)
    this.name = 'WorkerRequestError'
    this.status = status
    this.body = body
  }
}

/** CS-8 — bound every worker request so a wedged auth lock
 * (requireUserJwt → navigator.locks contention) or a black-holed connection
 * (no RST) cannot leave a save in flight forever. An unbounded hang pins
 * inFlightSaves / isSaving true for the whole session → a permanent
 * "unsaved changes" beforeunload nag and a misfiring conflict-resolution
 * confirm gate over a quiescent editor. 30s is generously above a slow but
 * live save while still settling a genuine hang in bounded time; on timeout
 * the request REJECTS so the standard failure re-queue runs and the in-flight
 * brackets unwind. The whole request (auth + fetch) is raced against the
 * deadline so a wedged getSession() is covered too, not just the fetch. */
const SAVE_REQUEST_TIMEOUT_MS = 30_000

/** Reads get their own, shorter deadline.
 *
 * 30s is the budget for a WRITE: losing a save is expensive, so it is worth
 * waiting out a slow one. A read has the opposite economics — nothing is lost
 * by giving up, and a screen that is still waiting on a list after 20s has
 * already failed the person looking at it. Sharing the save budget meant a
 * wedged read held the screen on a skeleton for a full 30s before any error
 * or retry could appear (operator measurement, 2026-07-31). The read deadline
 * still sits well above a slow-but-live list read, so this only changes when
 * a hung read is REPORTED, never whether a live one succeeds. */
const READ_REQUEST_TIMEOUT_MS = 20_000

async function workerRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | null = null
  const method = (init.method ?? 'GET').toUpperCase()
  const timeoutMs =
    method === 'GET' || method === 'HEAD'
      ? READ_REQUEST_TIMEOUT_MS
      : SAVE_REQUEST_TIMEOUT_MS
  // A standalone deadline promise that REJECTS when the timer fires. This is
  // raced against the whole request (auth + fetch) so the timeout settles even
  // if the transport ignores the abort signal — it also aborts the controller
  // so a signal-honoring fetch is cancelled for real (no leaked connection).
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort()
      reject(new Error(`Worker request timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })

  const send = async (): Promise<T> => {
    const jwt = await requireUserJwt()
    const headers = platformHeaders(init.headers)
    headers.set('Authorization', `Bearer ${jwt}`)
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    const res = await fetch(platformApiUrl(path), {
      ...init,
      headers,
      signal: init.signal ?? controller.signal,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new WorkerRequestError(res.status, err)
    }
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  try {
    // CS-8 — race the request against the deadline. A wedged
    // requireUserJwt() (navigator.locks contention) or a black-holed fetch
    // (no RST) loses to the deadline and rejects in bounded time, so the
    // standard failure re-queue runs and inFlightSaves / isSaving unwind —
    // instead of pinning the dirty guard for the whole session.
    return await Promise.race([send(), deadline])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** All new blocks are created with v2 schema format */
const SCHEMA_VERSION = '3.0' as const

/**
 * studio_blocks owns block-local ActionFlow declarations, while LoadedApp also
 * has an independent application-level `flows` partition.  A whole-project
 * PUT has only block rows available, so persist the latter as reserved
 * first-row metadata instead of overwriting that block's own `flow` value.
 *
 * The key starts with `_` so older readers already skip it as non-executable
 * flow metadata.  Current readers remove it from the block facade and restore
 * the exact application partition.  This keeps the established first-block
 * storage anchor without changing flow ownership or the Worker schema.
 */
const PROJECT_FLOW_PARTITION_KEY = '_cogoProjectFlowPartition'
const PROJECT_FLOW_PARTITION_SCHEMA_VERSION = 'cogo.project-flow-partition.v1'

export const COGO_DNS_LABEL_MAX_LENGTH = 63

export function toDnsSafeSlug(slug: string): string {
  const normalized = slug
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  if (normalized.length <= COGO_DNS_LABEL_MAX_LENGTH) return normalized || 'app'

  const suffixMatch = normalized.match(/-([a-z0-9]{6,12})$/)
  if (suffixMatch) {
    const suffix = suffixMatch[1]
    const base = normalized.slice(0, normalized.length - suffix.length - 1)
    const maxBaseLength = COGO_DNS_LABEL_MAX_LENGTH - suffix.length - 1
    const safeBase = base.slice(0, maxBaseLength).replace(/-+$/g, '') || 'app'
    return `${safeBase}-${suffix}`
  }

  return normalized.slice(0, COGO_DNS_LABEL_MAX_LENGTH).replace(/-+$/g, '') || 'app'
}

export function buildCogoLiveHostname(slug: string): string {
  return `${toDnsSafeSlug(slug)}.cogo.xyz`
}

export function buildCogoLiveUrl(slug: string): string {
  return `https://${buildCogoLiveHostname(slug)}`
}

// ---------------------------------------------------------------------------
// Quality Gate types (Edge Function response shape)
// ---------------------------------------------------------------------------

export interface QualityGateApiResponse {
  ok: boolean
  rejected?: boolean
  grade: string
  score: number
  bddPassRate: number
  version?: number
  reasons?: string[]
  publishReadinessGate?: StudioPublishReadinessGate
  failedBlocks?: Array<{
    blockName: string
    score: number
    grade: string
    issues: Array<{ stage: string; level: string; file: string; rule: string; message: string }>
  }>
  failedBddScenarios?: string[]
  crossBlockIssues?: Array<{ stage: string; level: string; file: string; rule: string; message: string }>
  versionDiff?: { previousScore: number; scoreDiff: number } | null
}

export interface StudioPublishReadinessGate {
  schemaVersion: 'studio-publish-readiness-gate.v1'
  ok: boolean
  previewReady: boolean
  publishReady: boolean
  packagingMode: 'static' | 'dynamic' | 'hybrid' | 'unknown'
  securityProfile: 'standard' | 'enterprise' | 'financial'
  reasons: string[]
  missingSecurityEvidence: string[]
  requiredSecurityEvidence: string[]
  missingProductionAdapterEvidence: string[]
  proofReadinessGate: StudioProofReadinessGate
  endUserProofGate: StudioEndUserProofGate
  renderPackPackagingGate: StudioRenderPackPackagingGate
  productionAdapterGate: StudioProductionAdapterGate
  targetMatrixGate: StudioTargetMatrixGate
  actionFlowReadinessGate: StudioContractReadinessGate
  reportExportReadinessGate: StudioContractReadinessGate
  ontologyMatchingReadinessGate: StudioOntologyMatchingReadinessGate
  ontologyOperatingModelGate: StudioOntologyOperatingModelGate
  applicationSourceSufficiencyGate: StudioApplicationSourceSufficiencyGate
  applicationOntologyV2Gate: StudioApplicationOntologyV2Gate
  generatedAppStatus?: string
  rendererTargets?: Record<string, string>
}

export type StudioIntegrationReadinessStatus = 'ready' | 'gap' | 'fallback' | 'invalid' | 'not-required'

export interface StudioDatasourceReadinessSidecar {
  schemaVersion: 'studio-datasource-readiness-sidecar.v1'
  title: string
  provenance: {
    contractVersion: string
    sourceRoute: string
    sourceVersion: string
    inputDigest?: string
    responseDigest?: string
    durableRef?: string
  }
  projectBinding: {
    dependencyDeclared: boolean
    projectId: string | null
    correlationStatus: 'linked' | 'unlinked'
  }
  readiness: {
    status: Exclude<StudioIntegrationReadinessStatus, 'not-required'>
  }
  publishReadiness: {
    allowed: boolean
    blockers: string[]
  }
  execution: {
    engine: string
    runs: unknown[]
  }
  summaryRows: Array<{
    label: string
    value: string
    status: string
  }>
  artifactRefs: string[]
  importQuarantine?: {
    schemaVersion: 'studio-datasource-readiness-import-quarantine.v1'
    state: 'quarantined'
    reasonCode: 'imported-sidecar-requires-destination-correlation'
    sourceProjectIdClaim: string
  }
}

type StudioDatasourceReadinessImportQuarantine = NonNullable<
  StudioDatasourceReadinessSidecar['importQuarantine']
>

interface StudioDatasourceReadinessResolution {
  sidecar: StudioDatasourceReadinessSidecar | null
  importQuarantine: StudioDatasourceReadinessImportQuarantine | null
  malformedImportQuarantine: boolean
  conflictingProjectIds: string[]
  conflictingCurrentProjectPayload: boolean
  malformedCurrentProjectSidecar: boolean
}

function datasourceReadinessJsonEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((entry, index) => datasourceReadinessJsonEqual(entry, right[index]))
  }
  const leftRecord = readRecord(left)
  const rightRecord = readRecord(right)
  if (!leftRecord || !rightRecord) return false
  const leftKeys = Object.keys(leftRecord).sort()
  const rightKeys = Object.keys(rightRecord).sort()
  return leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) =>
        key === rightKeys[index] &&
        datasourceReadinessJsonEqual(leftRecord[key], rightRecord[key]),
    )
}

function parseStudioDatasourceReadinessSidecar(
  value: unknown,
): StudioDatasourceReadinessSidecar | null {
  const candidate = readRecord(value)
  if (candidate?.schemaVersion !== 'studio-datasource-readiness-sidecar.v1') return null

  const title = readString(candidate.title)
  const provenance = readRecord(candidate.provenance)
  const contractVersion = readString(provenance?.contractVersion)
  const sourceRoute = readString(provenance?.sourceRoute)
  const sourceVersion = readString(provenance?.sourceVersion)
  const inputDigest = readString(provenance?.inputDigest)
  const responseDigest = readString(provenance?.responseDigest)
  const durableRef = readString(provenance?.durableRef)
  if (
    !title ||
    !contractVersion ||
    !sourceRoute ||
    !sourceVersion ||
    (!inputDigest && !responseDigest && !durableRef)
  ) return null

  const projectBinding = readRecord(candidate.projectBinding)
  const dependencyDeclared = projectBinding?.dependencyDeclared
  const projectIdValue = projectBinding?.projectId
  const projectId = readString(projectIdValue) ?? null
  const correlationStatus = projectBinding?.correlationStatus
  if (
    typeof dependencyDeclared !== 'boolean' ||
    (
      projectIdValue != null &&
      (
        typeof projectIdValue !== 'string' ||
        projectIdValue.length === 0 ||
        projectIdValue !== projectIdValue.trim()
      )
    ) ||
    (correlationStatus !== 'linked' && correlationStatus !== 'unlinked') ||
    (correlationStatus === 'linked' && !projectId) ||
    (correlationStatus === 'unlinked' && projectId !== null)
  ) return null

  const readiness = readRecord(candidate.readiness)
  const readinessStatus = readiness?.status
  if (
    readinessStatus !== 'ready' &&
    readinessStatus !== 'gap' &&
    readinessStatus !== 'fallback' &&
    readinessStatus !== 'invalid'
  ) return null

  const publishReadiness = readRecord(candidate.publishReadiness)
  if (typeof publishReadiness?.allowed !== 'boolean') return null
  const blockerValues = publishReadiness.blockers
  if (
    !Array.isArray(blockerValues) ||
    blockerValues.some(
      (blocker) => typeof blocker !== 'string' || blocker.trim().length === 0,
    )
  ) return null
  const blockers = blockerValues as string[]

  const execution = readRecord(candidate.execution)
  const engine = readString(execution?.engine)
  if (!engine || !Array.isArray(execution?.runs)) return null

  const summaryRows = readArray(candidate.summaryRows).map((entry) => {
    const row = readRecord(entry)
    const label = readString(row?.label)
    const rowValue = readString(row?.value)
    const status = readString(row?.status)
    return label && rowValue && status ? { label, value: rowValue, status } : null
  })
  if (summaryRows.length === 0 || summaryRows.some((row) => row === null)) return null
  const artifactRefs = readStringList(candidate.artifactRefs)
  if (artifactRefs.length === 0) return null

  return {
    schemaVersion: 'studio-datasource-readiness-sidecar.v1',
    title,
    provenance: {
      contractVersion,
      sourceRoute,
      sourceVersion,
      ...(inputDigest ? { inputDigest } : {}),
      ...(responseDigest ? { responseDigest } : {}),
      ...(durableRef ? { durableRef } : {}),
    },
    projectBinding: {
      dependencyDeclared,
      projectId,
      correlationStatus,
    },
    readiness: { status: readinessStatus },
    publishReadiness: {
      allowed: publishReadiness.allowed,
      blockers,
    },
    execution: {
      engine,
      runs: execution.runs,
    },
    summaryRows: summaryRows as StudioDatasourceReadinessSidecar['summaryRows'],
    artifactRefs,
  }
}

function readDatasourceReadinessImportQuarantine(
  metadata: Record<string, unknown> | null,
): StudioDatasourceReadinessImportQuarantine | null {
  const quarantine = readRecord(metadata?.datasourceReadinessImportQuarantine)
  const sourceProjectIdClaim = readString(quarantine?.sourceProjectIdClaim)
  if (
    quarantine?.schemaVersion !== 'studio-datasource-readiness-import-quarantine.v1' ||
    quarantine.state !== 'quarantined' ||
    quarantine.reasonCode !== 'imported-sidecar-requires-destination-correlation' ||
    !sourceProjectIdClaim
  ) return null

  return {
    schemaVersion: 'studio-datasource-readiness-import-quarantine.v1',
    state: 'quarantined',
    reasonCode: 'imported-sidecar-requires-destination-correlation',
    sourceProjectIdClaim,
  }
}

function hasMalformedDatasourceReadinessImportQuarantine(
  metadata: Record<string, unknown> | null,
  parsed: StudioDatasourceReadinessImportQuarantine | null,
): boolean {
  return Boolean(
    metadata &&
    metadata.datasourceReadinessImportQuarantine != null &&
    !parsed,
  )
}

function isMalformedCurrentProjectDatasourceReadinessSidecar(
  value: unknown,
  parsed: StudioDatasourceReadinessSidecar | null,
  projectId: string | null | undefined,
): boolean {
  if (parsed || !projectId) return false
  const candidate = readRecord(value)
  return candidate?.schemaVersion === 'studio-datasource-readiness-sidecar.v1'
}

function resolveProjectDatasourceReadiness(
  project: unknown,
  canonicalProjectId?: string | null,
): StudioDatasourceReadinessResolution {
  const record = readRecord(project)
  const manifest = readRecord(record?.manifest)
  const metadata = readRecord(record?.metadata)
  const manifestReadiness = readManifestDatasourceReadinessLocations(manifest ?? {})
  const metadataSidecar = parseStudioDatasourceReadinessSidecar(
    metadata?.datasourceReadinessSidecar,
  )
  const manifestSidecar = parseStudioDatasourceReadinessSidecar(
    manifestReadiness.direct.value,
  )
  const manifestMetadataSidecar = parseStudioDatasourceReadinessSidecar(
    manifestReadiness.metadata.value,
  )
  const importQuarantine = readDatasourceReadinessImportQuarantine(metadata)
  const candidates = [metadataSidecar, manifestSidecar, manifestMetadataSidecar].filter(
    (sidecar): sidecar is StudioDatasourceReadinessSidecar => sidecar !== null,
  )
  const projectId = readString(canonicalProjectId) ?? readString(metadata?.studioProjectId)
  const linkedCandidates = candidates.filter(
    (sidecar) => sidecar.projectBinding.correlationStatus === 'linked',
  )
  const currentProjectSidecar = projectId
    ? linkedCandidates.find((sidecar) => sidecar.projectBinding.projectId === projectId)
    : null
  const currentProjectSidecars = projectId
    ? linkedCandidates.filter((sidecar) => sidecar.projectBinding.projectId === projectId)
    : []
  const declaredUnlinkedSidecar = candidates.find(
    (sidecar) =>
      sidecar.projectBinding.dependencyDeclared &&
      sidecar.projectBinding.correlationStatus === 'unlinked',
  )
  const linkedProjectIds = Array.from(new Set(
    linkedCandidates
      .map((sidecar) => sidecar.projectBinding.projectId)
      .filter((id): id is string => Boolean(id)),
  ))
  return {
    sidecar:
      currentProjectSidecar ??
      declaredUnlinkedSidecar ??
      linkedCandidates[0] ??
      metadataSidecar ??
      manifestSidecar ??
      manifestMetadataSidecar ??
      null,
    importQuarantine,
    malformedImportQuarantine:
      hasMalformedDatasourceReadinessImportQuarantine(metadata, importQuarantine),
    conflictingProjectIds: linkedProjectIds.length > 1 ? linkedProjectIds : [],
    conflictingCurrentProjectPayload:
      currentProjectSidecars.length > 1 &&
      currentProjectSidecars.some(
        (sidecar) => !datasourceReadinessJsonEqual(sidecar, currentProjectSidecars[0]),
      ),
    malformedCurrentProjectSidecar:
      isMalformedCurrentProjectDatasourceReadinessSidecar(
        metadata?.datasourceReadinessSidecar,
        metadataSidecar,
        projectId,
      ) ||
      isMalformedCurrentProjectDatasourceReadinessSidecar(
        manifestReadiness.direct.value,
        manifestSidecar,
        projectId,
      ) ||
      isMalformedCurrentProjectDatasourceReadinessSidecar(
        manifestReadiness.metadata.value,
        manifestMetadataSidecar,
        projectId,
      ),
  }
}

/**
 * Reads the project-attached, consumer-only datasource sidecar projection.
 *
 * The public v3 observation is not authority by itself. A projection is
 * displayable only when it carries the route/version plus a digest or durable
 * reference, and it blocks only through the explicit project binding checked
 * by evaluateStudioPublishReadiness below. No network request is made here.
 */
export function readProjectDatasourceReadinessSidecar(
  project: unknown,
): StudioDatasourceReadinessSidecar | null {
  const resolution = resolveProjectDatasourceReadiness(project)
  if (!resolution.sidecar) return null
  return {
    ...resolution.sidecar,
    ...(resolution.importQuarantine
      ? { importQuarantine: resolution.importQuarantine }
      : {}),
  }
}

/**
 * Restores the settings-derived datasource projection for read-only consumers
 * after an authoritative generated bundle replaces the editable app document.
 * The projection overlays metadata only; creator blocks, data, flows, and
 * manifest edits remain owned by the working bundle.
 */
export function withServerDatasourceReadinessProjection(
  project: unknown,
  serverProject: Pick<
    ProjectMeta,
    | 'metadata'
    | 'datasourceReadinessManifestSidecar'
    | 'datasourceReadinessManifestLocations'
  > | null | undefined,
  projectId: string | null,
): unknown {
  const record = readRecord(project)
  if (!record) return project

  const metadata = { ...(readRecord(record.metadata) ?? {}) }
  if (serverProject) {
    const serverMetadata = serverProject.metadata ?? {}
    if ('datasourceReadinessSidecar' in serverMetadata) {
      metadata.datasourceReadinessSidecar = serverMetadata.datasourceReadinessSidecar
    } else {
      if (!shouldRetainNonAuthorizingDatasourceReadinessEvidence(
        metadata.datasourceReadinessSidecar,
      )) {
        delete metadata.datasourceReadinessSidecar
      }
    }
    if ('datasourceReadinessImportQuarantine' in serverMetadata) {
      metadata.datasourceReadinessImportQuarantine =
        serverMetadata.datasourceReadinessImportQuarantine
    } else {
      delete metadata.datasourceReadinessImportQuarantine
    }
  }
  if (projectId) metadata.studioProjectId = projectId

  const manifest = withServerDatasourceReadinessManifestAuthority(
    record.manifest,
    serverProject,
  )

  return {
    ...record,
    manifest,
    metadata,
  }
}

/**
 * Applies only the server-owned datasource readiness locations to an editable
 * manifest. Creator-owned manifest siblings remain byte-for-byte from the
 * editable value, while ordinary persistence cannot clear or relocate the
 * reserved readiness evidence.
 */
export function withServerDatasourceReadinessManifestAuthority(
  manifestValue: unknown,
  serverProject: Pick<
    ProjectMeta,
    | 'datasourceReadinessManifestSidecar'
    | 'datasourceReadinessManifestLocations'
  > | null | undefined,
): Record<string, unknown> {
  const manifest = { ...(readRecord(manifestValue) ?? {}) }
  if (serverProject?.datasourceReadinessManifestLocations) {
    return withDatasourceReadinessManifestLocationAuthority(
      manifest,
      serverProject.datasourceReadinessManifestLocations,
    )
  }
  if (!serverProject || !('datasourceReadinessManifestSidecar' in serverProject)) {
    return manifest
  }

  let manifestSidecar = serverProject.datasourceReadinessManifestSidecar
  if (manifestSidecar == null) {
    const supplementalSidecar = readProjectDatasourceReadinessSidecar({
      manifest: {
        datasourceReadinessSidecar: manifest.datasourceReadinessSidecar,
      },
    })
    if (supplementalSidecar?.projectBinding.correlationStatus === 'unlinked') {
      manifestSidecar = manifest.datasourceReadinessSidecar
    }
  }
  return withCanonicalDatasourceReadinessManifestProjection(
    manifest,
    manifestSidecar,
  )
}

export interface StudioContractReadinessGate {
  schemaVersion: 'studio-contract-readiness-gate.v1'
  kind: 'actionflow' | 'report-export'
  status: StudioIntegrationReadinessStatus
  required: boolean
  contractVersion?: string
  evidenceRefs: string[]
  reasons: string[]
}

export interface StudioOntologyMatchingReadinessGate {
  schemaVersion: 'studio-ontology-matching-readiness-gate.v1'
  status: StudioIntegrationReadinessStatus
  required: boolean
  compilerContractStatus: 'ready' | 'blocked' | 'missing' | 'not-required'
  compilerEntrypointCount: number
  enforcedEntrypoints: string[]
  decisionCount: number
  blockedDecisionCount: number
  candidateDecisionCount: number
  actionFlowValidationRequired: boolean
  requiredValidation: string[]
  evidenceRefs: string[]
  reasons: string[]
}

export type StudioOntologyOperatingModelAxis =
  | 'studioGenerationLoop'
  | 'figmaTemplateQuality'
  | 'endUserE2E'
  | 'reportDocumentParity'
  | 'actionFlowRuntime'
  | 'ciCostPolicy'

export interface StudioOntologyOperatingModelGate {
  schemaVersion: 'studio-ontology-operating-model-gate.v1'
  status: StudioIntegrationReadinessStatus
  required: boolean
  axisStatuses: Record<StudioOntologyOperatingModelAxis, StudioIntegrationReadinessStatus>
  evidenceRefs: string[]
  reasons: string[]
}

export interface StudioProofReadinessGate {
  schemaVersion: 'studio-proof-readiness-gate.v1'
  status: 'pass' | 'blocked' | 'not-required'
  required: boolean
  fullCoverageStatus: 'pass' | 'partial' | 'blocked' | 'missing'
  exhaustiveProofStatus: 'pass' | 'partial' | 'blocked' | 'missing'
  providerSlotHydrationProofStatus: 'pass' | 'partial' | 'blocked' | 'missing'
  runtimeProvenActionCount: number
  runtimeHonoredActionCount: number
  reasons: string[]
}

export interface StudioEndUserProofGate {
  schemaVersion: 'studio-end-user-proof-publish-gate.v1'
  status: 'pass' | 'blocked' | 'not-required'
  required: boolean
  speedStatus: string
  qualityStatus: string
  validationRagStatus: string
  validationRagRequiredKindCount: number
  validationRagAcceptedEvidenceCount: number
  validationRagMissingKinds: string[]
  visualRichnessStatus: string
  visualRichnessScenarioCount: number
  visualRichnessRequiredScenarioCount: number
  visualRichnessMinDistinctSurfaceColorCount: number
  visualRichnessShoppingProminentMediaCount: number
  visualRichnessMinSemanticVisualTypeCount: number
  designStyleSwitchStatus: string
  designStyleSwitchPrimaryChanged: boolean
  designStyleSwitchMutationApi: string
  designStyleSwitchStudioHtmlChanged: boolean
  designStyleSwitchStudioBodyChanged: boolean
  designSandboxStatus: string
  scenarioMatrixStatus: string
  adaptiveRuntimeStatus: string
  adaptiveAxisCoverageStatus: string
  adaptiveAxisCoveragePassed: number
  adaptiveAxisCoverageRequired: number
  adaptiveAxisCoverageMissing: string[]
  stateBoundaryStatus: string
  datasourceStateProjectionStatus: string
  datasourceStateProjectionPassCount: number
  datasourceStateProjectionScenarioCount: number
  datasourceStateProjectionRequiredScenarioCount: number
  datasourceStateProjectionStateDomains: string[]
  datasourceStateProjectionDatasourceKinds: string[]
  datasourceStateProjectionProjectionTextCount: number
  referoBatchStatus: string
  reasons: string[]
}

export interface StudioRenderPackPackagingGate {
  schemaVersion: 'studio-render-pack-packaging-gate.v1'
  status: 'pass' | 'blocked' | 'not-required'
  required: boolean
  transport: 'RPK1'
  blockCount: number
  errorCount: number
  reasons: string[]
}

export interface StudioRenderPackPublishPayload {
  schemaVersion: 'studio-render-pack-publish-payload.v1'
  transport: 'RPK1'
  encoding: 'base64'
  blockCount: number
  errorCount: number
  blocks: Record<string, {
    schemaVersion: string
    encoding: 'base64'
    bytes: string
    byteLength: number
    diagnostics: Array<{ code: string; severity: string; message: string; nodeId?: string; value?: string }>
  }>
}

interface StudioPublishProofPolicy {
  standardWebGeneratedApp: boolean
}

export interface StudioProductionAdapterGate {
  schemaVersion: 'studio-production-adapter-gate.v1'
  status: 'pass' | 'blocked' | 'not-required'
  required: boolean
  adapterActions: string[]
  datasourceRequired: boolean
  requiredEvidence: string[]
  missingEvidence: string[]
  reasons: string[]
}

export interface StudioTargetMatrixGate {
  schemaVersion: 'studio-target-matrix-gate.v1'
  status: 'pass' | 'blocked' | 'not-required'
  required: boolean
  requestedTargets: string[]
  readyTargets: string[]
  partialTargets: string[]
  missingTargets: string[]
  rendererTargets: Record<string, string>
  nativeAxisGaps: Record<string, string[]>
  reasons: string[]
}

export function evaluateStudioPublishReadiness(
  bundle: LoadedApp | null | undefined,
  projectPlan?: StudioProjectPlan | null,
  canonicalProjectId?: string | null,
): StudioPublishReadinessGate {
  const gate = readFirstRecordDeep(bundle, [
    'generatedAppReadinessGate',
    'publishReadinessGate',
  ])
  const targetMatrix = readFirstRecordDeep(bundle, ['rendererTargetMatrix'])
  const generationEvidence = readFirstRecordDeep(bundle, ['generationEvidence'])
  const plan = projectPlan ?? readStudioProjectPlan(readRecordDeep(bundle, ['settings']))
  const manifestPlan = readStudioProjectPlan({
    studioProjectPlan: readRecordDeep(bundle, ['manifest'])?.studioProjectPlan,
  })
  const packagingMode = normalizePackagingMode(
    plan?.appClass ??
    readString(readRecordDeep(bundle, ['manifest'])?.appClass) ??
    readString(readRecordDeep(bundle, ['manifest'])?.deploymentMode) ??
    readString(generationEvidence?.generatedApp && readRecord(generationEvidence.generatedApp)?.appClass),
  )
  const securityProfile = normalizeSecurityProfile(plan?.securityProfile)
  const previewReady = gate?.canPreview !== false
  const rendererTargets = readRendererTargetReadiness(targetMatrix)
  const proofPolicy = buildStudioPublishProofPolicy(bundle, {
    plan,
    packagingMode,
    securityProfile,
    generationEvidence,
    targetMatrix,
  })
  const publishReadyFromGeneratedApp = proofPolicy.standardWebGeneratedApp || gate?.canPublish !== false
  const renderPackPackagingGate = evaluateRenderPackPackagingGate(bundle, packagingMode)
  const proofReadinessGate = evaluateProofReadinessGate(bundle, packagingMode, proofPolicy)
  const endUserProofGate = evaluateEndUserProofGate(bundle, packagingMode, proofPolicy)
  const targetMatrixGate = evaluateTargetMatrixGate(targetMatrix, plan)
  const actionFlowReadinessGate = evaluateContractReadinessGate(bundle, {
    kind: 'actionflow',
    label: 'ActionFlow',
    required: packagingMode === 'dynamic' || packagingMode === 'hybrid',
    aliases: [
      'actionFlowReadiness',
      'actionflowReadiness',
      'actionFlowReadinessGate',
      'actionflowReadinessGate',
      'actionFlowContractReadiness',
      'actionFlowRuntimeReadiness',
    ],
    missingReason: `${capitalizePackagingMode(packagingMode)} publish requires ActionFlow readiness evidence from the ActionFlow core contract.`,
  })
  const reportExportReadinessGate = evaluateReportExportReadinessGate(bundle, {
    kind: 'report-export',
    label: 'Report/export',
    required: isReportLikeProject(bundle, plan),
    aliases: [
      'reportExportReadiness',
      'reportReadiness',
      'excelReportReadiness',
      'documentExportReadiness',
      'reportExportReadinessGate',
      'excelReportReadinessGate',
      'documentExportProductionGate',
      'documentExportEvidence',
      'documentExportServiceManifest',
      'documentExportArtifactManifest',
      'reportWorkspace',
      'reportWorkspaceEvidence',
    ],
    missingReason: 'Report/document publish requires report/export readiness evidence for every claimed output target.',
  })
  const ontologyMatchingReadinessGate = evaluateOntologyMatchingReadinessGate(bundle, {
    required: Boolean(
      gate ||
        generationEvidence?.generatedApp ||
        generationEvidence?.uiOntology ||
        plan?.designSystemId ||
        plan?.templateId,
    ),
    actionFlowReadinessGate,
  })
  const ontologyOperatingModelGate = evaluateOntologyOperatingModelGate(bundle, {
    plan,
    actionFlowReadinessGate,
    reportExportReadinessGate,
    ontologyMatchingReadinessGate,
    endUserProofGate,
  })
  const applicationSourceSufficiencyGate = evaluateApplicationSourceSufficiencyGate(bundle)
  const applicationOntologyV2Gate = evaluateApplicationOntologyV2Gate(bundle)
  const bundleManifest = readRecordDeep(bundle, ['manifest'])
  const bundleMetadata = readRecordDeep(bundle, ['metadata'])
  const bundleProjectId =
    readString(canonicalProjectId) ??
    readString(bundleMetadata?.studioProjectId) ??
    readString(bundleManifest?.id) ??
    readString(bundleManifest?.appId) ??
    null
  const datasourceReadiness = resolveProjectDatasourceReadiness(bundle, bundleProjectId)
  const datasourceReadinessSidecar = datasourceReadiness.sidecar
  const reasons: string[] = []

  if (plan?.designSystemRelease && !preservesSelectedDesignSystemRelease(plan, manifestPlan)) {
    reasons.push('Approved design-system release identity is missing or changed in the saved application manifest.')
  }
  if (!previewReady) reasons.push('Preview readiness gate is blocked.')
  if (!publishReadyFromGeneratedApp) {
    reasons.push('Generated app readiness gate is not publish-ready.')
    for (const reason of readStringList(gate?.blockedReasons)) reasons.push(reason)
  }

  const requiredSecurityEvidence = securityProfile === 'standard'
    ? []
    : [
        'signedManifest',
        'wasmIntegrity',
        'policyBundle',
        'actionAllowlist',
        'datasourceEntitlements',
      ]
  const missingSecurityEvidence = requiredSecurityEvidence.filter((key) => !hasPublishSecurityEvidence(bundle, key))
  for (const key of missingSecurityEvidence) {
    reasons.push(`${formatSecurityEvidenceLabel(key)} is required for ${securityProfile} publish readiness.`)
  }

  const productionAdapterGate = evaluateProductionAdapterGate(bundle)
  if (productionAdapterGate.status === 'blocked') {
    reasons.push(...productionAdapterGate.reasons)
  }
  if (renderPackPackagingGate.status === 'blocked') {
    reasons.push(...renderPackPackagingGate.reasons)
  }
  if (proofReadinessGate.status === 'blocked') {
    reasons.push(...proofReadinessGate.reasons)
  }
  if (endUserProofGate.status === 'blocked') {
    reasons.push(...endUserProofGate.reasons)
  }
  if (targetMatrixGate.status === 'blocked') {
    reasons.push(...targetMatrixGate.reasons)
  }
  if (actionFlowReadinessGate.status !== 'ready' && actionFlowReadinessGate.status !== 'not-required') {
    reasons.push(...actionFlowReadinessGate.reasons)
  }
  if (reportExportReadinessGate.status !== 'ready' && reportExportReadinessGate.status !== 'not-required') {
    reasons.push(...reportExportReadinessGate.reasons)
  }
  if (ontologyMatchingReadinessGate.status !== 'ready' && ontologyMatchingReadinessGate.status !== 'not-required') {
    reasons.push(...ontologyMatchingReadinessGate.reasons)
  }
  if (ontologyOperatingModelGate.status !== 'ready' && ontologyOperatingModelGate.status !== 'not-required') {
    reasons.push(...ontologyOperatingModelGate.reasons)
  }
  if (
    applicationSourceSufficiencyGate.status !== 'ready' &&
    applicationSourceSufficiencyGate.status !== 'not-required'
  ) {
    reasons.push(...applicationSourceSufficiencyGate.reasons)
  }
  if (applicationOntologyV2Gate.required && !applicationOntologyV2Gate.publishAllowed) {
    reasons.push(...applicationOntologyV2Gate.reasons)
  }
  if (datasourceReadiness.conflictingProjectIds.length > 0) {
    reasons.push(
      `Datasource readiness sidecar locations declare conflicting project bindings: ${datasourceReadiness.conflictingProjectIds.join(', ')}.`,
    )
  }
  if (datasourceReadiness.conflictingCurrentProjectPayload) {
    reasons.push(
      `Datasource readiness sidecar locations disagree for project ${bundleProjectId ?? 'unknown'}.`,
    )
  }
  if (datasourceReadiness.malformedCurrentProjectSidecar) {
    reasons.push(
      `Datasource readiness sidecar bound to project ${bundleProjectId ?? 'unknown'} is malformed.`,
    )
  }
  if (datasourceReadiness.malformedImportQuarantine) {
    reasons.push('Datasource readiness import quarantine metadata is malformed.')
  }
  if (datasourceReadiness.importQuarantine) {
    reasons.push(
      `Imported datasource readiness evidence is quarantined until it is correlated to project ${bundleProjectId ?? 'unknown'}.`,
      ...(datasourceReadinessSidecar && datasourceReadinessSidecar.publishReadiness.blockers.length > 0
        ? datasourceReadinessSidecar.publishReadiness.blockers
        : ['Imported datasource readiness evidence cannot authorize publication.']),
    )
  } else if (
    datasourceReadinessSidecar &&
    datasourceReadinessSidecar.projectBinding.dependencyDeclared &&
    (
      datasourceReadinessSidecar.projectBinding.correlationStatus === 'unlinked' ||
      datasourceReadinessSidecar.projectBinding.projectId === bundleProjectId
    )
  ) {
    const sidecarBlocks =
      datasourceReadinessSidecar.projectBinding.correlationStatus !== 'linked' ||
      datasourceReadinessSidecar.readiness.status !== 'ready' ||
      !datasourceReadinessSidecar.publishReadiness.allowed ||
      datasourceReadinessSidecar.publishReadiness.blockers.length > 0 ||
      datasourceReadinessSidecar.execution.runs.length === 0
    if (sidecarBlocks) {
      const declaredBlockers = datasourceReadinessSidecar.publishReadiness.blockers
      reasons.push(
        ...(declaredBlockers.length > 0
          ? declaredBlockers
          : [
              `Datasource readiness sidecar is ${datasourceReadinessSidecar.readiness.status} and has no publish-ready correlated runtime evidence.`,
            ]),
      )
    }
  }

  const ok = reasons.length === 0
  return {
    schemaVersion: 'studio-publish-readiness-gate.v1',
    ok,
    previewReady,
    publishReady: ok,
    packagingMode,
    securityProfile,
    reasons,
    missingSecurityEvidence,
    requiredSecurityEvidence,
    missingProductionAdapterEvidence: productionAdapterGate.missingEvidence,
    proofReadinessGate,
    endUserProofGate,
    renderPackPackagingGate,
    productionAdapterGate,
    targetMatrixGate,
    actionFlowReadinessGate,
    reportExportReadinessGate,
    ontologyMatchingReadinessGate,
    ontologyOperatingModelGate,
    applicationSourceSufficiencyGate,
    applicationOntologyV2Gate,
    ...(readString(gate?.status) ? { generatedAppStatus: readString(gate?.status) } : {}),
    ...(Object.keys(rendererTargets).length > 0 ? { rendererTargets } : {}),
  }
}

function evaluateOntologyMatchingReadinessGate(
  source: unknown,
  input: {
    required: boolean
    actionFlowReadinessGate: StudioContractReadinessGate
  },
): StudioOntologyMatchingReadinessGate {
  const generationEvidence = readFirstRecordDeep(source, ['generationEvidence'])
  const plan =
    readFirstRecordDeep(source, ['ontologyMatchingPlan']) ??
    readRecord(readFirstRecordDeep(source, ['ontologyRetrievalTrace'])?.ontologyMatchingPlan) ??
    readRecord(generationEvidence?.ontologyMatchingPlan) ??
    readRecord(readRecord(generationEvidence?.ontologyRetrievalTrace)?.ontologyMatchingPlan) ??
    readRecord(readRecord(generationEvidence?.uiOntology)?.ontologyMatchingPlan)
  const required = input.required || Boolean(plan)

  if (!plan) {
    return {
      schemaVersion: 'studio-ontology-matching-readiness-gate.v1',
      status: required ? 'gap' : 'not-required',
      required,
      compilerContractStatus: required ? 'missing' : 'not-required',
      compilerEntrypointCount: 0,
      enforcedEntrypoints: [],
      decisionCount: 0,
      blockedDecisionCount: 0,
      candidateDecisionCount: 0,
      actionFlowValidationRequired: false,
      requiredValidation: [],
      evidenceRefs: [],
      reasons: required
        ? ['Ontology matching plan is missing; generated app publish requires deterministic intent-to-ontology evidence.']
        : [],
    }
  }

  const decisions = readArray(plan.decisions)
    .flatMap((value) => {
      const record = readRecord(value)
      return record ? [record] : []
    })
  const trace = readFirstRecordDeep(source, ['ontologyRetrievalTrace'])
  const compilerContract =
    readFirstRecordDeep(source, ['ontologyCompilerContract']) ??
    readRecord(trace?.ontologyCompilerContract) ??
    readRecord(generationEvidence?.ontologyCompilerContract) ??
    readRecord(readRecord(generationEvidence?.ontologyRetrievalTrace)?.ontologyCompilerContract) ??
    readRecord(readRecord(generationEvidence?.uiOntology)?.ontologyCompilerContract)
  const actionFlowImpact = readRecord(plan.actionFlowImpact)
  const requiredValidation = readStringList(actionFlowImpact?.requiredValidation)
  const actionFlowValidationRequired =
    actionFlowImpact?.required === true && requiredValidation.length > 0
  const blockedDecisions = decisions.filter((decision) => decision.generationAllowed === false)
  const candidateDecisions = decisions.filter((decision) => readString(decision.decision) === 'create_candidate')
  const decisionEvidenceRefs = decisions.flatMap((decision) => readStringList(decision.evidenceRefs))
  const compilerEvidenceRefs = readStringList(compilerContract?.evidenceRefs)
  const actionFlowValidationEvidenceRefs = input.actionFlowReadinessGate.evidenceRefs.filter((ref) => {
    const lower = ref.toLowerCase()
    return requiredValidation.some((validation) => lower.includes(validation.toLowerCase())) ||
      lower.includes('actionflowtopology') ||
      lower.includes('compilerparambindings')
  })
  const evidenceRefs = Array.from(new Set([
    ...decisionEvidenceRefs,
    ...compilerEvidenceRefs,
    ...actionFlowValidationEvidenceRefs,
  ]))
  const reasons: string[] = []
  const hardConstraints = readStringList(plan.hardConstraints)
  const traceRequiresCompilerContract = Boolean(
    trace &&
      (
        hardConstraints.some((constraint) => constraint.startsWith('design-system:') || constraint.startsWith('template:')) ||
        actionFlowValidationRequired
      ),
  )
  const entrypoints = readArray(compilerContract?.entrypoints).flatMap((entry) => {
    const record = readRecord(entry)
    return record ? [record] : []
  })
  const enforcedEntrypoints = entrypoints
    .filter((entry) => readString(entry.enforcement) === 'tool-compiler' || readString(entry.enforcement) === 'publish-gate')
    .map((entry) => readString(entry.entrypoint))
    .filter((entrypoint): entrypoint is string => Boolean(entrypoint))
  const compilerContractStatus: StudioOntologyMatchingReadinessGate['compilerContractStatus'] =
    !traceRequiresCompilerContract
      ? 'not-required'
      : !compilerContract
        ? 'missing'
        : readString(compilerContract.status) === 'blocked'
          ? 'blocked'
          : readString(compilerContract.authority) === 'tool'
            ? 'ready'
            : 'blocked'

  if (decisions.length === 0) {
    reasons.push('Ontology matching plan has no decisions; generated app publish requires deterministic intent-to-ontology decisions.')
  }
  if (traceRequiresCompilerContract && !compilerContract) {
    reasons.push('Ontology compiler contract is missing; template/design-system constrained generation must be tool-enforced before publish.')
  } else if (compilerContract) {
    if (readString(compilerContract.authority) !== 'tool') {
      reasons.push('Ontology compiler contract authority must be tool before publish.')
    }
    if (readString(compilerContract.status) === 'blocked') {
      reasons.push(...readStringList(compilerContract.reasons))
    }
  }
  if (blockedDecisions.length > 0) {
    reasons.push(
      `Ontology matching has unverifiable candidate decisions: ${blockedDecisions.map(formatOntologyMatchingDecision).join(', ')}.`,
    )
  }
  if (
    actionFlowValidationRequired &&
    (
      input.actionFlowReadinessGate.status !== 'ready' ||
      actionFlowValidationEvidenceRefs.length === 0
    )
  ) {
    reasons.push('Ontology matching requires ActionFlow validation evidence before publish.')
  }

  return {
    schemaVersion: 'studio-ontology-matching-readiness-gate.v1',
    status: reasons.length === 0 ? 'ready' : 'invalid',
    required,
    compilerContractStatus,
    compilerEntrypointCount: entrypoints.length,
    enforcedEntrypoints,
    decisionCount: decisions.length,
    blockedDecisionCount: blockedDecisions.length,
    candidateDecisionCount: candidateDecisions.length,
    actionFlowValidationRequired,
    requiredValidation,
    evidenceRefs,
    reasons,
  }
}

function evaluateOntologyOperatingModelGate(
  source: unknown,
  input: {
    plan?: StudioProjectPlan | null
    actionFlowReadinessGate: StudioContractReadinessGate
    reportExportReadinessGate: StudioContractReadinessGate
    ontologyMatchingReadinessGate: StudioOntologyMatchingReadinessGate
    endUserProofGate: StudioEndUserProofGate
  },
): StudioOntologyOperatingModelGate {
  const figmaTemplateQuality = evaluateFigmaTemplateQualityAxis(source, input)
  const ciCostPolicy = evaluateCiCostPolicyAxis(source)
  const axisStatuses: StudioOntologyOperatingModelGate['axisStatuses'] = {
    studioGenerationLoop: input.ontologyMatchingReadinessGate.required
      ? input.ontologyMatchingReadinessGate.status
      : 'not-required',
    figmaTemplateQuality: figmaTemplateQuality.status,
    endUserE2E: input.endUserProofGate.required
      ? input.endUserProofGate.status === 'pass'
        ? 'ready'
        : 'invalid'
      : 'not-required',
    reportDocumentParity: input.reportExportReadinessGate.required
      ? input.reportExportReadinessGate.status
      : 'not-required',
    actionFlowRuntime: input.actionFlowReadinessGate.required
      ? input.actionFlowReadinessGate.status
      : 'not-required',
    ciCostPolicy: ciCostPolicy.status,
  }
  const required = Object.values(axisStatuses).some((status) => status !== 'not-required')
  const reasons: string[] = []
  appendOntologyOperatingModelAxisReasons(
    reasons,
    'Studio generation loop',
    axisStatuses.studioGenerationLoop,
    input.ontologyMatchingReadinessGate.reasons,
  )
  appendOntologyOperatingModelAxisReasons(
    reasons,
    'Figma/template quality',
    axisStatuses.figmaTemplateQuality,
    figmaTemplateQuality.reasons,
  )
  appendOntologyOperatingModelAxisReasons(
    reasons,
    'End-user E2E',
    axisStatuses.endUserE2E,
    input.endUserProofGate.reasons,
  )
  appendOntologyOperatingModelAxisReasons(
    reasons,
    'Report/document parity',
    axisStatuses.reportDocumentParity,
    input.reportExportReadinessGate.reasons,
  )
  appendOntologyOperatingModelAxisReasons(
    reasons,
    'ActionFlow runtime',
    axisStatuses.actionFlowRuntime,
    input.actionFlowReadinessGate.reasons,
  )
  appendOntologyOperatingModelAxisReasons(
    reasons,
    'CI cost policy',
    axisStatuses.ciCostPolicy,
    ciCostPolicy.reasons,
  )
  const evidenceRefs = Array.from(new Set([
    ...input.ontologyMatchingReadinessGate.evidenceRefs,
    ...input.actionFlowReadinessGate.evidenceRefs,
    ...input.reportExportReadinessGate.evidenceRefs,
    ...figmaTemplateQuality.evidenceRefs,
    ...ciCostPolicy.evidenceRefs,
  ]))

  return {
    schemaVersion: 'studio-ontology-operating-model-gate.v1',
    status: !required
      ? 'not-required'
      : reasons.length === 0
        ? 'ready'
        : pickMostSevereIntegrationStatus(axisStatuses),
    required,
    axisStatuses,
    evidenceRefs,
    reasons,
  }
}

function evaluateFigmaTemplateQualityAxis(
  source: unknown,
  input: {
    plan?: StudioProjectPlan | null
    ontologyMatchingReadinessGate: StudioOntologyMatchingReadinessGate
  },
): { status: StudioIntegrationReadinessStatus; evidenceRefs: string[]; reasons: string[] } {
  const generationEvidence = readFirstRecordDeep(source, ['generationEvidence'])
  const templateEnforcement =
    readFirstRecordDeep(source, [
      'templateEnforcement',
      'figmaTemplateEnforcement',
      'designTemplateEnforcement',
    ]) ??
    readFirstRecordDeep(generationEvidence, [
      'templateEnforcement',
      'figmaTemplateEnforcement',
      'designTemplateEnforcement',
    ])
  const figmaImport =
    readFirstRecordDeep(source, [
      'cogoXyzFigmaImport',
      'figmaImportReadback',
      'figmaImportEvidence',
      'figmaTemplateImportEvidence',
    ]) ??
    readFirstRecordDeep(generationEvidence, [
      'cogoXyzFigmaImport',
      'figmaImportReadback',
      'figmaImportEvidence',
      'figmaTemplateImportEvidence',
    ])
  const visibleCompilerProof =
    readFirstRecordDeep(source, [
      'visibleCompilerProof',
      'figmaVisibleCompilerProof',
      'figmaDesignQualityGate',
      'figmaTemplateQualityGate',
    ]) ??
    readFirstRecordDeep(generationEvidence, [
      'visibleCompilerProof',
      'figmaVisibleCompilerProof',
      'figmaDesignQualityGate',
      'figmaTemplateQualityGate',
    ]) ??
    readRecord(figmaImport?.visibleCompilerProof) ??
    readRecord(figmaImport?.figmaDesignQualityGate) ??
    readRecord(figmaImport?.qualityGate)
  const designSystemSelected = Boolean(input.plan?.designSystemId)
  const templateSelected = Boolean(input.plan?.templateId)
  const hasFigmaEvidence = Boolean(figmaImport || visibleCompilerProof)
  const required = designSystemSelected || templateSelected || Boolean(templateEnforcement) || hasFigmaEvidence

  if (!required) {
    return { status: 'not-required', evidenceRefs: [], reasons: [] }
  }

  const enforcementAuthority = readRecord(templateEnforcement?.authority)
  const toolLockedByTemplateEnforcement = Boolean(
    templateEnforcement &&
      (
        readString(templateEnforcement.authority) === 'tool' ||
        readString(templateEnforcement.enforcement) === 'tool-compiler' ||
        readString(templateEnforcement.enforcement) === 'publish-gate' ||
        readString(enforcementAuthority?.source) === 'tool' ||
        (
          readBoolean(enforcementAuthority?.llmMayChooseTemplate) === false &&
          readBoolean(enforcementAuthority?.llmMayOverrideLayout) === false &&
          readBoolean(enforcementAuthority?.llmMayOverrideStyle) === false
        ) ||
        (
          readBoolean(templateEnforcement.llmMayChooseTemplate) === false &&
          readBoolean(templateEnforcement.llmMayOverrideLayout) === false &&
          readBoolean(templateEnforcement.llmMayOverrideStyle) === false
        )
      ),
  )
  const toolLockedByCompilerContract = input.ontologyMatchingReadinessGate.compilerContractStatus === 'ready'
  const commercialPreviewReady =
    readBoolean(visibleCompilerProof?.commercializationReady) === true ||
    readBoolean(figmaImport?.commercializationReady) === true ||
    normalizeIntegrationReadinessStatus(
      readString(visibleCompilerProof?.commercialUiQualityStatus) ??
        readString(visibleCompilerProof?.sourceFaithfulPreviewStatus) ??
        readString(figmaImport?.commercialUiQualityStatus),
    ) === 'ready'
  const editablePrimaryReady =
    readBoolean(visibleCompilerProof?.editablePrimaryPromotionReady) === true ||
    readBoolean(figmaImport?.editablePrimaryPromotionReady) === true ||
    normalizeIntegrationReadinessStatus(
      readString(visibleCompilerProof?.editablePrimaryQualityStatus) ??
        readString(figmaImport?.editablePrimaryQualityStatus),
    ) === 'ready'
  const reasons: string[] = []

  if (!toolLockedByTemplateEnforcement && !toolLockedByCompilerContract) {
    reasons.push('Selected design system/template must be enforced by a tool compiler or publish gate before publish.')
  }
  if (hasFigmaEvidence && !commercialPreviewReady && !editablePrimaryReady) {
    reasons.push('Figma template import needs source-faithful commercial preview or editable-primary promotion evidence before publish.')
  }

  return {
    status: reasons.length === 0 ? 'ready' : 'invalid',
    evidenceRefs: [
      ...readOperatingModelEvidenceRefs(templateEnforcement),
      ...readOperatingModelEvidenceRefs(figmaImport),
      ...readOperatingModelEvidenceRefs(visibleCompilerProof),
    ],
    reasons,
  }
}

function evaluateCiCostPolicyAxis(
  source: unknown,
): { status: StudioIntegrationReadinessStatus; evidenceRefs: string[]; reasons: string[] } {
  const generationEvidence = readFirstRecordDeep(source, ['generationEvidence'])
  const policy =
    readFirstRecordDeep(source, [
      'ciCostPolicy',
      'validationCostPolicy',
      'studioCiCostPolicy',
      'ciSurfacePolicy',
    ]) ??
    readFirstRecordDeep(generationEvidence, [
      'ciCostPolicy',
      'validationCostPolicy',
      'studioCiCostPolicy',
      'ciSurfacePolicy',
    ])

  if (!policy) {
    return { status: 'not-required', evidenceRefs: [], reasons: [] }
  }

  const baseStatus = normalizeIntegrationReadinessStatus(
    readString(policy.status) ??
      readString(policy.readiness) ??
      readString(policy.verdict),
  )
  const heavyGatePolicy = [
    readString(policy.heavyGatePolicy),
    readString(policy.heavyGateMode),
    readString(policy.expensiveGatePolicy),
    readString(policy.githubActionsPolicy),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  const heavyGatesControlled = Boolean(
    policy.expensiveGitHubActionsDefault === false ||
      policy.heavyGatesDefault === false ||
      policy.focusedChecksFirst === true ||
      policy.changedSurfaceDetection === true ||
      /\b(manual|affected|label|schedule|dispatch|changed-surface|focused)\b/.test(heavyGatePolicy),
  )
  const reasons: string[] = []

  if (baseStatus !== 'ready') {
    reasons.push(`CI cost policy readiness is ${baseStatus}; publish requires ready when a CI policy is attached.`)
  }
  if (!heavyGatesControlled) {
    reasons.push('CI cost policy must prove focused/affected checks run before expensive GitHub Actions gates.')
  }

  return {
    status: reasons.length === 0 ? 'ready' : baseStatus === 'ready' ? 'invalid' : baseStatus,
    evidenceRefs: readOperatingModelEvidenceRefs(policy),
    reasons,
  }
}

function appendOntologyOperatingModelAxisReasons(
  reasons: string[],
  label: string,
  status: StudioIntegrationReadinessStatus,
  axisReasons: string[],
): void {
  if (status === 'ready' || status === 'not-required') return
  reasons.push(`${label} ontology operating-model axis is ${status}; publish requires ready.`)
  reasons.push(...axisReasons)
}

function pickMostSevereIntegrationStatus(
  axisStatuses: StudioOntologyOperatingModelGate['axisStatuses'],
): StudioIntegrationReadinessStatus {
  const statuses = Object.values(axisStatuses)
  if (statuses.includes('invalid')) return 'invalid'
  if (statuses.includes('gap')) return 'gap'
  if (statuses.includes('fallback')) return 'fallback'
  return 'ready'
}

function readOperatingModelEvidenceRefs(source: unknown): string[] {
  const record = readRecord(source)
  if (!record) return []
  return Array.from(new Set([
    ...readStringList(record.evidenceRefs),
    ...readStringList(record.evidence),
    ...readStringList(record.artifacts),
    ...readStringList(record.sources),
  ]))
}

function formatOntologyMatchingDecision(decision: Record<string, unknown>): string {
  const kind = readString(decision.kind) ?? 'asset'
  const requirementId = readString(decision.requirementId) ?? readString(decision.selectedAssetId) ?? 'unknown'
  return `${kind}:${requirementId}`
}

function evaluateContractReadinessGate(
  source: unknown,
  config: {
    kind: StudioContractReadinessGate['kind']
    label: string
    required: boolean
    aliases: string[]
    missingReason: string
  },
): StudioContractReadinessGate {
  const generationEvidence = readFirstRecordDeep(source, ['generationEvidence'])
  const contract =
    readFirstRecordDeep(source, config.aliases) ??
    readFirstRecordDeep(generationEvidence, config.aliases)
  const required = config.required || Boolean(contract)

  if (!contract) {
    return {
      schemaVersion: 'studio-contract-readiness-gate.v1',
      kind: config.kind,
      status: required ? 'gap' : 'not-required',
      required,
      evidenceRefs: [],
      reasons: required ? [config.missingReason] : [],
    }
  }

  const baseStatus = normalizeIntegrationReadinessStatus(
    readString(contract.status) ??
    readString(contract.readiness) ??
    readString(contract.verdict),
  )
  const readinessPacket =
    readRecord(contract.readinessPacket) ??
    readRecord(contract.actionFlowReadinessPacket) ??
    readRecord(contract.headlessReadinessPacket)
  const readinessPacketBlocked =
    readinessPacket?.publishAllowed === false ||
    readString(readinessPacket?.status) === 'blocked'
  const readinessPacketReasons = readinessPacketBlocked
    ? readStringList(readinessPacket?.blockingFailures)
    : []
  const status =
    baseStatus === 'ready' && readinessPacketBlocked ? 'invalid' : baseStatus
  const contractReasons = [
    ...readStringList(contract.reasons),
    ...readStringList(contract.gaps),
    ...readStringList(contract.missing),
    ...readStringList(contract.blockedReasons),
  ]
  const evidenceRefs = [
    ...readStringList(contract.evidenceRefs),
    ...readStringList(contract.evidence),
    ...readStringList(contract.artifacts),
    ...readStringList(contract.sources),
    ...readStringList(readinessPacket?.evidenceRefs),
  ]
  const reasons = status === 'ready'
    ? []
    : [
        `${config.label} readiness is ${status}; publish requires ready.`,
        ...(readinessPacketReasons.length > 0
          ? readinessPacketReasons
          : contractReasons.length > 0
            ? contractReasons
            : [config.missingReason]),
      ]

  return {
    schemaVersion: 'studio-contract-readiness-gate.v1',
    kind: config.kind,
    status,
    required,
    ...(readString(contract.contractVersion) ? { contractVersion: readString(contract.contractVersion) } : {}),
    evidenceRefs,
    reasons,
  }
}

function evaluateReportExportReadinessGate(
  source: unknown,
  config: {
    kind: 'report-export'
    label: string
    required: boolean
    aliases: string[]
    missingReason: string
  },
): StudioContractReadinessGate {
  const genericGate = evaluateContractReadinessGate(source, config)
  if (genericGate.status === 'ready') return genericGate

  const generationEvidence = readFirstRecordDeep(source, ['generationEvidence'])
  const hasWorkspaceEvidence =
    Boolean(readReportWorkspaceEvidence(source)) ||
    Boolean(readReportWorkspaceEvidence(generationEvidence))

  if (!hasWorkspaceEvidence) return genericGate

  const model = buildReportWorkspaceModel(source)
  const workspaceStatus: StudioIntegrationReadinessStatus =
    model.status === 'ready'
      ? 'ready'
      : model.status === 'warn'
        ? 'fallback'
        : model.status === 'missing'
          ? 'gap'
          : 'invalid'

  return {
    schemaVersion: 'studio-contract-readiness-gate.v1',
    kind: config.kind,
    status: workspaceStatus,
    required: true,
    contractVersion: 'cogo.document-export-production-gate.v1',
    evidenceRefs: model.evidenceRefs,
    reasons: workspaceStatus === 'ready'
      ? []
      : [
          `${config.label} readiness is ${workspaceStatus}; publish requires ready.`,
          ...(model.blockers.length > 0 ? model.blockers : [config.missingReason]),
        ],
  }
}

function evaluateProofReadinessGate(
  source: unknown,
  packagingMode: StudioPublishReadinessGate['packagingMode'],
  proofPolicy: StudioPublishProofPolicy = { standardWebGeneratedApp: false },
): StudioProofReadinessGate {
  const required = packagingMode === 'dynamic' || packagingMode === 'hybrid'
  const fullCoverageAudit =
    readFirstRecordDeep(source, ['fullCoverageAudit', 'studioFullCoverageAudit']) ??
    readFirstRecordDeep(readFirstRecordDeep(source, ['generationEvidence']), ['fullCoverage'])
  const exhaustiveProofAudit =
    readFirstRecordDeep(source, ['exhaustiveProofAudit', 'studioExhaustiveProofAudit']) ??
    readFirstRecordDeep(readFirstRecordDeep(source, ['generationEvidence']), ['exhaustiveProof'])
  const fullCoverageStatus = normalizeAuditStatus(readString(fullCoverageAudit?.status))
  const exhaustiveProofStatus = normalizeAuditStatus(readString(exhaustiveProofAudit?.status))
  const exhaustiveComponents = readRecord(exhaustiveProofAudit?.components)
  const exhaustiveSummary = readRecord(exhaustiveProofAudit?.summary)
  const providerSlotProof = readRecord(exhaustiveComponents?.providerSlotHydrationProof)
  const providerSlotHydrationProofStatus = normalizeAuditStatus(
    readString(providerSlotProof?.status) ??
    readString(exhaustiveProofAudit?.providerSlotHydrationProofStatus),
  )
  const runtimeProvenActionCount =
    readNumber(exhaustiveSummary?.runtimeProvenActionCount) ||
    readNumber(exhaustiveProofAudit?.runtimeProvenActionCount)
  const runtimeHonoredActionCount =
    readNumber(exhaustiveSummary?.runtimeHonoredActionCount) ||
    readNumber(exhaustiveProofAudit?.runtimeHonoredActionCount)

  if (!required || proofPolicy.standardWebGeneratedApp) {
    return {
      schemaVersion: 'studio-proof-readiness-gate.v1',
      status: proofPolicy.standardWebGeneratedApp ? 'pass' : 'not-required',
      required: proofPolicy.standardWebGeneratedApp,
      fullCoverageStatus,
      exhaustiveProofStatus,
      providerSlotHydrationProofStatus,
      runtimeProvenActionCount,
      runtimeHonoredActionCount,
      reasons: [],
    }
  }

  const reasons: string[] = []
  if (fullCoverageStatus !== 'pass') {
    reasons.push('Dynamic/hybrid publish requires passing Studio full-coverage audit evidence.')
  }
  if (exhaustiveProofStatus !== 'pass') {
    reasons.push('Dynamic/hybrid publish requires passing Studio exhaustive verification audit evidence.')
  }
  if (providerSlotHydrationProofStatus !== 'pass') {
    reasons.push('Dynamic/hybrid publish requires hydrated provider-slot browser verification evidence.')
  }
  if (runtimeHonoredActionCount > 0 && runtimeProvenActionCount < runtimeHonoredActionCount) {
    reasons.push('Dynamic/hybrid publish requires all runtime-honored actions to be proven or adapter-proven.')
  }

  return {
    schemaVersion: 'studio-proof-readiness-gate.v1',
    status: reasons.length === 0 ? 'pass' : 'blocked',
    required: true,
    fullCoverageStatus,
    exhaustiveProofStatus,
    providerSlotHydrationProofStatus,
    runtimeProvenActionCount,
    runtimeHonoredActionCount,
    reasons,
  }
}

function evaluateTargetMatrixGate(
  targetMatrix: Record<string, unknown> | null,
  projectPlan?: StudioProjectPlan | null,
): StudioTargetMatrixGate {
  const targetEntries = readRendererTargetEntries(targetMatrix)
  const rendererTargets = Object.fromEntries(
    Object.entries(targetEntries).map(([target, entry]) => [target, entry.readiness]),
  )
  const requestedTargets = normalizeRuntimeTargets(projectPlan?.runtimeTargets ?? [])
  const requestedNativeTargets = requestedTargets.filter((target) => target === 'rn' || target === 'flutter' || target === 'mobile')
  const required = requestedNativeTargets.length > 0
  const readyTargets = Object.entries(rendererTargets)
    .filter(([, readiness]) => readiness === 'ready')
    .map(([target]) => target)
    .sort()
  const partialTargets = Object.entries(rendererTargets)
    .filter(([, readiness]) => readiness === 'partial')
    .map(([target]) => target)
    .sort()
  const missingTargets = required
    ? requestedTargets.filter((target) => {
        if (target === 'mobile') return !rendererTargets.rn && !rendererTargets.flutter
        return target === 'rn' || target === 'flutter'
          ? !rendererTargets[target]
          : false
      })
    : []
  const nativePartialTargets = partialTargets.filter((target) => target !== 'web')
  const nativeTargetsToValidate = requestedNativeTargets
    .flatMap((target) => {
      if (target === 'mobile') {
        return (['rn', 'flutter'] as const).filter((nativeTarget) => targetEntries[nativeTarget])
      }
      return [target]
    })
    .filter((target): target is 'rn' | 'flutter' => target === 'rn' || target === 'flutter')
  const nativeAxisGaps = Object.fromEntries(
    nativeTargetsToValidate.flatMap((target) => {
      const entry = targetEntries[target]
      if (!entry || entry.readiness !== 'ready') return []
      const gaps = requiredNativeAxisGaps(entry.entry)
      return gaps.length > 0 ? [[target, gaps]] : []
    }),
  )
  const reasons: string[] = []

  if (required && Object.keys(rendererTargets).length === 0) {
    reasons.push('Native runtime targets require cross-renderer target matrix evidence before publishing.')
  }
  if (required && missingTargets.length > 0) {
    reasons.push(`Native runtime targets are missing target matrix evidence: ${missingTargets.join(', ')}.`)
  }
  if (required && nativePartialTargets.length > 0) {
    reasons.push(`Native runtime targets remain partial: ${nativePartialTargets.join(', ')}.`)
    reasons.push(...readRendererTargetPartialReasons(targetMatrix, nativePartialTargets))
  }
  for (const [target, gaps] of Object.entries(nativeAxisGaps)) {
    reasons.push(`Native runtime target ${target} is marked ready but missing passing verification axes: ${gaps.join(', ')}.`)
  }

  return {
    schemaVersion: 'studio-target-matrix-gate.v1',
    status: reasons.length > 0 ? 'blocked' : Object.keys(rendererTargets).length > 0 ? 'pass' : 'not-required',
    required,
    requestedTargets,
    readyTargets,
    partialTargets,
    missingTargets,
    rendererTargets,
    nativeAxisGaps,
    reasons,
  }
}

function evaluateEndUserProofGate(
  source: unknown,
  packagingMode: StudioPublishReadinessGate['packagingMode'],
  proofPolicy: StudioPublishProofPolicy = { standardWebGeneratedApp: false },
): StudioEndUserProofGate {
  const required = packagingMode === 'dynamic' || packagingMode === 'hybrid'
  const standardWebGeneratedApp = proofPolicy.standardWebGeneratedApp
  const generationEvidence = readFirstRecordDeep(source, ['generationEvidence'])
  const proof =
    readFirstRecordDeep(source, ['endUserAppProof']) ??
    readRecord(generationEvidence?.endUserAppProof)
  const speed = readRecord(proof?.speed)
  const quality = readRecord(proof?.quality)
  const validationRag = readRecord(proof?.validationRag)
  const visualRichness = readRecord(quality?.visualRichness)
  const designStyleSwitch = readRecord(quality?.designStyleSwitch) ?? readRecord(proof?.designStyleSwitch)
  const designSandbox = readRecord(proof?.designSandbox)
  const scenarioMatrix = readRecord(proof?.scenarioMatrix)
  const adaptiveRuntime = readRecord(proof?.adaptiveRuntime)
  const adaptiveAxisCoverage = readRecord(adaptiveRuntime?.axisCoverage)
  const stateBoundary = readRecord(adaptiveRuntime?.stateBoundary)
  const datasourceStateProjection = readRecord(adaptiveRuntime?.datasourceStateProjection)
  const referoBatch = readRecord(proof?.referoBatch)
  const speedStatus = readString(speed?.status) ?? 'missing'
  const qualityStatus = readString(quality?.status) ?? 'missing'
  const validationRagStatus = readString(validationRag?.status) ?? 'missing'
  const validationRagRequiredKindCount = readArray(validationRag?.requiredKinds)?.length ?? 0
  const validationRagAcceptedEvidenceCount = readArray(validationRag?.acceptedEvidenceIds)?.length ?? 0
  const validationRagMissingKinds = readStringList(validationRag?.missingKinds)
  const visualRichnessStatus = readString(visualRichness?.status) ?? 'missing'
  const visualRichnessScenarioCount = readNumber(visualRichness?.scenarioCount)
  const visualRichnessRequiredScenarioCount = readNumber(visualRichness?.requiredScenarioCount)
  const visualRichnessMinDistinctSurfaceColorCount = readNumber(visualRichness?.minDistinctSurfaceColorCount)
  const visualRichnessShoppingProminentMediaCount = readNumber(visualRichness?.shoppingProminentMediaCount)
  const visualRichnessMinSemanticVisualTypeCount = readNumber(visualRichness?.minSemanticVisualTypeCount)
  const designStyleSwitchStatus = readString(designStyleSwitch?.status) ?? 'missing'
  const designStyleSwitchMutationApi = readString(designStyleSwitch?.mutationApi) ?? 'missing'
  const designStyleSwitchPrimaryChanged =
    designStyleSwitch?.primaryBackgroundChanged === true ||
    (
      !isTransparentCssColor(readString(designStyleSwitch?.basePrimaryBackground)) &&
      !isTransparentCssColor(readString(designStyleSwitch?.targetPrimaryBackground)) &&
      readString(designStyleSwitch?.basePrimaryBackground) !== readString(designStyleSwitch?.targetPrimaryBackground)
    )
  const designStyleSwitchStudioHtmlChanged =
    designStyleSwitch?.studioHtmlPrimaryChanged === true ||
    designStyleSwitch?.studioHtmlHasCompleteTokenAttr === true
  const designStyleSwitchStudioBodyChanged =
    designStyleSwitch?.studioBodyPrimaryChanged === true ||
    designStyleSwitch?.studioBodyHasCompleteTokenAttr === true
  const designSandboxStatus = readString(designSandbox?.status) ?? 'missing'
  const scenarioMatrixStatus = readString(scenarioMatrix?.status) ?? 'missing'
  const adaptiveRuntimeStatus = readString(adaptiveRuntime?.status) ?? 'missing'
  const adaptiveAxisCoverageStatus = readString(adaptiveAxisCoverage?.status) ?? 'missing'
  const adaptiveAxisCoveragePassed = readArray(adaptiveAxisCoverage?.passedAxes)?.length ?? 0
  const adaptiveAxisCoverageRequired = readArray(adaptiveAxisCoverage?.requiredAxes)?.length ?? 0
  const adaptiveAxisCoverageMissing = readStringList(adaptiveAxisCoverage?.missingAxes)
  const stateBoundaryStatus = readString(stateBoundary?.status) ?? 'missing'
  const datasourceStateProjectionStatus = readString(datasourceStateProjection?.status) ?? 'missing'
  const datasourceStateProjectionPassCount = readNumber(datasourceStateProjection?.passCount)
  const datasourceStateProjectionScenarioCount = readNumber(datasourceStateProjection?.scenarioCount)
  const datasourceStateProjectionRequiredScenarioCount = readNumber(datasourceStateProjection?.requiredScenarioCount)
  const datasourceStateProjectionStateDomains = readStringList(datasourceStateProjection?.stateDomains)
  const datasourceStateProjectionDatasourceKinds = readStringList(datasourceStateProjection?.datasourceKinds)
  const datasourceStateProjectionProjectionTextCount = readStringList(datasourceStateProjection?.projectionTexts).length
  const referoBatchStatus = readString(referoBatch?.status) ?? 'missing'

  if (!required) {
    return {
      schemaVersion: 'studio-end-user-proof-publish-gate.v1',
      status: 'not-required',
      required: false,
      speedStatus,
      qualityStatus,
      validationRagStatus,
      validationRagRequiredKindCount,
      validationRagAcceptedEvidenceCount,
      validationRagMissingKinds,
      visualRichnessStatus,
      visualRichnessScenarioCount,
      visualRichnessRequiredScenarioCount,
      visualRichnessMinDistinctSurfaceColorCount,
      visualRichnessShoppingProminentMediaCount,
      visualRichnessMinSemanticVisualTypeCount,
      designStyleSwitchStatus,
      designStyleSwitchPrimaryChanged,
      designStyleSwitchMutationApi,
      designStyleSwitchStudioHtmlChanged,
      designStyleSwitchStudioBodyChanged,
      designSandboxStatus,
      scenarioMatrixStatus,
      adaptiveRuntimeStatus,
      adaptiveAxisCoverageStatus,
      adaptiveAxisCoveragePassed,
      adaptiveAxisCoverageRequired,
      adaptiveAxisCoverageMissing,
      stateBoundaryStatus,
      datasourceStateProjectionStatus,
      datasourceStateProjectionPassCount,
      datasourceStateProjectionScenarioCount,
      datasourceStateProjectionRequiredScenarioCount,
      datasourceStateProjectionStateDomains,
      datasourceStateProjectionDatasourceKinds,
      datasourceStateProjectionProjectionTextCount,
      referoBatchStatus,
      reasons: [],
    }
  }

  const reasons: string[] = []
  if (!proof) {
    reasons.push('Dynamic/hybrid publish requires Studio end-user browser verification evidence.')
  }
  if (speedStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires passing end-user speed verification evidence; current status is ${speedStatus}.`)
  }
  if (qualityStatus !== 'pass') {
    const qualityReasons = readStringList(quality?.reasons)
    const blockingQualityReasons = standardWebGeneratedApp
      ? qualityReasons.filter((reason) => !isStandardWebGeneratedAppAdvisoryQualityReason(reason))
      : qualityReasons
    if (!standardWebGeneratedApp || blockingQualityReasons.length > 0) {
      reasons.push(`Dynamic/hybrid publish requires passing end-user quality verification evidence; current status is ${qualityStatus}.`)
      reasons.push(...blockingQualityReasons)
    }
  }
  if (validationRagStatus !== 'pass' && !standardWebGeneratedApp) {
    reasons.push(`Dynamic/hybrid publish requires passing Agentic RAG generation validation evidence; current status is ${validationRagStatus}.`)
    reasons.push(...readStringList(validationRag?.reasons))
  } else if (!standardWebGeneratedApp) {
    if (validationRagRequiredKindCount <= 0) {
      reasons.push('Dynamic/hybrid publish requires Agentic RAG validation required-kind evidence.')
    }
    if (validationRagAcceptedEvidenceCount < validationRagRequiredKindCount) {
      reasons.push(`Dynamic/hybrid publish requires admitted Agentic RAG validation evidence for every required kind; accepted ${validationRagAcceptedEvidenceCount}/${validationRagRequiredKindCount}.`)
    }
    if (validationRagMissingKinds.length > 0) {
      reasons.push(`Dynamic/hybrid publish has missing Agentic RAG validation kinds: ${validationRagMissingKinds.join(', ')}.`)
    }
  }
  if (visualRichnessStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires generated-app visual-richness verification evidence; current status is ${visualRichnessStatus}.`)
    reasons.push(...readStringList(visualRichness?.reasons))
  } else {
    if (visualRichnessRequiredScenarioCount <= 0) {
      reasons.push('Dynamic/hybrid publish requires a visual-richness required scenario denominator.')
    }
    if (visualRichnessScenarioCount < visualRichnessRequiredScenarioCount) {
      reasons.push(`Dynamic/hybrid publish requires visual-richness verification evidence for every required scenario; covered ${visualRichnessScenarioCount}/${visualRichnessRequiredScenarioCount}.`)
    }
    if (visualRichnessMinDistinctSurfaceColorCount < 3) {
      reasons.push(`Dynamic/hybrid publish requires at least 3 distinct surface colors in every scenario; minimum is ${visualRichnessMinDistinctSurfaceColorCount}.`)
    }
    if (visualRichnessShoppingProminentMediaCount < 3) {
      reasons.push(`Dynamic/hybrid publish requires at least 3 prominent shopping product media items; current count is ${visualRichnessShoppingProminentMediaCount}.`)
    }
    if (visualRichnessMinSemanticVisualTypeCount < 8) {
      reasons.push(`Dynamic/hybrid publish requires at least 8 semantic visual types in every scenario; minimum is ${visualRichnessMinSemanticVisualTypeCount}.`)
    }
  }
  if (designStyleSwitchStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires generated-app design style switch verification evidence; current status is ${designStyleSwitchStatus}.`)
    reasons.push(...readStringList(designStyleSwitch?.reasons))
  } else {
    if (designStyleSwitch?.structuralDomIdentical !== true) {
      reasons.push('Dynamic/hybrid publish requires design style switching without changing the generated-app DOM structure.')
    }
    if (!designStyleSwitchPrimaryChanged) {
      reasons.push('Dynamic/hybrid publish requires design style switching to change the rendered primary affordance.')
    }
    if (isTransparentCssColor(readString(designStyleSwitch?.basePrimaryBackground))) {
      reasons.push('Dynamic/hybrid publish requires a non-transparent base primary affordance before design style switching.')
    }
    if (isTransparentCssColor(readString(designStyleSwitch?.targetPrimaryBackground))) {
      reasons.push('Dynamic/hybrid publish requires a non-transparent target primary affordance after design style switching.')
    }
    if (designStyleSwitch?.appHostHasCompleteTokenAttr !== true) {
      reasons.push('Dynamic/hybrid publish requires the app render root to keep complete-token attrs during design style switching.')
    }
    if (!readString(designStyleSwitch?.targetPrimaryToken)) {
      reasons.push('Dynamic/hybrid publish requires target --primary token evidence on the generated-app render root.')
    }
    if (!readString(designStyleSwitch?.targetColorPrimaryToken)) {
      reasons.push('Dynamic/hybrid publish requires target --color-primary token evidence on the generated-app render root.')
    }
    if (designStyleSwitchMutationApi !== 'app-design-sandbox') {
      reasons.push('Dynamic/hybrid publish requires design style switching through the app-design sandbox API.')
    }
    if (designStyleSwitchStudioHtmlChanged) {
      reasons.push('Dynamic/hybrid publish blocks app-design switching that mutates Studio document html tokens.')
    }
    if (designStyleSwitchStudioBodyChanged) {
      reasons.push('Dynamic/hybrid publish blocks app-design switching that mutates Studio document body tokens.')
    }
  }
  if (designSandboxStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires generated-app design sandbox isolation verification evidence; current status is ${designSandboxStatus}.`)
    reasons.push(...readStringList(designSandbox?.reasons))
  } else {
    const portalInsideAppSurface = designSandbox?.portalInsideAppSurface ?? designSandbox?.portalInsideAppHost
    const appRenderRootHasCompleteTokenAttr =
      designSandbox?.appRenderRootHasCompleteTokenAttr ?? designSandbox?.appHostHasCompleteTokenAttr
    if (designSandbox?.appRootSandboxed !== true) {
      reasons.push('Dynamic/hybrid publish requires the generated-app root to be marked design-sandboxed.')
    }
    if (portalInsideAppSurface !== true) {
      reasons.push('Dynamic/hybrid publish requires generated-app portal roots to stay inside the generated-app surface.')
    }
    if (readString(designSandbox?.portalParentTag) === 'BODY') {
      reasons.push('Dynamic/hybrid publish cannot proceed while generated-app portal roots mount under document.body.')
    }
    if (designSandbox?.htmlHasAppStyleClass === true || designSandbox?.bodyHasAppStyleClass === true) {
      reasons.push('Dynamic/hybrid publish cannot proceed while generated-app style classes leak to Studio html/body.')
    }
    if (designSandbox?.htmlHasCompleteTokenAttr === true || designSandbox?.bodyHasCompleteTokenAttr === true) {
      reasons.push('Dynamic/hybrid publish cannot proceed while generated-app complete-token attrs leak to Studio html/body.')
    }
    if (readString(designSandbox?.appHostDesignScope) !== 'application') {
      reasons.push('Dynamic/hybrid publish requires the generated-app render root to be marked as application design scope.')
    }
    if (appRenderRootHasCompleteTokenAttr !== true) {
      reasons.push('Dynamic/hybrid publish requires the generated-app render root to carry complete-token attrs.')
    }
    if (readNumber(designSandbox?.outsideAppCompleteTokenScopeCount) > 0) {
      reasons.push('Dynamic/hybrid publish cannot proceed while generated-app complete-token scopes exist outside the app surface.')
    }
    if (readNumber(designSandbox?.outsideAppInlinePrimaryTokenCount) > 0) {
      reasons.push('Dynamic/hybrid publish cannot proceed while generated-app inline token styles exist outside the app surface.')
    }
    if (readNumber(designSandbox?.appStyleElementsInHeadCount) > 0) {
      reasons.push('Dynamic/hybrid publish cannot proceed while generated-app style elements exist in the Studio document head.')
    }
    if (readNumber(designSandbox?.outsideAppStyleElementCount) > 0) {
      reasons.push('Dynamic/hybrid publish cannot proceed while generated-app style elements exist outside the app surface.')
    }
    const appStyleElementCount = readNumber(designSandbox?.appStyleElementCount)
    if (appStyleElementCount > 0 && readNumber(designSandbox?.appStyleElementsInAppHostCount) !== appStyleElementCount) {
      reasons.push('Dynamic/hybrid publish requires every generated-app style element to be owned by the app surface.')
    }
    if (readNumber(designSandbox?.portalCount) <= 0) {
      reasons.push('Dynamic/hybrid publish requires an app-owned portal root for generated-app overlays.')
    }
    if (readString(designSandbox?.portalId) === 'cogo-app-portal-root') {
      reasons.push('Dynamic/hybrid publish requires app-specific portal root IDs, not the shared legacy portal ID.')
    }
    if (designSandbox?.portalHasStyleClass !== true) {
      reasons.push('Dynamic/hybrid publish requires the app portal root to carry the app style class.')
    }
    if (designSandbox?.portalHasCompleteTokenAttr !== true) {
      reasons.push('Dynamic/hybrid publish requires the app portal root to carry complete-token attrs.')
    }
  }
  if (scenarioMatrixStatus === 'fail' || (!standardWebGeneratedApp && scenarioMatrixStatus !== 'pass')) {
    reasons.push(`Dynamic/hybrid publish requires passing end-user scenario matrix verification evidence; current status is ${scenarioMatrixStatus}.`)
    reasons.push(...readStringList(scenarioMatrix?.reasons))
  }
  if (!standardWebGeneratedApp && adaptiveRuntimeStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires passing adaptive runtime verification evidence; current status is ${adaptiveRuntimeStatus}.`)
    reasons.push(...readStringList(adaptiveRuntime?.reasons))
  }
  if (!standardWebGeneratedApp && adaptiveAxisCoverageStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires passing adaptive axis coverage verification evidence; current status is ${adaptiveAxisCoverageStatus}.`)
    reasons.push(...readStringList(adaptiveAxisCoverage?.reasons))
  } else if (!standardWebGeneratedApp) {
    if (adaptiveAxisCoverageRequired <= 0) {
      reasons.push('Dynamic/hybrid publish requires at least one required adaptive axis in the end-user verification evidence.')
    }
    if (adaptiveAxisCoveragePassed < adaptiveAxisCoverageRequired) {
      reasons.push(`Dynamic/hybrid publish requires every adaptive axis to pass; passed ${adaptiveAxisCoveragePassed}/${adaptiveAxisCoverageRequired}.`)
    }
    if (adaptiveAxisCoverageMissing.length > 0) {
      reasons.push(`Dynamic/hybrid publish has missing adaptive axes: ${adaptiveAxisCoverageMissing.join(', ')}.`)
    }
  }
  if (!standardWebGeneratedApp && stateBoundaryStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires passing local/appData/_results state boundary verification evidence; current status is ${stateBoundaryStatus}.`)
    reasons.push(...readStringList(stateBoundary?.reasons))
  } else if (!standardWebGeneratedApp) {
    if ((readArray(stateBoundary?.localStateEvidencePaths)?.length ?? 0) <= 0) {
      reasons.push('Dynamic/hybrid publish requires local.* state boundary evidence.')
    }
    if ((readArray(stateBoundary?.globalStateEvidencePaths)?.length ?? 0) <= 0) {
      reasons.push('Dynamic/hybrid publish requires appData.* state boundary evidence.')
    }
    if ((readArray(stateBoundary?.resultStateEvidencePaths)?.length ?? 0) <= 0) {
      reasons.push('Dynamic/hybrid publish requires _results.* ActionFlow state boundary evidence.')
    }
    if ((readArray(stateBoundary?.legacyStateEvidencePaths)?.length ?? 0) > 0) {
      reasons.push('Dynamic/hybrid publish rejects legacy state prefixes in state boundary evidence.')
    }
  }
  if (!standardWebGeneratedApp && datasourceStateProjectionStatus !== 'pass') {
    reasons.push(`Dynamic/hybrid publish requires generated-app datasource/state projection verification evidence; current status is ${datasourceStateProjectionStatus}.`)
    reasons.push(...readStringList(datasourceStateProjection?.reasons))
  } else if (!standardWebGeneratedApp) {
    if (datasourceStateProjectionRequiredScenarioCount <= 0) {
      reasons.push('Dynamic/hybrid publish requires a datasource/state projection required scenario denominator.')
    }
    if (datasourceStateProjectionPassCount < datasourceStateProjectionRequiredScenarioCount) {
      reasons.push(`Dynamic/hybrid publish requires datasource/state projection verification evidence for every required scenario; passed ${datasourceStateProjectionPassCount}/${datasourceStateProjectionRequiredScenarioCount}.`)
    }
    if (datasourceStateProjectionScenarioCount < datasourceStateProjectionRequiredScenarioCount) {
      reasons.push(`Dynamic/hybrid publish requires datasource/state projection coverage for every required scenario; covered ${datasourceStateProjectionScenarioCount}/${datasourceStateProjectionRequiredScenarioCount}.`)
    }
    if (!datasourceStateProjectionStateDomains.includes('block_logic')) {
      reasons.push('Dynamic/hybrid publish requires datasource/state projection verification evidence to include block_logic state.')
    }
    if (!datasourceStateProjectionStateDomains.includes('application_global')) {
      reasons.push('Dynamic/hybrid publish requires datasource/state projection verification evidence to include application_global state.')
    }
    if (!datasourceStateProjectionStateDomains.includes('runtime')) {
      reasons.push('Dynamic/hybrid publish requires datasource/state projection verification evidence to include runtime result state.')
    }
    if (datasourceStateProjectionDatasourceKinds.length <= 0) {
      reasons.push('Dynamic/hybrid publish requires datasource/state projection verification evidence to include datasource kind evidence.')
    }
    if (datasourceStateProjectionProjectionTextCount <= 0) {
      reasons.push('Dynamic/hybrid publish requires datasource/state projection verification evidence to include rendered projection text evidence.')
    }
  }
  if (!standardWebGeneratedApp && referoBatchStatus === 'blocked') {
    reasons.push(...(readStringList(referoBatch?.blockedReasons).length > 0
      ? readStringList(referoBatch?.blockedReasons)
      : ['Studio Refero batch gate is blocked by end-user verification evidence.']))
  }

  return {
    schemaVersion: 'studio-end-user-proof-publish-gate.v1',
    status: reasons.length === 0 ? 'pass' : 'blocked',
    required: true,
    speedStatus,
    qualityStatus,
    validationRagStatus,
    validationRagRequiredKindCount,
    validationRagAcceptedEvidenceCount,
    validationRagMissingKinds,
    visualRichnessStatus,
    visualRichnessScenarioCount,
    visualRichnessRequiredScenarioCount,
    visualRichnessMinDistinctSurfaceColorCount,
    visualRichnessShoppingProminentMediaCount,
    visualRichnessMinSemanticVisualTypeCount,
    designStyleSwitchStatus,
    designStyleSwitchPrimaryChanged,
    designStyleSwitchMutationApi,
    designStyleSwitchStudioHtmlChanged,
    designStyleSwitchStudioBodyChanged,
    designSandboxStatus,
    scenarioMatrixStatus,
    adaptiveRuntimeStatus,
    adaptiveAxisCoverageStatus,
    adaptiveAxisCoveragePassed,
    adaptiveAxisCoverageRequired,
    adaptiveAxisCoverageMissing,
    stateBoundaryStatus,
    datasourceStateProjectionStatus,
    datasourceStateProjectionPassCount,
    datasourceStateProjectionScenarioCount,
    datasourceStateProjectionRequiredScenarioCount,
    datasourceStateProjectionStateDomains,
    datasourceStateProjectionDatasourceKinds,
    datasourceStateProjectionProjectionTextCount,
    referoBatchStatus,
    reasons,
  }
}

const PRODUCTION_ADAPTER_GROUNDINGS = new Set<RuntimeActionGroundingKind>([
  'native-adapter',
  'store-adapter',
  'datasource-adapter',
  'wallet-adapter',
  'media-adapter',
  'network-adapter',
  'ai-adapter',
  'cloudflare-adapter',
  'google-adapter',
  'payment-adapter',
  'realtime-adapter',
])

export function buildRenderPackPublishPayload(
  blocks: Record<string, unknown> | null | undefined,
): StudioRenderPackPublishPayload | null {
  const blockRecords = readRecord(blocks)
  if (!blockRecords) return null

  const payloadBlocks: StudioRenderPackPublishPayload['blocks'] = {}
  let errorCount = 0
  for (const [blockName, block] of Object.entries(blockRecords)) {
    if (!hasRenderableRoot(block)) continue
    const compiledDesign = compileCogoDesignContract(block as Record<string, unknown>)
    const pack = compileCogoRenderPack(compiledDesign.blockJson as Parameters<typeof compileCogoRenderPack>[0])
    const bytes = encodeRenderPack(pack)
    const diagnostics = pack.diagnostics.map((diagnostic) => {
      if (diagnostic.severity === 'error') errorCount += 1
      return { ...diagnostic }
    })
    payloadBlocks[blockName] = {
      schemaVersion: pack.schemaVersion,
      encoding: 'base64',
      bytes: bytesToBase64(bytes),
      byteLength: bytes.byteLength,
      diagnostics,
    }
  }

  const blockCount = Object.keys(payloadBlocks).length
  if (blockCount === 0) return null
  return {
    schemaVersion: 'studio-render-pack-publish-payload.v1',
    transport: 'RPK1',
    encoding: 'base64',
    blockCount,
    errorCount,
    blocks: payloadBlocks,
  }
}

function attachRenderPackPublishPayload(bundle: LoadedApp): LoadedApp {
  const payload = buildRenderPackPublishPayload(bundle.blocks as Record<string, unknown> | undefined)
  if (!payload) return bundle
  return {
    ...bundle,
    renderPackPublishPayload: payload,
    metadata: {
      ...(readRecord((bundle as unknown as Record<string, unknown>).metadata) ?? {}),
      renderPackPublishPayload: {
        schemaVersion: payload.schemaVersion,
        transport: payload.transport,
        blockCount: payload.blockCount,
        errorCount: payload.errorCount,
        status: payload.errorCount === 0 ? 'pass' : 'blocked',
      },
    },
  } as LoadedApp
}

export function mergeStoreProjectForPublishBundle(
  bundle: LoadedApp,
  storeProject: LoadedApp | null | undefined,
  dbHasBlocks: boolean,
): LoadedApp {
  if (!storeProject) return bundle

  const bundleRecord = bundle as unknown as Record<string, unknown>
  const storeRecord = storeProject as unknown as Record<string, unknown>
  const storeMetadata = readRecord(storeRecord.metadata)
  const bundleMetadata = readRecord(bundleRecord.metadata) ?? {}
  const bundleManifest = readRecord(bundle.manifest) ?? {}
  const storeManifest = readRecord(storeProject.manifest) ?? {}
  const mergedManifest = mergeManifestWithDatasourceReadinessAuthority(
    bundleManifest,
    storeManifest,
  )
  const mergedMetadata = storeMetadata
    ? { ...bundleMetadata, ...storeMetadata }
    : { ...bundleMetadata }
  // The freshly loaded server row owns readiness evidence and project
  // identity. Unsaved creator content may overlay the publish bundle, but a
  // stale tab must neither erase nor replace these server projections.
  for (const key of [
    'datasourceReadinessSidecar',
    'datasourceReadinessImportQuarantine',
    'studioProjectId',
  ] as const) {
    if (key in bundleMetadata) mergedMetadata[key] = bundleMetadata[key]
    else if (
      key === 'datasourceReadinessSidecar' &&
      shouldRetainNonAuthorizingDatasourceReadinessEvidence(storeMetadata?.[key])
    ) mergedMetadata[key] = storeMetadata?.[key]
    else delete mergedMetadata[key]
  }
  const next: Record<string, unknown> = {
    ...bundleRecord,
    manifest: mergedManifest,
    ...(Object.keys(mergedMetadata).length > 0
      ? {
          metadata: mergedMetadata,
        }
      : {}),
  }

  if (!dbHasBlocks && Object.keys(storeProject.blocks ?? {}).length > 0) {
    next.blocks = storeProject.blocks
    next.flows = storeProject.flows ?? bundle.flows
    next.tokens = storeProject.tokens ?? bundle.tokens
  }

  return next as unknown as LoadedApp
}

function evaluateRenderPackPackagingGate(
  source: unknown,
  packagingMode: StudioPublishReadinessGate['packagingMode'],
): StudioRenderPackPackagingGate {
  const required = packagingMode === 'dynamic' || packagingMode === 'hybrid'
  if (!required) {
    return {
      schemaVersion: 'studio-render-pack-packaging-gate.v1',
      status: 'not-required',
      required: false,
      transport: 'RPK1',
      blockCount: 0,
      errorCount: 0,
      reasons: [],
    }
  }

  const explicit = readFirstRecordDeep(source, ['renderPackPublishPayload', 'renderPackPackaging', 'renderPack'])
  const explicitBlockCount = readNumber(explicit?.blockCount)
  const explicitErrorCount = readNumber(explicit?.errorCount)
  const explicitTransport = readString(explicit?.transport)
  if (explicit && explicitTransport === 'RPK1' && explicitBlockCount > 0 && explicitErrorCount === 0) {
    return {
      schemaVersion: 'studio-render-pack-packaging-gate.v1',
      status: 'pass',
      required: true,
      transport: 'RPK1',
      blockCount: explicitBlockCount,
      errorCount: 0,
      reasons: [],
    }
  }

  const payload = buildRenderPackPublishPayload(readRecord(source)?.blocks as Record<string, unknown> | undefined)
  const blockCount = payload?.blockCount ?? explicitBlockCount
  const errorCount = payload?.errorCount ?? explicitErrorCount
  const ok = blockCount > 0 && errorCount === 0
  return {
    schemaVersion: 'studio-render-pack-packaging-gate.v1',
    status: ok ? 'pass' : 'blocked',
    required: true,
    transport: 'RPK1',
    blockCount,
    errorCount,
    reasons: ok ? [] : ['Dynamic/hybrid publish requires RPK1 RenderPack packaging evidence.'],
  }
}

function hasRenderableRoot(block: unknown): boolean {
  const record = readRecord(block)
  return Boolean(readRecord(record?.ui)?.root ?? record?.root)
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize)
    binary += String.fromCharCode(...chunk)
  }
  return btoa(binary)
}

const PRODUCTION_ADAPTER_EVIDENCE_ALIASES: Record<string, string[]> = {
  signedManifest: ['signedManifest'],
  actionAllowlist: ['actionAllowlist', 'allowedActions'],
  adapterEntitlements: ['adapterEntitlements', 'serviceEntitlements', 'entitlements'],
  credentialPolicy: ['credentialPolicy', 'credentialProxy', 'serverProxyPolicy', 'secretPolicy'],
  networkPolicy: ['networkPolicy', 'egressPolicy', 'adapterNetworkPolicy'],
  datasourceEntitlements: ['datasourceEntitlements', 'datasourceEntitlement', 'datasourceAccess'],
  adapterSmokeProof: ['adapterSmokeProof', 'productionAdapterSmokeProof', 'liveAdapterSmokeProof'],
}

const FLOW_ACTION_CHILD_KEYS = [
  'steps',
  'actions',
  'then',
  'else',
  'branches',
  'cases',
  'rollback',
  'onSuccess',
  'onFailure',
] as const

const META_ACTIONS_WITH_SUBACTION_PARAMS = new Set([
  'batchAction',
  'queueAction',
  'retryAction',
  'throttleAction',
  'debounceAction',
  'transactionAction',
])

function evaluateProductionAdapterGate(source: unknown): StudioProductionAdapterGate {
  const adapterActions = collectProductionAdapterActions(source)
  const datasourceIds = collectDatasourceIds(source)
  const datasourceRequired = datasourceIds.length > 0 ||
    adapterActions.some((action) => {
      const grounding = getRuntimeActionGrounding(action)
      return grounding?.grounding === 'datasource-adapter' || grounding?.action === 'loadDatasource'
    })

  if (adapterActions.length === 0 && !datasourceRequired) {
    return {
      schemaVersion: 'studio-production-adapter-gate.v1',
      status: 'not-required',
      required: false,
      adapterActions: [],
      datasourceRequired: false,
      requiredEvidence: [],
      missingEvidence: [],
      reasons: [],
    }
  }

  const requiredEvidence = [
    'signedManifest',
    'actionAllowlist',
    'adapterEntitlements',
    'credentialPolicy',
    'networkPolicy',
    ...(datasourceRequired ? ['datasourceEntitlements'] : []),
    'adapterSmokeProof',
  ]
  const missingEvidence = requiredEvidence.filter((key) => !hasProductionAdapterEvidence(source, key, adapterActions, datasourceIds))
  const actionList = adapterActions.length > 0 ? adapterActions.join(', ') : 'datasource definitions'
  const reasons = missingEvidence.map((key) => {
    const label = formatSecurityEvidenceLabel(key)
    const verb = label.endsWith('entitlements') ? 'are' : 'is'
    return `${label} ${verb} required before publishing adapter-backed runtime access (${actionList}).`
  })

  return {
    schemaVersion: 'studio-production-adapter-gate.v1',
    status: missingEvidence.length === 0 ? 'pass' : 'blocked',
    required: true,
    adapterActions,
    datasourceRequired,
    requiredEvidence,
    missingEvidence,
    reasons,
  }
}

function collectProductionAdapterActions(source: unknown): string[] {
  const actions = new Set<string>()
  for (const flowSource of collectFlowSources(source)) {
    collectActionNames(flowSource, actions)
  }
  return [...actions]
    .filter((action) => {
      const grounding = getRuntimeActionGrounding(action)
      return grounding ? PRODUCTION_ADAPTER_GROUNDINGS.has(grounding.grounding) : false
    })
    .sort()
}

function collectFlowSources(source: unknown): unknown[] {
  const record = readRecord(source)
  if (!record) return []
  const sources: unknown[] = [record.flow, record.flows]
  const manifest = readRecord(record.manifest)
  if (manifest) sources.push(manifest.flow, manifest.flows)
  const blocks = readRecord(record.blocks)
  if (blocks) {
    for (const block of Object.values(blocks)) {
      const blockRecord = readRecord(block)
      if (!blockRecord) continue
      sources.push(blockRecord.flow, blockRecord.flows)
      const blockManifest = readRecord(blockRecord.manifest)
      if (blockManifest) sources.push(blockManifest.flow, blockManifest.flows)
    }
  }
  return sources.filter((item) => item !== undefined && item !== null)
}

function collectActionNames(value: unknown, actions: Set<string>): void {
  if (Array.isArray(value)) {
    for (const item of value) collectActionNames(item, actions)
    return
  }
  const record = readRecord(value)
  if (!record) return

  const action = readString(record.action)
  if (action) actions.add(action)
  if (record.action && typeof record.action === 'object') collectActionNames(record.action, actions)

  for (const key of FLOW_ACTION_CHILD_KEYS) {
    collectActionNames(record[key], actions)
  }
  if (action && META_ACTIONS_WITH_SUBACTION_PARAMS.has(action)) {
    collectActionNames(record.params, actions)
  }
  if (!action) {
    for (const value of Object.values(record)) {
      collectActionNames(value, actions)
    }
  }
}

function collectDatasourceIds(source: unknown): string[] {
  const ids = new Set<string>()
  const collect = (value: unknown) => {
    if (typeof value === 'string') {
      if (value.trim()) ids.add(value.trim())
      return
    }
    if (Array.isArray(value)) {
      for (const item of value) collect(item)
      return
    }
    const record = readRecord(value)
    if (!record || Object.keys(record).length === 0) return
    const explicitName = readString(record.name) ?? readString(record.id) ?? readString(record.datasourceId)
    if (explicitName) ids.add(explicitName)
    for (const [key, entry] of Object.entries(record)) {
      if (key === 'name' || key === 'id' || key === 'datasourceId') continue
      if (key === 'sources' || key === 'datasources' || key === 'datasource' || key === 'data') {
        collect(entry)
        continue
      }
      ids.add(key)
      const entryRecord = readRecord(entry)
      const nestedName = readString(entryRecord?.name) ?? readString(entryRecord?.id) ?? readString(entryRecord?.datasourceId)
      if (nestedName) ids.add(nestedName)
    }
  }
  const collectFromRecord = (record: Record<string, unknown> | null | undefined) => {
    if (!record) return
    collect(record.datasources)
    collect(record.datasource)
    collect(record.data)
  }

  const record = readRecord(source)
  if (!record) return []
  collectFromRecord(record)
  const manifest = readRecord(record.manifest)
  collectFromRecord(manifest)
  const blocks = readRecord(record.blocks)
  if (blocks) for (const block of Object.values(blocks)) {
    const blockRecord = readRecord(block)
    collectFromRecord(blockRecord)
  }
  return [...ids].filter((id) => id !== 'sources').sort()
}

function hasProductionAdapterEvidence(source: unknown, key: string, adapterActions: string[], datasourceIds: string[]): boolean {
  if (key === 'actionAllowlist') return hasActionAllowlistCoverage(source, adapterActions)
  if (key === 'datasourceEntitlements') return hasDatasourceEntitlementCoverage(source, datasourceIds)
  if (key === 'adapterSmokeProof') return hasAdapterSmokeProofCoverage(source, adapterActions, datasourceIds)
  return PRODUCTION_ADAPTER_EVIDENCE_ALIASES[key]?.some((alias) => hasPublishSecurityEvidence(source, alias)) ?? false
}

function hasAdapterSmokeProofCoverage(source: unknown, adapterActions: string[], datasourceIds: string[]): boolean {
  const evidence = readFirstRecordDeep(source, [
    'publishSecurityEvidence',
    'securityEvidence',
    'packagingSecurity',
  ])
  const aliases = PRODUCTION_ADAPTER_EVIDENCE_ALIASES.adapterSmokeProof
  const rawValues = aliases.map((alias) => evidence?.[alias]).filter((value) => value !== undefined)
  return rawValues.some((value) => {
    const record = readRecord(value)
    if (!record) return false
    if (record.mock === true || record.isMock === true || record.localOnly === true) return false
    const statusOk = record.status === 'pass' || record.ok === true
    if (!statusOk) return false
    const environment = (readString(record.environment) ?? readString(record.mode) ?? '').toLowerCase()
    const liveTarget = environment === 'production' || environment === 'prod' || environment === 'live' || record.production === true || record.live === true
    if (!liveTarget) return false
    const coveredActions = readStringSet([
      record.actions,
      record.adapterActions,
      record.coveredActions,
      record.targetActions,
    ])
    const coveredDatasources = readStringSet([
      record.datasources,
      record.datasourceIds,
      record.coveredDatasources,
      record.entitledDatasources,
    ])
    const actionsCovered = adapterActions.every((action) => coveredActions.has(action))
    const datasourcesCovered = datasourceIds.every((id) => coveredDatasources.has(id))
    return actionsCovered && datasourcesCovered
  })
}

function hasDatasourceEntitlementCoverage(source: unknown, datasourceIds: string[]): boolean {
  const evidence = readFirstRecordDeep(source, [
    'publishSecurityEvidence',
    'securityEvidence',
    'packagingSecurity',
  ])
  const aliases = PRODUCTION_ADAPTER_EVIDENCE_ALIASES.datasourceEntitlements
  const rawValues = aliases.map((alias) => evidence?.[alias]).filter((value) => value !== undefined)
  if (datasourceIds.length === 0) return rawValues.some(hasAnyEvidence)
  const entitled = new Set<string>()
  for (const value of rawValues) {
    if (typeof value === 'string') {
      entitled.add(value)
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string') entitled.add(item)
        const itemRecord = readRecord(item)
        const itemName = readString(itemRecord?.name) ?? readString(itemRecord?.id) ?? readString(itemRecord?.datasourceId)
        if (itemName) entitled.add(itemName)
      }
      continue
    }
    const record = readRecord(value)
    if (!record) continue
    for (const listKey of ['datasources', 'sources', 'allowedDatasources', 'entitledDatasources', 'entries']) {
      const list = record[listKey]
      if (Array.isArray(list)) {
        for (const item of list) {
          if (typeof item === 'string') entitled.add(item)
          const itemRecord = readRecord(item)
          const itemName = readString(itemRecord?.name) ?? readString(itemRecord?.id) ?? readString(itemRecord?.datasourceId)
          if (itemName) entitled.add(itemName)
        }
      }
    }
  }
  return datasourceIds.every((id) => entitled.has(id))
}

function hasActionAllowlistCoverage(source: unknown, adapterActions: string[]): boolean {
  const evidence = readFirstRecordDeep(source, [
    'publishSecurityEvidence',
    'securityEvidence',
    'packagingSecurity',
  ])
  const aliases = PRODUCTION_ADAPTER_EVIDENCE_ALIASES.actionAllowlist
  const rawValues = aliases.map((alias) => evidence?.[alias]).filter((value) => value !== undefined)
  if (adapterActions.length === 0) return rawValues.some(hasAnyEvidence)
  const allowlisted = new Set<string>()
  let explicitPass = false
  for (const value of rawValues) {
    if (value === true) {
      explicitPass = true
      continue
    }
    if (typeof value === 'string') {
      allowlisted.add(value)
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) if (typeof item === 'string') allowlisted.add(item)
      continue
    }
    const record = readRecord(value)
    if (!record) continue
    if (record.status === 'pass' || record.ok === true) explicitPass = true
    for (const listKey of ['actions', 'allowedActions', 'coveredActions', 'entries']) {
      const list = record[listKey]
      if (Array.isArray(list)) {
        for (const item of list) if (typeof item === 'string') allowlisted.add(item)
      }
    }
  }
  if (explicitPass && allowlisted.size === 0) return true
  return adapterActions.every((action) => allowlisted.has(action))
}

function hasAnyEvidence(value: unknown): boolean {
  if (value === true) return true
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  const record = readRecord(value)
  return Boolean(record && (record.status === 'pass' || record.ok === true || Object.keys(record).length > 0))
}

function readFirstRecordDeep(source: unknown, keys: string[]): Record<string, unknown> | null {
  const candidates = [
    source,
    readRecord(source)?.metadata,
    readRecord(source)?.meta,
    readRecord(readRecord(source)?.manifest),
    readRecord(readRecord(source)?.manifest)?.metadata,
    readRecord(readRecord(source)?.manifest)?.meta,
  ]

  const blocks = readRecord(readRecord(source)?.blocks)
  if (blocks) {
    for (const block of Object.values(blocks)) {
      const blockRecord = readRecord(block)
      candidates.push(blockRecord, readRecord(blockRecord?.metadata), readRecord(blockRecord?.meta))
    }
  }

  for (const candidate of candidates) {
    const record = readRecord(candidate)
    if (!record) continue
    for (const key of keys) {
      const value = readRecord(record[key])
      if (value) return value
    }
  }
  return null
}

function readRecordDeep(source: unknown, keys: string[]): Record<string, unknown> | null {
  let current: unknown = source
  for (const key of keys) {
    current = readRecord(current)?.[key]
  }
  return readRecord(current)
}

function hasPublishSecurityEvidence(source: unknown, key: string): boolean {
  const evidence = readFirstRecordDeep(source, [
    'publishSecurityEvidence',
    'securityEvidence',
    'packagingSecurity',
  ])
  const value = evidence?.[key]
  if (value === true) return true
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  const record = readRecord(value)
  if (!record) return false
  return record.status === 'pass' || record.ok === true || Object.keys(record).length > 0
}

function readRendererTargetReadiness(targetMatrix: Record<string, unknown> | null): Record<string, string> {
  return Object.fromEntries(
    Object.entries(readRendererTargetEntries(targetMatrix)).map(([target, entry]) => [target, entry.readiness]),
  )
}

function readRendererTargetEntries(
  targetMatrix: Record<string, unknown> | null,
): Record<string, { readiness: string; entry: Record<string, unknown> }> {
  const targets = readRecord(targetMatrix?.targets)
  if (!targets) return {}
  return Object.fromEntries(
    Object.entries(targets).flatMap(([target, value]) => {
      const entry = readRecord(value)
      const readiness = readString(entry?.readiness)
      return readiness && entry ? [[normalizeRuntimeTarget(target), { readiness, entry }]] : []
    }),
  )
}

function requiredNativeAxisGaps(targetEntry: Record<string, unknown>): string[] {
  const requiredAxes = [
    'r2VisualFaithfulness',
    'nativeRuntimeParity',
    'nativeLivePackaging',
  ]
  return requiredAxes.filter((axis) => readString(targetEntry[axis]) !== 'pass')
}

function normalizeRuntimeTargets(values: readonly string[]): string[] {
  const normalized = values
    .map(normalizeRuntimeTarget)
    .filter((target) => target.length > 0)
  return Array.from(new Set(normalized)).sort()
}

function normalizeRuntimeTarget(value: string): string {
  const target = value.trim().toLowerCase()
  if (target === 'react' || target === 'desktop') return 'web'
  if (target === 'react-native' || target === 'reactnative') return 'rn'
  return target
}

function readRendererTargetPartialReasons(
  targetMatrix: Record<string, unknown> | null,
  targetNames: string[],
): string[] {
  const targets = readRecord(targetMatrix?.targets)
  if (!targets) return []
  return targetNames.flatMap((targetName) => {
    const targetEntry = Object.entries(targets)
      .find(([rawTarget]) => normalizeRuntimeTarget(rawTarget) === targetName)
    const target = readRecord(targetEntry?.[1])
    return readStringList(target?.partialReasons)
      .map((reason) => `${targetName}: ${reason}`)
  })
}

function normalizePackagingMode(value: unknown): StudioPublishReadinessGate['packagingMode'] {
  return value === 'static' || value === 'dynamic' || value === 'hybrid' ? value : 'unknown'
}

function normalizeSecurityProfile(value: unknown): StudioPublishReadinessGate['securityProfile'] {
  return value === 'enterprise' || value === 'financial' ? value : 'standard'
}

function buildStudioPublishProofPolicy(
  source: unknown,
  input: {
    plan?: StudioProjectPlan | null
    packagingMode: StudioPublishReadinessGate['packagingMode']
    securityProfile: StudioPublishReadinessGate['securityProfile']
    generationEvidence: Record<string, unknown> | null
    targetMatrix: Record<string, unknown> | null
  },
): StudioPublishProofPolicy {
  const dynamic = input.packagingMode === 'dynamic' || input.packagingMode === 'hybrid'
  const generatedApp = readRecord(input.generationEvidence?.generatedApp)
  const gate = readFirstRecordDeep(source, [
    'generatedAppReadinessGate',
    'publishReadinessGate',
  ])
  const targetsFromEvidence = readStringList(readRecord(input.generationEvidence?.uiOntology)?.runtimeTargets)
  const targetsFromMatrix = Object.keys(readRendererTargetEntries(input.targetMatrix))
  const planTargets = normalizeRuntimeTargets(input.plan?.runtimeTargets ?? [])
  const matrixTargets = normalizeRuntimeTargets(targetsFromMatrix)
  const evidenceTargets = normalizeRuntimeTargets(targetsFromEvidence)
  const runtimeTargets = planTargets.length > 0
    ? planTargets
    : matrixTargets.length > 0
      ? matrixTargets
      : evidenceTargets
  const webOnly = runtimeTargets.length === 0 || runtimeTargets.every((target) => target === 'web')

  return {
    standardWebGeneratedApp: Boolean(
      dynamic &&
        input.securityProfile === 'standard' &&
        webOnly &&
        (generatedApp || gate) &&
        hasAppliedEndUserBrowserProof(source, input.generationEvidence),
    ),
  }
}

function hasAppliedEndUserBrowserProof(
  source: unknown,
  generationEvidence: Record<string, unknown> | null,
): boolean {
  const proof =
    readFirstRecordDeep(source, ['endUserAppProof']) ??
    readRecord(generationEvidence?.endUserAppProof)
  const quality = readRecord(proof?.quality)
  return Boolean(
    proof &&
      readNumber(quality?.renderedBlockCount) > 0 &&
      readNumber(quality?.screenshotArtifactCount) > 0 &&
      (quality?.nonFixturePrompt === true || proof.nonFixturePrompt === true),
  )
}

function isStandardWebGeneratedAppAdvisoryQualityReason(reason: string): boolean {
  return (
    /^Post-generation app validation result is missing\./i.test(reason) ||
    /^Average validation score is below floor/i.test(reason) ||
    /^Generated app has no validated blocks\./i.test(reason) ||
    /^Generated-app readiness gate is/i.test(reason) ||
    /^Generated app artifact parity is/i.test(reason) ||
    /^Full component\/action\/style\/FlatBuffer coverage audit is/i.test(reason) ||
    /^Exhaustive component SSR\/style\/runtime action proof audit is/i.test(reason) ||
    /^Agentic RAG validation:/i.test(reason)
  )
}

function normalizeIntegrationReadinessStatus(value: unknown): StudioIntegrationReadinessStatus {
  const status = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (status === 'ready' || status === 'pass' || status === 'passed' || status === 'ok') return 'ready'
  if (status === 'fallback' || status === 'degraded') return 'fallback'
  if (status === 'invalid' || status === 'fail' || status === 'failed' || status === 'error') return 'invalid'
  if (status === 'not-required' || status === 'not_required' || status === 'not-applicable' || status === 'not_applicable') return 'not-required'
  return 'gap'
}

function capitalizePackagingMode(value: StudioPublishReadinessGate['packagingMode']): string {
  if (!value || value === 'unknown') return 'Generated app'
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`
}

function isReportLikeProject(source: unknown, projectPlan?: StudioProjectPlan | null): boolean {
  if (
    readFirstRecordDeep(source, [
      'documentExportProductionGate',
      'documentExportEvidence',
      'reportWorkspace',
      'reportWorkspaceEvidence',
      'reportExportProductionGate',
    ])
  ) {
    return true
  }

  const manifest = readRecord(readRecord(source)?.manifest)
  const metadata = readRecord(readRecord(source)?.metadata)
  const generationEvidence = readFirstRecordDeep(source, ['generationEvidence'])
  const haystack = [
    projectPlan?.intent,
    projectPlan?.appClass,
    readString(manifest?.appClass),
    readString(manifest?.appType),
    readString(manifest?.applicationKind),
    readString(manifest?.reportApplicationSchemaVersion),
    readString(manifest?.reportApplicationKind),
    readString(manifest?.archetype),
    readString(manifest?.category),
    readString(metadata?.appClass),
    readString(metadata?.appType),
    readString(metadata?.applicationKind),
    readString(metadata?.reportApplicationSchemaVersion),
    readString(metadata?.reportApplicationKind),
    readString(metadata?.archetype),
    readString(metadata?.category),
    readString(readFirstRecordDeep(source, ['reportApplicationIntegration'])?.applicationKind),
    readString(readFirstRecordDeep(source, ['reportApplicationIntegration'])?.reportApplicationSchemaVersion),
    readString(generationEvidence?.appClass),
    readString(readRecord(generationEvidence?.generatedApp)?.appClass),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return /\b(report|reporting|excel|xlsx|spreadsheet|workbook|worksheet|document|publishing|pdf|docx|pptx|hwp|hwpx)\b/.test(haystack)
}

function normalizeAuditStatus(value: unknown): StudioProofReadinessGate['fullCoverageStatus'] {
  return value === 'pass' || value === 'partial' || value === 'blocked' ? value : 'missing'
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function readNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function readBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return undefined
  const normalized = value.trim().toLowerCase()
  if (normalized === 'true' || normalized === 'pass' || normalized === 'ready') return true
  if (normalized === 'false' || normalized === 'fail' || normalized === 'blocked') return false
  return undefined
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

interface StudioDatasourceReadinessManifestLocation {
  present: boolean
  value: unknown
}

interface StudioDatasourceReadinessManifestLocations {
  direct: StudioDatasourceReadinessManifestLocation
  metadata: StudioDatasourceReadinessManifestLocation
}

function readManifestDatasourceReadinessLocations(
  manifest: Record<string, unknown>,
): StudioDatasourceReadinessManifestLocations {
  const metadata = readRecord(manifest.metadata)
  return {
    direct: {
      present: 'datasourceReadinessSidecar' in manifest,
      value: manifest.datasourceReadinessSidecar,
    },
    metadata: {
      present: Boolean(metadata && 'datasourceReadinessSidecar' in metadata),
      value: metadata?.datasourceReadinessSidecar,
    },
  }
}

function shouldRetainNonAuthorizingDatasourceReadinessEvidence(
  value: unknown,
): boolean {
  const parsed = parseStudioDatasourceReadinessSidecar(value)
  if (parsed) return parsed.projectBinding.correlationStatus === 'unlinked'
  return readRecord(value)?.schemaVersion === 'studio-datasource-readiness-sidecar.v1'
}

function withDatasourceReadinessManifestLocationAuthority(
  manifest: Record<string, unknown>,
  authority: StudioDatasourceReadinessManifestLocations,
): Record<string, unknown> {
  const projected = { ...manifest }
  if (authority.direct.present) {
    projected.datasourceReadinessSidecar = authority.direct.value
  } else if (!shouldRetainNonAuthorizingDatasourceReadinessEvidence(
    projected.datasourceReadinessSidecar,
  )) {
    delete projected.datasourceReadinessSidecar
  }

  const existingMetadata = readRecord(manifest.metadata)
  if (existingMetadata || authority.metadata.present) {
    const metadata = { ...(existingMetadata ?? {}) }
    if (authority.metadata.present) {
      metadata.datasourceReadinessSidecar = authority.metadata.value
    } else if (!shouldRetainNonAuthorizingDatasourceReadinessEvidence(
      metadata.datasourceReadinessSidecar,
    )) {
      delete metadata.datasourceReadinessSidecar
    }
    projected.metadata = metadata
  }
  return projected
}

function mergeManifestWithDatasourceReadinessAuthority(
  serverManifest: Record<string, unknown>,
  editableManifest: Record<string, unknown>,
): Record<string, unknown> {
  const authority = readManifestDatasourceReadinessLocations(serverManifest)
  const merged = { ...serverManifest, ...editableManifest }

  const serverMetadata = readRecord(serverManifest.metadata)
  const editableMetadata = readRecord(editableManifest.metadata)
  if (serverMetadata || editableMetadata) {
    merged.metadata = { ...(serverMetadata ?? {}), ...(editableMetadata ?? {}) }
  }
  return withDatasourceReadinessManifestLocationAuthority(merged, authority)
}

function withCanonicalDatasourceReadinessManifestProjection(
  manifest: Record<string, unknown>,
  sidecar: unknown | null,
): Record<string, unknown> {
  return withDatasourceReadinessManifestLocationAuthority(manifest, {
    direct: { present: sidecar != null, value: sidecar },
    metadata: { present: false, value: undefined },
  })
}

function readStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0) : []
}

function readStringSet(values: unknown[]): Set<string> {
  const entries = new Set<string>()
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      entries.add(value.trim())
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) entries.add(item.trim())
        const record = readRecord(item)
        const name = readString(record?.name) ?? readString(record?.id) ?? readString(record?.action) ?? readString(record?.datasourceId)
        if (name) entries.add(name)
      }
      continue
    }
    const record = readRecord(value)
    const name = readString(record?.name) ?? readString(record?.id) ?? readString(record?.action) ?? readString(record?.datasourceId)
    if (name) entries.add(name)
  }
  return entries
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function isTransparentCssColor(value: string | null | undefined): boolean {
  const normalized = String(value ?? '').trim().toLowerCase()
  return (
    normalized.length === 0 ||
    normalized === 'transparent' ||
    normalized === 'rgba(0, 0, 0, 0)' ||
    normalized === 'rgb(0 0 0 / 0)' ||
    normalized === 'rgba(0 0 0 / 0)'
  )
}

function formatSecurityEvidenceLabel(key: string): string {
  return key.replace(/[A-Z]/g, (match) => ` ${match.toLowerCase()}`)
}

export interface ProjectMeta {
  id: string
  name: string
  slug: string | null
  description: string | null
  status: 'draft' | 'active' | 'published' | 'archived'
  app_target: string
  thumbnail: string | null
  version: number
  /** Platform-owned block artifact counter retained from the authenticated row. */
  artifact_revision?: number
  /** Exact composite block authority; never derived from generic `version`. */
  projectRevision?: StudioProjectServerRevisionRef
  updated_at: string
  /** design system preset bound to this project (nullable) */
  design_system_preset_id?: string | null
  /** Cogo app metadata persisted under settings.cogoApplicationMetadata */
  metadata?: Record<string, unknown>
  /** Manifest-attached datasource readiness retained outside replaceable app state. */
  datasourceReadinessManifestSidecar?: unknown | null
  /** Exact server-owned manifest locations, including conflicts and explicit clears. */
  datasourceReadinessManifestLocations?: StudioDatasourceReadinessManifestLocations
  /**
   * Project-declared terminology scope (mirrors `metadata.terminologyScope`),
   * e.g. `"app:wonbanking"`. Declared by the project, never synthesized here.
   * Shape is validated by callers that forward it across a trust boundary
   * (see `app-spec-from-prompt.ts`); this field is a passthrough only.
   */
  terminologyScope?: string
  /**
   * Template-instantiation lineage (mirrors `metadata.instantiatedFrom`,
   * persisted by `persistProjectTemplateLineage`). Same shape as
   * `TemplateLineage` in
   * `features/templates/content-form/project-lineage.ts` — duplicated as a
   * structural type rather than imported, since that module already
   * imports FROM this one (importing back would create a cycle). Declared
   * by the project, never synthesized here; malformed lineage (missing a
   * required string field) is omitted, same as `terminologyScope`.
   */
  instantiatedFrom?: {
    templateId: string
    version: string
    structureHash: string
    divergedAt?: string
  }
  studioProjectPlan?: StudioProjectPlan | null
  /**
   * G1 durability marker (mirrors `settings.generation`). Present only when
   * the project declares a well-formed marker; the dashboard uses it to
   * surface orphaned generations distinctly instead of as normal drafts.
   */
  generation?: ProjectGenerationMarker
}

// ---------------------------------------------------------------------------
// StudioProject — full project shape returned by the platform.cogo.xyz Worker
// ---------------------------------------------------------------------------

export interface StudioProject {
  id: string
  owner_id: string
  name: string
  slug: string
  description: string
  app_target: 'web' | 'mobile' | 'both'
  status: 'draft' | 'active' | 'published' | 'archived'
  manifest: unknown
  tokens: unknown
  settings: unknown
  semantic: unknown
  thumbnail: string | null
  version: number
  /** Platform-owned block-bound revision counter. Absent on pre-handshake servers. */
  artifact_revision?: number
  /** Exact composite revision derived from `id` + `artifact_revision`. */
  projectRevision?: StudioProjectServerRevisionRef
  /** Platform-owned settings overlay revision counter. */
  settings_revision?: number
  /** Exact settings revision derived from `id` + `settings_revision`. */
  settingsRevision?: StudioProjectSettingsRevisionRef
  is_public: boolean
  design_system_preset_id: string | null
  created_at: string
  updated_at: string
}

export interface StudioProjectServerRevisionRef {
  authority: 'studio-project-server'
  value: string
}

export interface StudioProjectSettingsRevisionRef {
  authority: 'studio-project-settings-server'
  value: string
}

function projectServerRevisionRef(
  projectId: string,
  artifactRevision: unknown,
): StudioProjectServerRevisionRef | null {
  if (
    !Number.isSafeInteger(artifactRevision) ||
    (artifactRevision as number) < 1
  ) {
    return null
  }
  return {
    authority: 'studio-project-server',
    value: `${projectId}@${artifactRevision as number}`,
  }
}

function projectSettingsRevisionRef(
  projectId: string,
  settingsRevision: unknown,
): StudioProjectSettingsRevisionRef | null {
  if (!Number.isSafeInteger(settingsRevision) || (settingsRevision as number) < 1) {
    return null
  }
  return {
    authority: 'studio-project-settings-server',
    value: `${projectId}@${settingsRevision as number}`,
  }
}

function withProjectServerRevisions<T extends StudioProject>(project: T): T {
  const rawProject = { ...project }
  delete rawProject.projectRevision
  delete rawProject.settingsRevision
  const projectRevision = projectServerRevisionRef(project.id, project.artifact_revision)
  const settingsRevision = projectSettingsRevisionRef(project.id, project.settings_revision)
  return {
    ...rawProject,
    ...(projectRevision ? { projectRevision } : {}),
    ...(settingsRevision ? { settingsRevision } : {}),
  } as T
}

interface StudioProjectFull extends StudioProject {
  studio_blocks?: Array<Record<string, any>>
  studio_pages?: Array<Record<string, any>>
}

export interface StudioProjectBundleBlock {
  block_name: string
  ui: unknown
  flow: unknown
  variables: unknown
  events: unknown
  datasource: unknown
  manifest: unknown
  semantic: unknown
  description: string | null
  sort_order: number
}

export interface StudioProjectExportBundle {
  schemaVersion: 'cogo.studio.project-export.v1'
  exportedAt: string
  project: {
    id: string
    name: string
    slug: string | null
    description: string | null
    app_target: StudioProject['app_target']
    version: number
  }
  manifest: unknown
  tokens: unknown
  settings: unknown
  studio_blocks: StudioProjectBundleBlock[]
}

export interface StudioProjectImportResult {
  projectId: string
  name: string
  blockIdMap: Record<string, string>
  sourceKind: 'studio-export' | 'cogo-app-bundle'
  summary: {
    pages: number
    blocks: number
    datasourceReferences: number
    webMcpDatasourceReferences: number
  }
}

/** List user's projects */
export async function listProjects(): Promise<ProjectMeta[]> {
  let rawList: StudioProject[]
  try {
    rawList = await workerRequest<StudioProject[]>('/api/projects')
  } catch (err) {
    if (err instanceof Error && err.message === 'Not authenticated') return []
    throw err
  }
  return rawList.filter(proj => {
    const isLegacyTime = new Date(proj.updated_at).getTime() <= new Date('2026-05-25T16:45:00.000Z').getTime()
    const isKeepable = proj.name.toLowerCase().includes('zenflow') || proj.name.toLowerCase().includes('yoga')
    if (isLegacyTime && !isKeepable) {
      return false
    }
    return true
  }).map(toProjectMeta)
}

// ---------------------------------------------------------------------------
// Worker-backed project CRUD  (platform.cogo.xyz /api/projects/*)
// These functions do NOT use supabase.schema('cogo') — all calls go through
// the Cloudflare Worker which enforces row-level auth server-side.
// ---------------------------------------------------------------------------

/**
 * Fetch a single project by ID from the Worker.
 * Returns null when the project is not found (404) or the user is not authorised.
 */
export async function getProject(id: string): Promise<StudioProject | null> {
  try {
    return withProjectServerRevisions(
      await workerRequest<StudioProject>(`/api/projects/${encodeURIComponent(id)}`),
    )
  } catch (e) {
    console.error('[getProject]', e)
    return null
  }
}

/**
 * Patch a project via the Worker.
 * Returns the updated StudioProject or null on error.
 */
export async function updateProject(
  id: string,
  patch: Partial<Omit<StudioProject, 'id' | 'owner_id' | 'artifact_revision' | 'projectRevision' | 'settings_revision' | 'settingsRevision' | 'created_at' | 'updated_at'>>,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<StudioProject | null> {
  try {
    return withProjectServerRevisions(
      await workerRequest<StudioProject>(`/api/projects/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
        ...opts,
      }),
    )
  } catch (e) {
    console.error('[updateProject]', e)
    return null
  }
}

/**
 * Delete a project via the Worker.
 * Returns true on success, false on error.
 */
export async function deleteProject(id: string): Promise<boolean> {
  try {
    await workerRequest<void>(`/api/projects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
    return true
  } catch (e) {
    console.error('[deleteProject]', e)
    return false
  }
}

/** Load full project as LoadedApp */
export async function loadProject(projectId: string, traceId?: string): Promise<{ project: StudioProject; meta: ProjectMeta; app: LoadedApp; blockIdMap: Record<string, string>; settings: Record<string, unknown> | null }> {
  const data = withProjectServerRevisions(
    await workerRequest<StudioProjectFull>(
      `/api/projects/${encodeURIComponent(projectId)}/full`,
      traceId ? { headers: { 'X-Cogo-Trace-Id': traceId } } : {},
    ),
  )
  const meta = toProjectMeta(data)

  // Build block_name → DB UUID mapping for auto-save — same deterministic
  // order as toLoadedApp so first-block fallbacks agree with the flow merge.
  // Alongside it, capture each row's updated_at as the optimistic-concurrency
  // baseline (F11). Feature-detect: rows without updated_at (older worker
  // deployments / partial mocks) get no baseline, so If-Match is never sent
  // for them — load-time-only opportunism, never invented tokens.
  const blockIdMap: Record<string, string> = {}
  const blockBaselines: Record<string, string> = {}
  for (const b of sortStudioBlocks(data.studio_blocks ?? [])) {
    blockIdMap[b.block_name] = b.id
    if (typeof b.updated_at === 'string' && b.updated_at) {
      blockBaselines[b.id] = b.updated_at
    }
  }
  replaceProjectBlockBaselines(projectId, blockBaselines)

  return {
    project: data,
    meta,
    app: toLoadedApp(data),
    blockIdMap,
    settings: (data.settings as Record<string, unknown>) ?? null,
  }
}

export async function exportProjectBundle(projectId: string): Promise<StudioProjectExportBundle> {
  const data = await workerRequest<StudioProjectFull>(`/api/projects/${encodeURIComponent(projectId)}/full`)
  return toStudioProjectExportBundle(data)
}

export async function importProjectBundle(
  bundle: unknown,
  opts: { name?: string; description?: string } = {},
): Promise<StudioProjectImportResult> {
  const normalized = normalizeProjectBundleForImport(bundle, opts)
  const project = await workerRequest<StudioProject>('/api/projects', {
    method: 'POST',
    body: JSON.stringify({
      name: normalized.name,
      app_target: normalized.appTarget,
      description: normalized.description,
      settings: normalized.settings,
    }),
  })
  if (!project?.id) throw new Error('Project create response missing id')

  await saveProjectMeta(project.id, {
    manifest: normalized.manifest,
    tokens: normalized.tokens,
    settings: normalized.settings,
    description: normalized.description,
  })

  const result = await workerRequest<{ blockIdMap?: Record<string, string> }>(
    `/api/projects/${encodeURIComponent(project.id)}/blocks`,
    {
      method: 'PUT',
      body: JSON.stringify({ blocks: normalized.blocks }),
    },
  )
  dropProjectBlockBaselines(project.id)

  return {
    projectId: project.id,
    name: normalized.name,
    blockIdMap: result.blockIdMap ?? {},
    sourceKind: normalized.sourceKind,
    summary: summarizeBundleRows(normalized.manifest, normalized.blocks),
  }
}

/**
 * Birth-write options for {@link createProject} (G1/G2 durability contract).
 * `generation` and `designSystemPresetId` ride the writes createProject
 * already performs (POST + meta PATCH) — no extra round-trip. Both are
 * sent on BOTH legs deliberately: the POST makes the project row born with
 * the marker/binding, and the meta PATCH re-asserts them for a Worker
 * deployment whose POST handler ignores unknown body fields.
 *
 * F1b (adversarial review) — createProject owns the failure handling of the
 * meta-PATCH leg itself (no caller retries in practice): the PATCH is retried
 * exactly once, and if the retry also fails the just-created row is
 * best-effort DELETEd before rethrowing, so a transient PATCH failure can
 * never strand an UNMARKED project row (settings={}) that the orphan
 * predicate cannot see. If the compensating delete fails too, the thrown
 * error names the project id and says it may exist unmarked (honest
 * residual).
 */
export interface CreateProjectOptions {
  traceId?: string
  /** Pending generation durability marker persisted as `settings.generation`. */
  generation?: ProjectGenerationMarker
  /** Design-system preset uuid persisted to `studio_projects.design_system_preset_id`. */
  designSystemPresetId?: string | null
  /** G3 — U2 library card lineage persisted as `settings.libraryStructureSelection`. */
  libraryStructureSelection?: LibraryStructureSelection
  /** F5 — verified pack release lineage persisted as `settings.designSystemPackRelease`. */
  designSystemPackRelease?: StudioDesignSystemPackReleaseLineage
  /**
   * R1 — source-derived design system lineage persisted as
   * `settings.projectDesignSystemGeneration`. Carries the preview-only rights
   * ceiling into the generated project so `publishProject` can enforce it.
   */
  projectDesignSystemGeneration?: StudioProjectDesignSystemGenerationLineage
  /** Final generation authority bound to the server-issued project id before return. */
  generationAuthority?: StudioGenerationAuthorityEnvelope
  /** Canonical AppSpec authenticated by `generationAuthority.applicationAuthority.appSpecRef`. */
  generationAuthorityAppSpec?: AppSpecFull
  /**
   * Human-confirmed intake-card decisions (WonBanking S1-S4/S7 lane)
   * persisted as `settings.intakeDecisions` at birth — the structured record
   * of what the person ruled on the intake cards, born WITH the project so a
   * generation that dies mid-flight still leaves the decisions readable.
   */
  intakeDecisions?: import('@cogo/agentic-generator').IntakeDecisions
  /**
   * The original screen captured at S3 paste time, persisted as
   * `settings.asIsSnapshot` at birth. Born WITH the project for the same
   * reason as the decisions above: a conversion's "what did we start from"
   * must not depend on the to-be generation succeeding.
   */
  asIsSnapshot?: import('@/features/preview/services/as-is-snapshot').AsIsSnapshot
  /**
   * A4b — authenticated generated-artifact binding written with the project
   * row itself. This is deliberately a closed settings value rather than a
   * browser-store marker: Bot retries discover the server-owned binding
   * before attempting another create, and the fresh project readback proves
   * the owner/content tuple. The current Worker does not expose an atomic
   * create idempotency primitive, so callers must still describe cross-client
   * uniqueness as a server follow-up rather than as a guarantee of this
   * client option.
   */
  generatedArtifactPersistence?: Record<string, unknown>
  /**
   * Chat has already created this authenticated empty draft on the platform.
   * Project birth must reuse the exact reserved row instead of creating a
   * second generic project after decomposition.
   */
  chatGeneratedApplicationDraft?: ChatGeneratedApplicationDraftPreparationReceipt
}

class LosslessJsonTransportError extends Error {
  constructor() {
    super('Value graph is not lossless JSON transport data')
    this.name = 'LosslessJsonTransportError'
  }
}

function rejectLossyJsonTransport(): never {
  throw new LosslessJsonTransportError()
}

function assertLosslessJsonTransport(value: unknown): void {
  const ancestors = new Set<object>()

  const visit = (candidate: unknown): void => {
    if (candidate === null) return
    const valueType = typeof candidate
    if (valueType === 'string' || valueType === 'boolean') return
    if (valueType === 'number') {
      if (Number.isFinite(candidate) && !Object.is(candidate, -0)) return
      rejectLossyJsonTransport()
    }
    if (valueType !== 'object') rejectLossyJsonTransport()

    const objectValue = candidate as object
    if (ancestors.has(objectValue)) rejectLossyJsonTransport()
    ancestors.add(objectValue)

    try {
      const prototype = Object.getPrototypeOf(objectValue)
      if (Array.isArray(objectValue)) {
        if (prototype !== Array.prototype) rejectLossyJsonTransport()
        const descriptors = Object.getOwnPropertyDescriptors(objectValue)
        const ownKeys = Reflect.ownKeys(objectValue)
        for (let index = 0; index < objectValue.length; index += 1) {
          const descriptor = descriptors[String(index)]
          if (
            !descriptor ||
            !descriptor.enumerable ||
            !Object.prototype.hasOwnProperty.call(descriptor, 'value')
          ) {
            rejectLossyJsonTransport()
          }
          visit(descriptor.value)
        }
        for (const key of ownKeys) {
          if (key === 'length') continue
          if (typeof key !== 'string') rejectLossyJsonTransport()
          const index = Number(key)
          if (
            !Number.isInteger(index) ||
            index < 0 ||
            index >= objectValue.length ||
            String(index) !== key
          ) {
            rejectLossyJsonTransport()
          }
        }
      } else {
        if (prototype !== Object.prototype && prototype !== null) {
          rejectLossyJsonTransport()
        }
        const descriptors = Object.getOwnPropertyDescriptors(objectValue)
        for (const key of Reflect.ownKeys(objectValue)) {
          if (typeof key !== 'string') rejectLossyJsonTransport()
          const descriptor = descriptors[key]
          if (
            !descriptor?.enumerable ||
            !Object.prototype.hasOwnProperty.call(descriptor, 'value')
          ) {
            rejectLossyJsonTransport()
          }
          visit(descriptor.value)
        }
      }
    } catch (error) {
      if (error instanceof LosslessJsonTransportError) throw error
      rejectLossyJsonTransport()
    } finally {
      ancestors.delete(objectValue)
    }
  }

  visit(value)
}

export function withStudioGenerationAuthorityEnvelope(
  settings: Record<string, unknown>,
  envelope: StudioGenerationAuthorityEnvelope,
): Record<string, unknown> {
  if (envelope.stage !== 'final') {
    throw new Error('Only final generation authority can be persisted.')
  }
  if (!envelope.subject.projectId) {
    throw new Error('Persisted generation authority must be project-bound.')
  }
  validateStudioGenerationAuthorityEnvelope(envelope)
  assertLosslessJsonTransport(settings)
  assertLosslessJsonTransport(envelope)
  return structuredClone({
    ...settings,
    generationAuthority: envelope,
  })
}

export interface StudioGenerationAuthorityAppSpecSnapshot {
  schemaVersion: 'cogo.studio-generation-authority-app-spec.v1'
  projectId: string
  authorityAppSpecRef: string
  appSpecFingerprint: string
  appSpec: AppSpecFull
}

export function readStudioGenerationAuthorityAppSpecSnapshot(
  settingsValue: unknown,
  expectedProjectId: string,
  expectedAuthorityAppSpecRef: string,
): StudioGenerationAuthorityAppSpecSnapshot {
  const settings = settingsValue && typeof settingsValue === 'object' && !Array.isArray(settingsValue)
    ? settingsValue as Record<string, unknown>
    : {}
  const value = settings.generationAuthorityAppSpec
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Persisted generation authority is missing its authenticated AppSpec snapshot.')
  }
  const snapshot = value as Record<string, unknown>
  if (
    snapshot.schemaVersion !== 'cogo.studio-generation-authority-app-spec.v1' ||
    snapshot.projectId !== expectedProjectId ||
    snapshot.authorityAppSpecRef !== expectedAuthorityAppSpecRef ||
    !isCanonicalAppGenerationSpecRef(snapshot.appSpecFingerprint) ||
    !snapshot.appSpec ||
    typeof snapshot.appSpec !== 'object' ||
    Array.isArray(snapshot.appSpec)
  ) {
    throw new Error('Persisted generation authority AppSpec snapshot binding is invalid.')
  }
  const appSpec = snapshot.appSpec as AppSpecFull
  if (fingerprintAppGenerationSpec(appSpec) !== snapshot.appSpecFingerprint) {
    throw new Error('Persisted generation authority AppSpec snapshot digest does not match its content.')
  }
  return structuredClone(snapshot) as unknown as StudioGenerationAuthorityAppSpecSnapshot
}

async function saveStudioGenerationAuthority(
  projectId: string,
  generationAuthority: StudioGenerationAuthorityEnvelope,
  appSpec: AppSpecFull,
  traceId?: string,
): Promise<void> {
  const attempt = beginStudioSettingsWriteOutcome('generation-authority')
  try {
    validateStudioGenerationAuthorityEnvelope(generationAuthority)
    if (
      generationAuthority.stage !== 'final' ||
      generationAuthority.subject.projectId !== projectId ||
      generationAuthority.applicationAuthority.appSpecRef !==
        fingerprintAppGenerationSpec(appSpec)
    ) {
      throw new Error(
        'Dedicated generation authority persistence requires a project-bound final envelope and its exact canonical AppSpec.',
      )
    }
    assertLosslessJsonTransport(generationAuthority)
    assertLosslessJsonTransport(appSpec)
    const receipt = await workerRequest<unknown>(
      `/api/projects/${encodeURIComponent(projectId)}/generation-authority`,
      {
        method: 'PUT',
        body: JSON.stringify({ generationAuthority, appSpec }),
        ...(traceId ? { headers: { 'X-Cogo-Trace-Id': traceId } } : {}),
      },
    )
    assertStudioGenerationAuthorityRecoveryReceipt(
      receipt,
      projectId,
      fingerprintAppGenerationSpec(appSpec),
    )
    publishStudioSettingsWriteOutcome({
      state: 'accepted',
      operation: 'generation-authority',
      observedAt: new Date().toISOString(),
    }, attempt)
  } catch (error) {
    publishStudioSettingsWriteOutcome(
      classifyStudioSettingsWriteFailure(error, 'generation-authority'),
      attempt,
    )
    throw error
  }
}

function assertStudioGenerationAuthorityRecoveryReceipt(
  value: unknown,
  projectId: string,
  expectedFingerprint: string,
): void {
  const receipt = readRecord(value)
  if (
    !receipt ||
    receipt.schemaVersion !== 'cogo.studio-generation-authority-recovery.v1' ||
    (receipt.status !== 'created' && receipt.status !== 'idempotent') ||
    receipt.projectId !== projectId ||
    receipt.authorityAppSpecRef !== expectedFingerprint ||
    receipt.appSpecFingerprint !== expectedFingerprint
  ) {
    throw new Error('Generation authority recovery receipt is invalid.')
  }
}

/**
 * Project birth transport. Generated canonical blocks are intentionally
 * accepted as unknown JSON here: createProject immediately lowers them
 * through buildBlockRows before any LoadedApp/editor projection exists.
 */
export interface CreateProjectTemplate extends Omit<LoadedApp, 'blocks'> {
  blocks: Record<string, unknown>
}

/** Create project (optionally seeded from a project birth template) */
export async function createProject(
  name: string,
  template?: CreateProjectTemplate,
  options?: CreateProjectOptions,
): Promise<string> {
  const {
    traceId,
    generation,
    designSystemPresetId,
    libraryStructureSelection,
    designSystemPackRelease,
    projectDesignSystemGeneration,
    generationAuthority,
    generationAuthorityAppSpec,
    intakeDecisions,
    asIsSnapshot,
    generatedArtifactPersistence,
    chatGeneratedApplicationDraft,
  } = options ?? {}
  if (Boolean(generationAuthority) !== Boolean(generationAuthorityAppSpec)) {
    throw new Error('Generation authority and its canonical AppSpec snapshot must be persisted together.')
  }
  const generationAuthoritySnapshot = generationAuthority
    ? (() => {
        validateStudioGenerationAuthorityEnvelope(generationAuthority)
        assertLosslessJsonTransport(generationAuthority)
        return structuredClone(generationAuthority)
      })()
    : null
  const generationAuthorityAppSpecSnapshot = generationAuthorityAppSpec
    ? (() => {
        assertLosslessJsonTransport(generationAuthorityAppSpec)
        const snapshot = structuredClone(generationAuthorityAppSpec)
        if (
          generationAuthoritySnapshot?.applicationAuthority.appSpecRef !==
          fingerprintAppGenerationSpec(snapshot)
        ) {
          throw new Error(
            'Generation authority AppSpec snapshot does not match the final canonical authority reference.',
          )
        }
        return snapshot
      })()
    : null
  // Birth settings compose the G1 durability marker and the G3 library
  // selection lineage into ONE record so neither write clobbers the other.
  const birthSettings = ((): Record<string, unknown> | null => {
    if (
      !generation &&
      !libraryStructureSelection &&
      !designSystemPackRelease &&
      !projectDesignSystemGeneration &&
      !intakeDecisions &&
      !asIsSnapshot &&
      !generatedArtifactPersistence
    ) {
      return null
    }
    let settings: Record<string, unknown> = {}
    if (generation) settings = withProjectGenerationMarker(settings, generation)
    if (libraryStructureSelection) {
      settings = withLibraryStructureSelection(
        settings,
        libraryStructureSelection,
      )
    }
    if (designSystemPackRelease) {
      settings = withDesignSystemPackReleaseLineage(
        settings,
        designSystemPackRelease,
      )
    }
    if (projectDesignSystemGeneration) {
      settings = withProjectDesignSystemGenerationLineage(
        settings,
        projectDesignSystemGeneration,
      )
    }
    if (intakeDecisions) {
      settings = { ...settings, intakeDecisions: structuredClone(intakeDecisions) }
    }
    if (asIsSnapshot) {
      settings = { ...settings, asIsSnapshot: structuredClone(asIsSnapshot) }
    }
    if (generatedArtifactPersistence) {
      assertLosslessJsonTransport(generatedArtifactPersistence)
      settings = {
        ...settings,
        generatedArtifactPersistence: structuredClone(generatedArtifactPersistence),
      }
    }
    return settings
  })()
  const templateManifest = readRecord(template?.manifest) ?? {}
  const copiedReadinessMetadata = template
    ? normalizeImportedDatasourceReadinessMetadata(
        (template as unknown as Record<string, unknown>).metadata,
        templateManifest,
      )
    : {}
  const copiedReadinessProjection =
    'datasourceReadinessImportQuarantine' in copiedReadinessMetadata
      ? {
          ...('datasourceReadinessSidecar' in copiedReadinessMetadata
            ? {
                datasourceReadinessSidecar:
                  copiedReadinessMetadata.datasourceReadinessSidecar,
              }
            : {}),
          datasourceReadinessImportQuarantine:
            copiedReadinessMetadata.datasourceReadinessImportQuarantine,
        }
      : {}
  let reservedProject: StudioProject | null = null
  let reservedSettings: Record<string, unknown> | null = null
  if (chatGeneratedApplicationDraft) {
    reservedSettings = {
      chatGeneratedApplicationDraft:
        chatGeneratedApplicationDraftMarkerForReceipt(
          chatGeneratedApplicationDraft,
        ),
    }
  }
  const composedBirthSettings = reservedSettings
    ? { ...reservedSettings, ...(birthSettings ?? {}) }
    : birthSettings
  const creationSettings =
    Object.keys(copiedReadinessProjection).length > 0
      ? {
          ...(composedBirthSettings ?? {}),
          cogoApplicationMetadata: {
            ...(readRecord(composedBirthSettings?.cogoApplicationMetadata) ?? {}),
            ...copiedReadinessProjection,
          },
        }
      : composedBirthSettings
  const birthSettingsSnapshot = creationSettings
    ? structuredClone(creationSettings)
    : null
  let project: StudioProject
  if (chatGeneratedApplicationDraft) {
    const accessToken = (await getStudioAccessToken())?.trim()
    if (!accessToken || !birthSettingsSnapshot) {
      throw new Error(
        'Chat generation draft finalization requires an authenticated settings snapshot.',
      )
    }
    const manifest =
      template?.manifest ?? { name, pages: [{ id: 'home', name: 'Home', route: '/' }] }
    const finalization = await finalizeChatGeneratedApplicationDraft({
      accessToken,
      preparationReceipt: chatGeneratedApplicationDraft,
      name,
      manifest: readRecord(manifest) ?? {},
      settings: birthSettingsSnapshot,
    })
    const readback = await getProject(
      chatGeneratedApplicationDraft.subject.projectId,
    )
    if (
      !readback ||
      readback.id !== chatGeneratedApplicationDraft.subject.projectId ||
      readback.name !== name ||
      readback.projectRevision?.value !== finalization.projectRevision.value ||
      readback.settingsRevision?.value !== finalization.settingsRevision.value ||
      hashCanonicalContent(readback.manifest) !== hashCanonicalContent(manifest) ||
      hashCanonicalContent(readback.settings) !==
        hashCanonicalContent(birthSettingsSnapshot) ||
      !chatGeneratedApplicationDraftMarkerMatchesReceipt(
        readback.settings,
        chatGeneratedApplicationDraft,
      )
    ) {
      throw new Error(
        'Chat generation draft finalization did not survive an independent authenticated readback.',
      )
    }
    reservedProject = readback
    project = readback
  } else {
    project = await workerRequest<StudioProject>('/api/projects', {
        method: 'POST',
        body: JSON.stringify({
          name,
          app_target: 'web',
          description: '',
          ...(typeof designSystemPresetId === 'string' && designSystemPresetId
            ? { design_system_preset_id: designSystemPresetId }
            : {}),
          ...(birthSettingsSnapshot ? { settings: birthSettingsSnapshot } : {}),
        }),
        ...(traceId ? { headers: { 'X-Cogo-Trace-Id': traceId } } : {}),
      })
  }
  if (!project?.id) throw new Error('Project create response missing id')

  if (
    generationAuthoritySnapshot?.subject.projectId &&
    generationAuthoritySnapshot.subject.projectId !== project.id
  ) {
    if (reservedProject) {
      throw new Error(
        `Generation authority subject ${generationAuthoritySnapshot.subject.projectId} does not match reserved destination project ${project.id}.`,
      )
    }
    await deleteProject(project.id)
    throw new Error(
      `Generation authority subject ${generationAuthoritySnapshot.subject.projectId} does not match destination project ${project.id}.`,
    )
  }
  const boundGenerationAuthority = generationAuthoritySnapshot
    ? {
        ...generationAuthoritySnapshot,
        subject: {
          ...generationAuthoritySnapshot.subject,
          projectId: project.id,
        },
      }
    : null

  const metaPatch = {
    ...(reservedProject ? { name } : {}),
    manifest: template?.manifest ?? { name, pages: [{ id: 'home', name: 'Home', route: '/' }] },
    tokens: template?.tokens ?? {},
    ...(birthSettingsSnapshot ? { settings: birthSettingsSnapshot } : {}),
    ...(typeof designSystemPresetId === 'string' && designSystemPresetId
      ? { design_system_preset_id: designSystemPresetId }
      : {}),
  }
  // F1b — retry the meta PATCH once; on double failure, compensate by
  // deleting the just-created row (same delete the chat-generation cleanup
  // path uses) so an unmarked/manifest-less project never survives a
  // createProject failure. See the CreateProjectOptions docstring.
  try {
    if (!reservedProject) await saveProjectMeta(project.id, metaPatch)
  } catch (firstErr) {
    console.warn('[createProject] meta PATCH failed, retrying once:', firstErr)
    try {
      if (!reservedProject) await saveProjectMeta(project.id, metaPatch)
    } catch (retryErr) {
      if (reservedProject) throw retryErr
      const deleted = await deleteProject(project.id)
      if (!deleted) {
        throw new Error(
          `Project creation failed: metadata write failed twice and cleanup delete was not acknowledged — project ${project.id} may exist unmarked (no generation marker/manifest). Cause: ${
            retryErr instanceof Error ? retryErr.message : String(retryErr)
          }`,
        )
      }
      throw retryErr
    }
  }

  // The generic metadata PATCH replaces the whole settings JSON column.
  // Persist the immutable authority receipt only after that replacement so
  // the PATCH cannot erase the authority or its authenticated AppSpec
  // snapshot. Both failure paths compensate by deleting the new project.
  if (boundGenerationAuthority && generationAuthorityAppSpecSnapshot) {
    try {
      await saveStudioGenerationAuthority(
        project.id,
        boundGenerationAuthority,
        generationAuthorityAppSpecSnapshot,
        traceId,
      )
    } catch (authorityError) {
      if (reservedProject) throw authorityError
      const deleted = await deleteProject(project.id)
      if (!deleted) {
        throw new Error(
          `Project creation failed: dedicated generation authority persistence was rejected and cleanup delete was not acknowledged - project ${project.id} may exist without canonical authority. Cause: ${
            authorityError instanceof Error
              ? authorityError.message
              : String(authorityError)
          }`,
        )
      }
      throw authorityError
    }
  }

  // Seed blocks from template
  // CogoBlockDef has { schemaVersion, blockType, root, variables, events, ... }
  // DB studio_blocks.ui column stores { schemaVersion, blockType, root }
  if (template?.blocks && Object.keys(template.blocks).length > 0) {
    await persistGeneratedBlocks(project.id, template.blocks, template.flows, undefined, traceId)
  }

  // Copy thumbnail from template (non-blocking, best-effort)
  if (template) {
    const templateId = template.manifest?.name
      ? toSlug(template.manifest.name).replace(/-[a-z0-9]{6}$/, '')
      : null
    if (templateId) {
      ThumbnailService.copyFromTemplate(templateId, project.slug).then((thumbUrl) => {
        if (thumbUrl) {
          updateProject(project.id, { thumbnail: thumbUrl }).catch((thumbErr) => {
            console.warn('[createProject] thumbnail copy failed:', thumbErr)
          })
        }
      }).catch(() => {
        // Best-effort — thumbnail copy failure is non-fatal
      })
    }
  }

  return project.id
}

/**
 * Persist generated blocks to studio_blocks table.
 * Called after app generation completes to save in-memory blocks to DB.
 * Legacy callers receive blockIdMap (block_name → DB UUID). A caller that
 * supplies an exact project revision receives both the map and the
 * authority-issued successor revision from the atomic replace.
 */
export interface PersistGeneratedBlocksRevisionOptions extends Pick<RequestInit, 'keepalive'> {
  expectedProjectRevision: StudioProjectServerRevisionRef
}

export interface PersistGeneratedBlocksRevisionResult {
  blockIdMap: Record<string, string>
  projectRevision: StudioProjectServerRevisionRef
}

export function persistGeneratedBlocks(
  projectId: string,
  blocks: Record<string, unknown>,
  flows: Record<string, unknown> | undefined,
  opts: PersistGeneratedBlocksRevisionOptions,
  traceId?: string,
): Promise<PersistGeneratedBlocksRevisionResult>
export function persistGeneratedBlocks(
  projectId: string,
  blocks: Record<string, unknown>,
  flows?: Record<string, unknown>,
  opts?: Pick<RequestInit, 'keepalive'>,
  traceId?: string,
): Promise<Record<string, string>>
export async function persistGeneratedBlocks(
  projectId: string,
  blocks: Record<string, unknown>,
  flows?: Record<string, unknown>,
  opts?: Pick<RequestInit, 'keepalive'> | PersistGeneratedBlocksRevisionOptions,
  traceId?: string,
): Promise<Record<string, string> | PersistGeneratedBlocksRevisionResult> {
  const expectedProjectRevision = opts && 'expectedProjectRevision' in opts
    ? opts.expectedProjectRevision
    : null
  if (expectedProjectRevision) {
    const expected = projectServerRevisionRef(
      projectId,
      Number(expectedProjectRevision.value.slice(expectedProjectRevision.value.lastIndexOf('@') + 1)),
    )
    if (
      expectedProjectRevision.authority !== 'studio-project-server' ||
      !expected ||
      expected.value !== expectedProjectRevision.value
    ) {
      throw new Error('Invalid expected Studio project revision')
    }
  }
  const requestOptions = opts
    ? { keepalive: opts.keepalive }
    : {}
  const headers = new Headers()
  if (traceId) headers.set('X-Cogo-Trace-Id', traceId)
  if (expectedProjectRevision) headers.set('If-Match', expectedProjectRevision.value)
  const rows = buildBlockRows(blocks, flows)
  const expectedBlocksHash = await studioBlocksArtifactHash(
    rows as StudioBlockRowInput[],
  )
  const result = await workerRequest<{
    blockIdMap: Record<string, string>
    persisted?: { blockCount: number; blocksHash: string; superset: boolean; traceId: string }
    projectRevision?: StudioProjectServerRevisionRef
  }>(
    `/api/projects/${encodeURIComponent(projectId)}/blocks`,
    {
      method: 'PUT',
      body: JSON.stringify({ blocks: rows }),
      ...(traceId || expectedProjectRevision ? { headers } : {}),
      // L2-COR-05/CS-4/RUX-3 — a dismissal-time lane-2 flush threads
      // keepalive so the whole-replace PUT can outlive the document on a
      // direct tab close (where the PUT would otherwise be aborted at
      // teardown). HONEST RESIDUAL: keepalive bodies share a ~64KB in-flight
      // quota; a full-project PUT can exceed it → the fetch rejects and the
      // persist's catch reports the failure, leaving the preventDefault
      // dismissal dialog as the only protection (same residual class as
      // lane-1's oversize re-queue).
      ...requestOptions,
    },
  )
  // D5 (W0-2) — the server's persisted-set hash receipt is compared
  // fail-closed: a mismatch (a silently dropped row, a leaked superset row
  // from a stale replace, or in-transit corruption) throws rather than
  // returning a blockIdMap the caller would otherwise trust. A pre-D4 worker
  // omits the receipt entirely — tolerated for rollout compatibility.
  if (result.persisted && result.persisted.blocksHash !== expectedBlocksHash) {
    console.error('[persistGeneratedBlocks] blocks_persist_hash_mismatch', {
      projectId, expected: expectedBlocksHash, persisted: result.persisted,
    })
    throw new Error('blocks_persist_hash_mismatch')
  }
  if (!result.persisted) {
    console.warn('[persistGeneratedBlocks] no persisted receipt (pre-D4 worker)', { projectId })
  }
  // PUT whole-replace deletes + reinserts every row: ids can churn AND
  // every updated_at resets server-side, so every baseline this client held
  // for the project is now stale (a same-id reinsert would spuriously 409
  // the very next save). The PUT response carries no tokens — drop the
  // project's baselines wholesale; saves fall back to opportunistic
  // no-If-Match until the next load or PATCH response re-seeds them (F11).
  dropProjectBlockBaselines(projectId)
  if (expectedProjectRevision) {
    const issued = result.projectRevision
    const issuedRevision = issued
      ? projectServerRevisionRef(
          projectId,
          Number(issued.value.slice(issued.value.lastIndexOf('@') + 1)),
        )
      : null
    if (
      issued?.authority !== 'studio-project-server' ||
      !issuedRevision ||
      issuedRevision.value !== issued.value
    ) {
      throw new Error('Block replace response missing an exact Studio project revision')
    }
    return {
      blockIdMap: result.blockIdMap ?? {},
      projectRevision: issuedRevision,
    }
  }
  return result.blockIdMap ?? {}
}

/**
 * Typed 409 for block saves (F11): the server (or e2e mock) rejected the
 * PATCH because its If-Match precondition no longer matches the row —
 * another session modified the block since this client last observed it.
 * Carries the server's CURRENT updated_at so the caller can show what won.
 * Detected structurally via `code` (isBlockSaveConflictError) so the check
 * survives vi.mock module-identity splits in tests.
 */
export class BlockSaveConflictError extends Error {
  readonly code = 'BLOCK_SAVE_CONFLICT' as const
  readonly projectId: string
  readonly blockId: string
  /** The row's current updated_at as reported by the 409 body (null when
   * the server withheld it). */
  readonly serverUpdatedAt: string | null

  constructor(projectId: string, blockId: string, serverUpdatedAt: string | null) {
    super('Block save conflict: another session modified this block')
    this.name = 'BlockSaveConflictError'
    this.projectId = projectId
    this.blockId = blockId
    this.serverUpdatedAt = serverUpdatedAt
  }
}

/** Structural guard (no instanceof — robust across mocked module graphs). */
export function isBlockSaveConflictError(err: unknown): err is BlockSaveConflictError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === 'BLOCK_SAVE_CONFLICT'
  )
}

export interface SaveBlockResult {
  /** The row's post-save updated_at when the server reports it (CAS-aware
   * worker / e2e mock); null against the current production worker, whose
   * `select=id,block_name` projection withholds it — the feature-detect
   * that keeps this client change production-inert. */
  updated_at: string | null
}

export interface SaveBlockOptions extends Pick<RequestInit, 'keepalive'> {
  /** Opaque updated_at token to send as `If-Match` (opportunistic CAS).
   * Omitted → no precondition header, exactly the pre-F11 request shape. */
  baseline?: string
}

/** Save block content (partial update — only changed fields).
 * `opts` is an option bag: `keepalive` lets dismissal-time flushes outlive
 * the document (F27); `baseline` sends an If-Match precondition (F11).
 * A 409 response rejects with BlockSaveConflictError; any 2xx body carrying
 * updated_at refreshes the block's baseline and is returned to the caller. */
export async function saveBlock(
  projectId: string,
  blockId: string,
  updates: Partial<Record<'ui' | 'flow' | 'variables' | 'events' | 'datasource' | 'manifest' | 'semantic' | 'description', unknown>>,
  opts?: SaveBlockOptions,
): Promise<SaveBlockResult> {
  const { baseline, ...requestOpts } = opts ?? {}
  // CS-7 — only echo a baseline that is a valid HTTP header value. A token
  // with CRLF / control bytes would make `new Headers({'If-Match': baseline})`
  // throw a synchronous TypeError inside workerRequest — a non-typed rejection
  // that the generic re-queue retries forever with the same poisoned token
  // (permanent unsaveable block). The registry already refuses to store such
  // tokens (block-baselines), but guard here too (defense in depth): a
  // malformed token proceeds WITHOUT If-Match (LWW) rather than throwing.
  const safeBaseline = baseline && isHeaderSafeToken(baseline) ? baseline : undefined
  // CS-5 — snapshot the drop generation BEFORE the PATCH. If a wholesale drop
  // (e.g. persistGeneratedBlocks' PUT) lands while this PATCH is on the wire,
  // refreshBlockBaseline ignores the now-stale 2xx token instead of
  // resurrecting it over the dropped baseline.
  const dispatchGeneration = getBaselineGeneration()
  try {
    const body = await workerRequest<{ updated_at?: unknown } | null | undefined>(
      `/api/projects/${encodeURIComponent(projectId)}/blocks/${encodeURIComponent(blockId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
        // Token is echoed VERBATIM — never Date-parsed/reformatted (the CAS
        // compares it byte-for-byte against the stored serialization).
        ...(safeBaseline ? { headers: { 'If-Match': safeBaseline } } : {}),
        ...requestOpts,
      },
    )
    const updatedAt = typeof body?.updated_at === 'string' && body.updated_at
      ? body.updated_at
      : null
    if (updatedAt) {
      // CAS-aware worker (post-deploy / e2e mock): the response carries the
      // row's new token → advance the baseline so the next If-Match is fresh
      // (unless a drop superseded this PATCH mid-flight — CS-5).
      refreshBlockBaseline(projectId, blockId, updatedAt, dispatchGeneration)
    } else if (baseline) {
      // L2-COR-07 — CAS-deploy boundary. The pre-CAS production worker
      // applies LWW and returns only {id, block_name}, withholding updated_at
      // while its BEFORE-UPDATE trigger advances the row's server token. If we
      // KEPT the load-time baseline here it would go stale through this
      // client's OWN saves, and the first save after the CAS deploy would
      // send a token already behind the row → a spurious 409 + forced reload
      // with no second session involved. Dropping the baseline on a 2xx that
      // withholds updated_at makes the next save send NO If-Match (LWW) —
      // matching the pre-CAS reality — until a CAS-aware response re-seeds a
      // trustworthy token. (Only the no-updated_at branch drops; the
      // updated_at branch above keeps refreshing the real CAS path.)
      dropBlockBaseline(blockId)
    }
    return { updated_at: updatedAt }
  } catch (err) {
    if (err instanceof WorkerRequestError && err.status === 409) {
      const conflictBody = (err.body ?? {}) as { updated_at?: unknown }
      throw new BlockSaveConflictError(
        projectId,
        blockId,
        typeof conflictBody.updated_at === 'string' ? conflictBody.updated_at : null,
      )
    }
    throw err
  }
}

/** Save project metadata */
export async function saveProjectMeta(
  projectId: string,
  updates: Partial<Record<'name' | 'manifest' | 'tokens' | 'settings' | 'slug' | 'description' | 'design_system_preset_id', unknown>>,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  const updated = await updateProject(
    projectId,
    updates as Partial<Omit<StudioProject, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>,
    opts,
  )
  if (!updated) throw new Error('Project metadata save failed')
}

export interface StudioProjectSettingsDelta {
  studioProjectPlan?: StudioProjectPlan
  studioPlanningMetadata?: unknown
  cogoApplicationMetadata?: Record<string, unknown>
  cogoXyzRegistry?: unknown
  generatedArtifactPersistence?: Record<string, unknown>
}

const STUDIO_PROJECT_SETTINGS_DELTA_KEYS = new Set<
  keyof StudioProjectSettingsDelta
>([
  'studioProjectPlan',
  'studioPlanningMetadata',
  'cogoApplicationMetadata',
  'cogoXyzRegistry',
  'generatedArtifactPersistence',
])

/**
 * Apply a closed, server-owned settings overlay. The client sends only the
 * keys it owns; immutable authority/provenance siblings are never read or
 * retransmitted through the generic project PATCH.
 */
export interface PatchProjectSettingsOptions {
  expectedSettingsRevision: StudioProjectSettingsRevisionRef
}

export interface PatchProjectSettingsResult {
  settings: Record<string, unknown>
  settingsRevision: StudioProjectSettingsRevisionRef
}

export class ProjectSettingsConflictError extends Error {
  readonly code = 'PROJECT_SETTINGS_CONFLICT' as const

  constructor(
    readonly projectId: string,
    readonly expectedSettingsRevision: StudioProjectSettingsRevisionRef,
    readonly currentSettingsRevision: StudioProjectSettingsRevisionRef,
  ) {
    super('Project settings changed before the settings overlay was applied')
    this.name = 'ProjectSettingsConflictError'
  }
}

export async function patchProjectSettings(
  projectId: string,
  set: StudioProjectSettingsDelta,
  options?: PatchProjectSettingsOptions,
): Promise<PatchProjectSettingsResult> {
  const attempt = beginStudioSettingsWriteOutcome('settings-overlay')
  try {
    const keys = Object.keys(set) as Array<keyof StudioProjectSettingsDelta>
    if (
      keys.length === 0 ||
      keys.some((key) => !STUDIO_PROJECT_SETTINGS_DELTA_KEYS.has(key))
    ) {
      throw new Error('Project settings overlay contains no allowed keys.')
    }
    assertLosslessJsonTransport(set)
    const expectedSettingsRevision = options?.expectedSettingsRevision ??
      withProjectServerRevisions(
        await workerRequest<StudioProject>(
          `/api/projects/${encodeURIComponent(projectId)}`,
        ),
      ).settingsRevision
    if (!expectedSettingsRevision) {
      throw new Error('Exact Studio project settings revision is unavailable')
    }
    const expected = projectSettingsRevisionRef(
      projectId,
      Number(expectedSettingsRevision.value.slice(expectedSettingsRevision.value.lastIndexOf('@') + 1)),
    )
    if (
      expectedSettingsRevision.authority !== 'studio-project-settings-server' ||
      !expected ||
      expected.value !== expectedSettingsRevision.value
    ) {
      throw new Error('Invalid expected Studio project settings revision')
    }
    let response: { settings?: unknown; settingsRevision?: StudioProjectSettingsRevisionRef }
    try {
      response = await workerRequest<{
        settings?: unknown
        settingsRevision?: StudioProjectSettingsRevisionRef
      }>(
        `/api/projects/${encodeURIComponent(projectId)}/settings`,
        {
          method: 'PATCH',
          headers: { 'If-Match': expectedSettingsRevision.value },
          body: JSON.stringify({ set }),
        },
      )
    } catch (error) {
      if (
        error instanceof WorkerRequestError &&
        error.status === 409 &&
        readRecord(error.body)?.error === 'project-settings-revision-conflict'
      ) {
        const currentRaw = readRecord(error.body)?.settingsRevision
        const currentRecord = readRecord(currentRaw)
        const current = currentRecord
          ? projectSettingsRevisionRef(
              projectId,
              Number(String(currentRecord.value ?? '').slice(String(currentRecord.value ?? '').lastIndexOf('@') + 1)),
            )
          : null
        if (
          currentRecord?.authority === 'studio-project-settings-server' &&
          current &&
          current.value === currentRecord.value
        ) {
          throw new ProjectSettingsConflictError(
            projectId,
            expectedSettingsRevision,
            current,
          )
        }
      }
      throw error
    }
    const settings = readRecord(response?.settings)
    if (!settings) {
      throw new Error('Project settings overlay response is missing settings.')
    }
    const issued = response.settingsRevision
    const issuedRevision = issued
      ? projectSettingsRevisionRef(
          projectId,
          Number(issued.value.slice(issued.value.lastIndexOf('@') + 1)),
        )
      : null
    const expectedNumber = Number(expectedSettingsRevision.value.slice(expectedSettingsRevision.value.lastIndexOf('@') + 1))
    const issuedNumber = issuedRevision
      ? Number(issuedRevision.value.slice(issuedRevision.value.lastIndexOf('@') + 1))
      : null
    if (
      issued?.authority !== 'studio-project-settings-server' ||
      !issuedRevision ||
      issuedRevision.value !== issued.value ||
      issuedNumber !== expectedNumber + 1
    ) {
      throw new Error('Project settings overlay response is missing its exact successor revision.')
    }
    const snapshot = structuredClone(settings)
    publishStudioSettingsWriteOutcome({
      state: 'accepted',
      operation: 'settings-overlay',
      observedAt: new Date().toISOString(),
    }, attempt)
    return {
      settings: snapshot,
      settingsRevision: issuedRevision,
    }
  } catch (error) {
    publishStudioSettingsWriteOutcome(
      classifyStudioSettingsWriteFailure(error, 'settings-overlay'),
      attempt,
    )
    throw error
  }
}

/**
 * F2 (adversarial review) — merge-on-write settings PATCH for the debounced
 * auto-save queue. saveProjectMeta's `settings` PATCH replaces the WHOLE
 * settings JSON column server-side; the design-system auto-save lane used to
 * send a freshly built literal, erasing `settings.generation` and every
 * other sibling key (receipt-bound application settings, ontology selection)
 * on the next design-system edit.
 *
 * The merge base is a FRESH authenticated read at write time (same
 * read-merge-write shape as persistProjectOntologyReleaseSelection). A
 * load-time store copy is NOT a correct base: markProjectGenerationComplete,
 * persistProjectOntologyReleaseSelection and the MCP studio tools all PATCH
 * settings server-side mid-session via their own read-merge-write, and —
 * worse — a chat-generated project is opened while `settings.generation` is
 * still 'pending', so merging onto the load-time copy would RESURRECT the
 * pending marker after completion flipped it (a false orphan).
 *
 * Fail-closed: a failed read THROWS (the auto-save dispatcher re-queues the
 * payload) instead of degrading to the whole-object clobber this prevents.
 * `updates.settings` is treated as an overlay of the keys the caller owns;
 * any other fields in `updates` ride the same single PATCH unchanged.
 */
export async function saveProjectMetaMergingSettings(
  projectId: string,
  updates: Record<string, unknown>,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  const overlay = readRecord(updates.settings) ?? {}
  const project = await workerRequest<StudioProject>(
    `/api/projects/${encodeURIComponent(projectId)}`,
  )
  const base = readRecord(project?.settings) ?? {}
  await saveProjectMeta(
    projectId,
    { ...updates, settings: { ...base, ...overlay } },
    opts,
  )
}

/**
 * G1 durability contract — flip `settings.generation` to 'complete' AFTER
 * the block persist + readback boundary has succeeded. Read-merge-write on a
 * fresh authenticated read so sibling settings written during generation
 * (receipt-bound application settings, persistence subject) are preserved.
 *
 * Returns false (never throws on the read leg) when the project cannot be
 * read or there is nothing provable to record: the generated app is already
 * durable server data at every call site, so marker bookkeeping must not
 * roll it back. The PATCH leg still throws so callers can retry once.
 */
const GENERATED_APPLICATION_READBACK_OBSERVATION_HASH = /^sha256:[a-f0-9]{64}$/

/**
 * Studio Task 5 — the ref naming the fresh-readback observation that was
 * APPENDED before this completion was requested.
 *
 * Shape-checked here rather than trusted, and the check is fail-closed: a
 * malformed ref THROWS instead of being dropped from the body. Dropping it
 * would turn "this generation proved its readback" into "this generation said
 * nothing", which is indistinguishable on the wire from an unbound
 * generation — the completion would then be recorded as if no observation
 * were ever required.
 */
function readGeneratedApplicationReadbackObservationRef(
  value: unknown,
): { ref: string; contentHash: string } {
  const reference = readRecord(value) ?? null
  if (
    !reference ||
    Object.keys(reference).length !== 2 ||
    typeof reference.ref !== 'string' ||
    reference.ref.trim().length === 0 ||
    typeof reference.contentHash !== 'string' ||
    !GENERATED_APPLICATION_READBACK_OBSERVATION_HASH.test(reference.contentHash)
  ) {
    throw new Error(
      'Generated application readback observation ref is not a content ref.',
    )
  }
  return { ref: reference.ref, contentHash: reference.contentHash }
}

export async function markProjectGenerationComplete(
  projectId: string,
  traceId?: string,
  generatedApplicationReadbackObservationRef?:
    | { ref: string; contentHash: string }
    | null,
): Promise<boolean> {
  const body: Record<string, unknown> = traceId ? { traceId } : {}
  if (
    generatedApplicationReadbackObservationRef !== undefined &&
    generatedApplicationReadbackObservationRef !== null
  ) {
    body.generatedApplicationReadbackObservationRef =
      readGeneratedApplicationReadbackObservationRef(
        generatedApplicationReadbackObservationRef,
      )
    assertLosslessJsonTransport(body)
  }
  try {
    await workerRequest<{ settings: unknown }>(
      `/api/projects/${encodeURIComponent(projectId)}/generation/complete`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    )
    return true
  } catch (error) {
    if (
      error instanceof WorkerRequestError &&
      (error.status === 404 || error.status === 409)
    ) {
      return false
    }
    throw error
  }
}

/**
 * Task 3 (2026-07-28 finalization-state design) — record that the browser-
 * side generation flow failed before acknowledging the server's persistence,
 * AFTER the client-side cleanup decided to preserve the project (the
 * generation workflow may already have durably written its blocks and
 * flipped `settings.generation.state` to 'complete' server-side).
 * Read-merge-write on a FRESH authenticated read, exactly like
 * `markProjectGenerationComplete` above — the Cloudflare workflow is the
 * OTHER writer of this same `settings.generation` marker, racing this call
 * to flip `state` to 'complete' at the end of its own run. Merging onto any
 * settings snapshot captured earlier in the generation flow (e.g. the
 * persisted-block-count readback the cleanup path already performed) could
 * write back a stale 'pending' state and resurrect exactly the orphan
 * mislabeling this marker exists to remove, so this always re-reads here,
 * at the moment of the write, instead of accepting a caller-supplied
 * settings snapshot.
 *
 * Never throws: every failure (read, missing project, missing marker, or
 * PATCH) resolves to `false` so the fire-and-forget caller on the cleanup
 * path — which must never mask the real downstream error it is about to
 * rethrow — can await this without a try/catch of its own. Records nothing
 * when the project has no generation marker at all: there is nothing
 * provable to attach the failure to.
 *
 * WRITES ONLY ONTO A `state === 'complete'` MARKER, and this guard is
 * load-bearing rather than defensive. Without it, this function's own
 * read-merge-write reintroduces the false orphan it exists to prevent: the
 * user aborts during execution while the Cloudflare workflow keeps running
 * server-side; this detached helper GETs settings (marker still 'pending');
 * the workflow then PATCHes `state:'complete'`; this call's PATCH writes
 * back `{...pending, finalization}` — and ten minutes later the dashboard
 * shows the ORPHAN card ("Generation didn't finish") for a project whose
 * blocks are durably saved. The card gates on `complete` anyway, so a
 * finalization attached to a 'pending' marker is pure liability with no
 * upside.
 *
 * This is NOT the same exposure `markProjectGenerationComplete` carries: its
 * unguarded clobber can only ever write the TERMINAL 'complete' value, and
 * re-asserting 'complete' over 'complete' cannot produce a false claim. Only
 * this function could write a non-terminal state back over a terminal one.
 */
export async function markProjectGenerationFinalizationFailed(
  projectId: string,
  reasonCode: string,
): Promise<boolean> {
  try {
    const project = await getProject(projectId)
    if (!project) return false
    const settings = readRecord(project.settings) ?? {}
    const marker = readProjectGenerationMarker(settings)
    if (!marker) return false
    // See the `state === 'complete'` paragraph above — writing a
    // finalization onto a freshly-read 'pending' marker would PATCH that
    // stale 'pending' back over a 'complete' the workflow may have just
    // written, manufacturing a false orphan card.
    if (marker.state !== 'complete') return false
    const failed = withFailedGenerationFinalization(marker, reasonCode)
    await saveProjectMeta(projectId, {
      settings: withProjectGenerationMarker(settings, failed),
    })
    return true
  } catch (err) {
    console.warn(
      '[markProjectGenerationFinalizationFailed] Failed to record generation finalization failure',
      { projectId, reasonCode, err },
    )
    return false
  }
}

/**
 * Retirement counterpart to `markProjectGenerationFinalizationFailed`: drop
 * the `finalization` notice once the user has done what the card asks (open
 * the app and look at it). Without this the notice is permanent — the only
 * other writer that clears the field is `completeGenerationMarker`, reachable
 * solely through a subsequent successful generation into the same project.
 *
 * Same shape as its sibling in every respect that matters: a FRESH
 * authenticated read-merge-write (sibling settings keys preserved), the same
 * `state === 'complete'` guard (a 'pending' marker must never be PATCHed
 * back over a workflow's 'complete' flip), and it NEVER throws — the caller
 * is a UI click handler that must not block or fail navigation. `state`,
 * `traceId`, `startedAt` and `completedAt` are carried through untouched;
 * only the notice goes away.
 *
 * Resolves `false` without writing when there is nothing to retire (no
 * project, no marker, non-'complete' marker, or no finalization recorded).
 */
export async function clearProjectGenerationFinalization(
  projectId: string,
): Promise<boolean> {
  try {
    const project = await getProject(projectId)
    if (!project) return false
    const settings = readRecord(project.settings) ?? {}
    const marker = readProjectGenerationMarker(settings)
    if (!marker) return false
    if (marker.state !== 'complete') return false
    if (!hasFailedGenerationFinalization(marker)) return false
    await saveProjectMeta(projectId, {
      settings: withProjectGenerationMarker(
        settings,
        withoutGenerationFinalization(marker),
      ),
    })
    return true
  } catch (err) {
    console.warn(
      '[clearProjectGenerationFinalization] Failed to clear generation finalization notice',
      { projectId, err },
    )
    return false
  }
}

/**
 * G1.4 ghost-job probe — three-valued read of a project's durability state,
 * distinguishing authoritative absence (404/410) from transient failure.
 * `getProject` flattens every error to null and therefore CANNOT be used to
 * invalidate a durable reconnect anchor; only 'missing' here is proof the
 * project is gone. (Type lives in block-api-service — the pointer module —
 * imported type-only, so no runtime dependency is added in either direction.)
 */
export async function probeProjectGenerationState(
  projectId: string,
): Promise<AppGenerationJobServerProbe> {
  try {
    const project = await workerRequest<StudioProject>(
      `/api/projects/${encodeURIComponent(projectId)}`,
    )
    const marker = readProjectGenerationMarker(readRecord(project?.settings))
    return { outcome: 'exists', generationState: marker?.state ?? null }
  } catch (err) {
    if (
      err instanceof WorkerRequestError &&
      (err.status === 404 || err.status === 410)
    ) {
      return { outcome: 'missing' }
    }
    return { outcome: 'unknown' }
  }
}

/**
 * Resolve the exact ontology release selected for a Studio project. The
 * selection is intentionally a small metadata snapshot; renderers never need
 * to read the mutable active pointer or load the release statement set.
 */
export function readProjectOntologyReleaseSelection(
  project: unknown,
): OntologyReleaseSelection | null {
  const record = readRecord(project)
  const settings = readRecord(record?.settings)
  const settingsMetadata = readRecord(settings?.cogoApplicationMetadata)
  const loadedMetadata = readRecord(record?.metadata)
  return parseOntologyReleaseSelection(
    settingsMetadata?.ontologyReleaseSelection
      ?? loadedMetadata?.ontologyReleaseSelection
      ?? record?.ontologyReleaseSelection,
  )
}

/**
 * Persist a verified release selection without clobbering sibling settings.
 * Within one project the binding may only advance monotonically; an equal
 * pointer version cannot be rebound to a different immutable release.
 */
export async function persistProjectOntologyReleaseSelection(
  projectId: string,
  selection: OntologyReleaseSelection,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<OntologyReleaseSelection> {
  const project = await workerRequest<StudioProjectFull>(
    `/api/projects/${encodeURIComponent(projectId)}/full`,
  )
  const merged = mergeProjectOntologyReleaseSelection(project, selection)
  if (!merged.changed) return merged.selection
  await saveProjectMeta(projectId, { settings: merged.settings }, opts)
  return merged.selection
}

export function mergeProjectOntologyReleaseSelection(
  project: unknown,
  selection: OntologyReleaseSelection,
): {
  selection: OntologyReleaseSelection
  settings: Record<string, unknown>
  changed: boolean
} {
  const normalized = parseOntologyReleaseSelection(selection)
  if (!normalized) throw new Error('Invalid ontology release selection')
  const projectRecord = readRecord(project) ?? {}
  const manifestAppId = readString(readRecord(projectRecord.manifest)?.appId)
  if (manifestAppId && manifestAppId !== normalized.appId) {
    throw new Error(
      `Ontology release appId ${normalized.appId} does not match project appId ${manifestAppId}`,
    )
  }

  const existing = readProjectOntologyReleaseSelection(projectRecord)
  if (existing) {
    const sameScope =
      existing.tenantId === normalized.tenantId
      && existing.appId === normalized.appId
      && existing.branch === normalized.branch
    if (!sameScope) {
      throw new Error('Ontology release scope cannot be rebound on an existing project')
    }
    if (normalized.pointerVersion < existing.pointerVersion) {
      throw new Error('Ontology release selection cannot move to an older pointer version')
    }
    if (
      normalized.pointerVersion === existing.pointerVersion
      && normalized.releaseId !== existing.releaseId
    ) {
      throw new Error('Ontology release pointer version cannot select a different release')
    }
    if (normalized.releaseId === existing.releaseId) {
      return {
        selection: existing,
        settings: readRecord(projectRecord.settings) ?? {},
        changed: false,
      }
    }
  }

  const settings = readRecord(projectRecord.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  return {
    selection: normalized,
    settings: {
      ...settings,
      cogoApplicationMetadata: {
        ...metadata,
        ontologyReleaseSelection: normalized,
      },
    },
    changed: true,
  }
}

/**
 * Persist a terminology-selection pick (Task 3,
 * docs/superpowers/plans/terminology-selection-ui-plan.md) as the project's
 * declared scope — `settings.cogoApplicationMetadata.terminologyScope`, read
 * back by `toProjectMeta` as `ProjectMeta.terminologyScope`.
 *
 * Mirrors persistProjectOntologyReleaseSelection's merge-then-patch shape:
 * saveProjectMeta's `settings` PATCH replaces the whole `settings` object
 * server-side, so the current settings are read back first and only
 * `cogoApplicationMetadata.terminologyScope` is touched — every sibling
 * settings field (studioProjectPlan, ontologyReleaseSelection, proofRecords,
 * ...) round-trips untouched.
 */
export async function persistProjectTerminologyScope(
  projectId: string,
  scope: string,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  const project = await workerRequest<StudioProjectFull>(
    `/api/projects/${encodeURIComponent(projectId)}/full`,
  )
  const settings = readRecord(project.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  await saveProjectMeta(
    projectId,
    {
      settings: {
        ...settings,
        cogoApplicationMetadata: { ...metadata, terminologyScope: scope },
      },
    },
    opts,
  )
}

/**
 * Persist a template-instantiation lineage record (Plan B1 Task 3,
 * docs/superpowers/plans/2026-07-24-cogo-template-b1-studio-loop.md) as
 * the project's declared provenance —
 * `settings.cogoApplicationMetadata.instantiatedFrom`, so a project produced
 * by `instantiateTemplateToProject` can later be traced back to the template
 * package + structureHash it was compiled from.
 *
 * Mirrors persistProjectTerminologyScope's merge-then-patch shape exactly:
 * saveProjectMeta's `settings` PATCH replaces the whole `settings` object
 * server-side, so the current settings are read back first and only
 * `cogoApplicationMetadata.instantiatedFrom` is touched — every sibling
 * settings field (terminologyScope, ontologyReleaseSelection, proofRecords,
 * ...) round-trips untouched.
 */
export async function persistProjectTemplateLineage(
  projectId: string,
  lineage: { templateId: string; version: string; structureHash: string; divergedAt?: string },
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  const project = await workerRequest<StudioProjectFull>(
    `/api/projects/${encodeURIComponent(projectId)}/full`,
  )
  const settings = readRecord(project.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  await saveProjectMeta(
    projectId,
    {
      settings: {
        ...settings,
        cogoApplicationMetadata: { ...metadata, instantiatedFrom: lineage },
      },
    },
    opts,
  )
}

/**
 * Mark boundary-exit lineage divergence (Plan B2 Task 5,
 * `.superpowers/sdd/task-5-brief.md`): once a project produced by
 * `instantiateTemplateToProject` is edited OUTSIDE the template
 * content-form (`TemplateContentPanel` / `applySlotValues`), its lineage is
 * stamped with `instantiatedFrom.divergedAt` — an ISO timestamp marking the
 * FIRST such edit. This is a one-way transition: once set, this function
 * no-ops on every subsequent call (a server-side backstop for the caller's
 * own client-side idempotence check — see
 * `markTemplateDivergenceOnForeignEdit` in
 * `features/templates/content-form/project-lineage.ts`), and a project with
 * no lineage at all (`instantiatedFrom` absent — never instantiated from a
 * template) also no-ops, since there is nothing to mark diverged.
 *
 * Mirrors persistProjectTemplateLineage's merge-then-patch shape exactly:
 * the current settings are read back first and only
 * `cogoApplicationMetadata.instantiatedFrom.divergedAt` is touched — every
 * sibling settings field AND every sibling key already on
 * `instantiatedFrom` (templateId, version, structureHash, ...) round-trips
 * untouched.
 */
export async function markProjectTemplateDiverged(
  projectId: string,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  const project = await workerRequest<StudioProjectFull>(
    `/api/projects/${encodeURIComponent(projectId)}/full`,
  )
  const settings = readRecord(project.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  const instantiatedFrom = readRecord(metadata.instantiatedFrom)
  if (!instantiatedFrom || typeof instantiatedFrom.divergedAt === 'string') return

  await saveProjectMeta(
    projectId,
    {
      settings: {
        ...settings,
        cogoApplicationMetadata: {
          ...metadata,
          instantiatedFrom: { ...instantiatedFrom, divergedAt: new Date().toISOString() },
        },
      },
    },
    opts,
  )
}

/**
 * Plan B3 Task 3 — records the STRICT fail-closed publication judge gate's
 * PASS verdict (`features/templates/publish-gate.ts`'s
 * `runTemplatePublishGate`) alongside the project's template lineage, as
 * `instantiatedFrom.lastPublishJudge`.
 *
 * Evidence-recording choice (`publish-gate.ts` documents the full reasoning
 * at its call site): this reuses the LINEAGE merge-then-patch surface —
 * the same one `persistProjectTemplateLineage`/`markProjectTemplateDiverged`
 * above already use — rather than growing a new additive field on the
 * publish-evidence projection, which is parsed by a strict server-driven
 * schema this studio-client-only task cannot extend alone.
 *
 * Mirrors `markProjectTemplateDiverged`'s merge-then-patch shape exactly:
 * current settings are read back first and only
 * `cogoApplicationMetadata.instantiatedFrom.lastPublishJudge` is touched —
 * every sibling settings field AND every sibling key already on
 * `instantiatedFrom` (templateId, version, structureHash, divergedAt, ...)
 * round-trips untouched. No-ops when there is no lineage to attach to
 * (never instantiated from a template) — mirrors
 * `markProjectTemplateDiverged`'s own no-op guard; `runTemplatePublishGate`
 * itself only ever calls this for a project it already confirmed has
 * lineage, so this guard is a defensive backstop, not the primary check.
 */
export async function persistTemplatePublishJudgeVerdict(
  projectId: string,
  verdict: { pass: boolean; reasons: string[]; at: string },
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  const project = await workerRequest<StudioProjectFull>(
    `/api/projects/${encodeURIComponent(projectId)}/full`,
  )
  const settings = readRecord(project.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  const instantiatedFrom = readRecord(metadata.instantiatedFrom)
  if (!instantiatedFrom) return

  await saveProjectMeta(
    projectId,
    {
      settings: {
        ...settings,
        cogoApplicationMetadata: {
          ...metadata,
          instantiatedFrom: { ...instantiatedFrom, lastPublishJudge: verdict },
        },
      },
    },
    opts,
  )
}

/**
 * Plan B3 Task 4 — records a design-release rebind
 * (`features/templates/content-form/apply-design-rebind.ts`'s
 * `applyDesignRebind`, spec §3.4/§6.5's PRESENTATION-only rebind axis)
 * alongside the project's template lineage, as
 * `instantiatedFrom.designRelease`.
 *
 * Mirrors `persistTemplatePublishJudgeVerdict`'s merge-then-patch shape
 * exactly (same reasoning applies here: growing this on the LINEAGE surface
 * rather than a new additive field elsewhere) — current settings are read
 * back first and only `cogoApplicationMetadata.instantiatedFrom.designRelease`
 * is touched — every sibling settings field AND every sibling key already on
 * `instantiatedFrom` (templateId, version, structureHash, divergedAt,
 * lastPublishJudge, ...) round-trips untouched. No-ops when there is no
 * lineage to attach to (never instantiated from a template) — same no-op
 * guard as `persistTemplatePublishJudgeVerdict`/`markProjectTemplateDiverged`.
 */
export async function persistProjectDesignRelease(
  projectId: string,
  designRelease: { presetId: string; appliedAt: string },
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  const project = await workerRequest<StudioProjectFull>(
    `/api/projects/${encodeURIComponent(projectId)}/full`,
  )
  const settings = readRecord(project.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  const instantiatedFrom = readRecord(metadata.instantiatedFrom)
  if (!instantiatedFrom) return

  await saveProjectMeta(
    projectId,
    {
      settings: {
        ...settings,
        cogoApplicationMetadata: {
          ...metadata,
          instantiatedFrom: { ...instantiatedFrom, designRelease },
        },
      },
    },
    opts,
  )
}

export async function upsertProjectProofRecord(
  projectId: string,
  record: Record<string, unknown>,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<Record<string, unknown>[]> {
  const recordId = readString(record.id)
  if (!recordId) throw new Error('Proof record id is required')

  const project = await workerRequest<StudioProjectFull>(`/api/projects/${encodeURIComponent(projectId)}/full`)
  const settings = readRecord(project.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  const existing = normalizeProjectProofRecordList(metadata.proofRecords)
  const byId = new Map<string, Record<string, unknown>>()

  for (const existingRecord of existing) {
    const existingId = readString(existingRecord.id)
    if (existingId) byId.set(existingId, existingRecord)
  }
  byId.set(recordId, record)

  const next = Array.from(byId.values())
  await saveProjectMeta(
    projectId,
    {
      settings: {
        ...settings,
        cogoApplicationMetadata: {
          ...metadata,
          proofRecords: next,
        },
      },
    },
    opts,
  )
  return next
}

export async function removeProjectProofRecord(
  projectId: string,
  recordId: string,
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<Record<string, unknown>[]> {
  const normalizedRecordId = readString(recordId)
  if (!normalizedRecordId) throw new Error('Proof record id is required')

  const project = await workerRequest<StudioProjectFull>(`/api/projects/${encodeURIComponent(projectId)}/full`)
  const settings = readRecord(project.settings) ?? {}
  const metadata = readRecord(settings.cogoApplicationMetadata) ?? {}
  const next = normalizeProjectProofRecordList(metadata.proofRecords)
    .filter((record) => readString(record.id) !== normalizedRecordId)

  await saveProjectMeta(
    projectId,
    {
      settings: {
        ...settings,
        cogoApplicationMetadata: {
          ...metadata,
          proofRecords: next,
        },
      },
    },
    opts,
  )
  return next
}

function normalizeProjectProofRecordList(value: unknown): Record<string, unknown>[] {
  return readArray(value)
    .map(readRecord)
    .filter((record): record is Record<string, unknown> => Boolean(record && readString(record.id)))
}

// ---------------------------------------------------------------------------
// Version history types
// ---------------------------------------------------------------------------

export interface VersionEntry {
  version: number
  r2Key: string
  size: number
  deployedAt: string
  status: 'live' | 'archived'
}

/** Run quality gate: score blocks using browser-safe scoring, submit to publish-app Edge Function */
export async function checkQualityGate(
  projectId: string,
  version: number,
): Promise<QualityGateApiResponse> {
  // Read cross-block issues before any network project load. Error-level chain
  // breaks are already enough to reject locally, so the gate must not call the
  // Worker or Edge Function in that state.
  const storeIssues = usePreviewTestingStore.getState().crossBlockIssues
  const hasChainBreaks = storeIssues.some(i => i.level === 'error')
  const crossBlockIssues = storeIssues.map(issue => ({
    stage: `S${issue.stage}`,
    level: issue.level,
    file: '',
    rule: 'cross-block-chain',
    message: issue.message,
  }))

  if (hasChainBreaks) {
    return {
      ok: false,
      rejected: true,
      grade: 'reject',
      score: 0,
      bddPassRate: 100,
      reasons: ['Cross-block chain integrity errors detected'],
      failedBlocks: [],
      crossBlockIssues,
      failedBddScenarios: [],
    } satisfies QualityGateApiResponse
  }

  const { app } = await loadProject(projectId)

  // Build blocks record from studio_blocks
  const blocks: Record<string, Record<string, unknown>> = {}
  for (const [blockName, blockDef] of Object.entries(app.blocks ?? {})) {
    const block = blockDef as Record<string, any>
    blocks[blockName] = {
      block: block.manifest ?? block.meta ?? {},
      ui: block.ui ?? {},
      events: block.events ?? {},
      flow: block.flow ?? {},
      variables: block.variables ?? block.state ?? {},
      datasource: block.datasource ?? block.data ?? {},
    }
  }

  // Browser-safe scoring (no node:fs — uses computeScore/computeGrade from browser entry)
  const { computeScore, computeGrade } = await import('@cogo/validator/browser')

  const blockResults: Array<{ blockName: string; score: number; grade: 'pass' | 'warn' | 'reject'; issues: Array<{ stage: string; level: string; file: string; rule: string; message: string }> }> = []
  const reasons: string[] = []

  for (const [name] of Object.entries(blocks)) {
    // Without contract stage (requires node:fs), assign base score from block completeness
    const block = blocks[name] as Record<string, unknown>
    const hasUi = Object.keys(block.ui as Record<string, unknown> ?? {}).length > 0
    const issues: Array<{ stage: string; level: string; file: string; rule: string; message: string }> = []

    if (!hasUi) {
      issues.push({ stage: 'structural', level: 'error', file: name, rule: 'missing-ui', message: `Block '${name}' has no UI definition` })
    }

    const score = computeScore(issues as any)
    const grade = computeGrade(score) as 'pass' | 'warn' | 'reject'

    blockResults.push({ blockName: name, score, grade, issues })

    if (score < 80) {
      reasons.push(`Block '${name}' score ${score} < 80 (grade: ${grade})`)
    }
  }

  const overallScore = blockResults.length > 0
    ? Math.round(blockResults.reduce((sum, b) => sum + b.score, 0) / blockResults.length)
    : 100

  const allowed = reasons.length === 0 && !hasChainBreaks
  const grade = (allowed ? computeGrade(overallScore) : 'reject') as 'pass' | 'warn' | 'reject'

  // Local rejection guard: block publishing client-side when quality gate fails
  // Defense-in-depth: do not rely on Edge Function to mirror rejection (per Phase 57 decision)
  if (!allowed) {
    return {
      ok: false,
      rejected: true,
      grade,
      score: overallScore,
      bddPassRate: 100,
      reasons,
      failedBlocks: blockResults,
      crossBlockIssues,
      failedBddScenarios: [],
    } satisfies QualityGateApiResponse
  }

  const gateResult = {
    allowed,
    grade,
    overallScore,
    bddPassRate: 100,
    blockResults,
    crossBlockIssues,
    contractIssues: [] as any[],
    failedBddScenarios: [] as string[],
    reasons,
  }

  // Call publish-app Edge Function (best-effort — client-side gate is authoritative)
  const edgeUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/publish-app`
  const accessToken = await getStudioAccessToken()

  try {
    const res = await fetch(edgeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken ?? ''}`,
      },
      body: JSON.stringify({
        appId: projectId,
        version,
        gateResult,
      }),
    })

    const body = await res.json()
    if (typeof body === 'object' && body !== null && 'grade' in body) {
      return body as QualityGateApiResponse
    }
  } catch {
    // Edge Function unavailable — fall through to client-side result
  }

  // Fallback: use client-side gate result (already validated above)
  return {
    ok: true,
    grade: grade as string,
    score: overallScore,
    bddPassRate: 100,
  } satisfies QualityGateApiResponse
}

/**
 * The refusal a caller routes on when a project belongs to the canonical
 * publication release control plane rather than to this legacy path.
 */
export const ONTOLOGY_BOUND_PROJECT_PUBLISH_REFUSAL =
  'ontology-bound-project-requires-publication-release-control-plane'

/** Publish project — bundle → Cloudflare R2 → update DB version */
export async function publishProject(
  projectId: string,
  onProgress?: (step: string) => void,
  /** Optional in-memory project data — used when blocks are not yet saved to DB (e.g., chat-generated apps) */
  storeProject?: LoadedApp | null,
): Promise<{ url: string; version: number; thumbnailUrl?: string; gateResponse?: QualityGateApiResponse }> {
  const userId = await getUserIdForPublish()

  onProgress?.('saving')

  // 1. Load full project through the owner-scoped Worker API.
  let project = await workerRequest<StudioProjectFull>(`/api/projects/${encodeURIComponent(projectId)}/full`)
  if (!project) throw new Error('Project not found')
  // R1 — the preview-only ceiling, enforced.
  //
  // A project generated from a locally imported .fig carries
  // `settings.projectDesignSystemGeneration`, copied from a rights declaration
  // whose schema pins `productionGenerationAllowed: false`. Publishing is the
  // production act, so it stops here. Without this the ceiling would be a
  // field nobody reads — the record would say "preview only" while the app it
  // produced went live at a public URL. Checked against SERVER state (the
  // `/full` row just loaded), so a client that never wrote the marker locally,
  // or a stale tab, cannot route around it.
  // R2 — the ontology-bound ceiling, enforced.
  //
  // An application generated under an `ApplicationOntologyGenerationBinding`
  // has a control plane: a candidate, a verification matrix, a signed
  // authorization, a deployment receipt and an independent readback. None of
  // it is reachable from this function, which bundles, self-scores, and POSTs.
  // Letting a bound project through here would put a generated application
  // live with its whole evidence chain unconsulted — the record would say
  // "publication requires eligibility" while the app it produced went live at
  // a public URL anyway.
  //
  // Checked FIRST, against SERVER state (the `/full` row just loaded), and
  // before every effect below: slug normalization is an authenticated write,
  // the in-memory bundle fallback silently substitutes unsaved client blocks
  // for absent server ones, and the quality gate auto-passes that fallback.
  //
  // PRESENCE is the test, not validity. A binding that no longer parses is a
  // bound project whose identity was damaged; falling back to the legacy
  // publisher is the one thing that must not follow from that.
  const ontologyBinding = readRecord(
    project.settings as unknown as Record<string, unknown> | null | undefined,
  )?.[APPLICATION_ONTOLOGY_GENERATION_BINDING_SETTINGS_KEY]
  if (ontologyBinding !== undefined && ontologyBinding !== null) {
    throw new Error(ONTOLOGY_BOUND_PROJECT_PUBLISH_REFUSAL)
  }
  const sourceGenerationLineage = readProjectDesignSystemGenerationLineage(
    project.settings,
  )
  if (sourceGenerationLineage) {
    throw new Error(
      'This app was generated from an imported design (.fig) under preview-only rights, ' +
        'so it cannot be published. The confirmed rights cover reviewing the design inside ' +
        'this project, not distributing an app made from it.',
    )
  }
  if (!project.slug) throw new Error('Project needs a slug before publishing')
  const publishSlug = toDnsSafeSlug(project.slug)
  if (publishSlug !== project.slug) {
    const updatedProject = await updateProject(projectId, { slug: publishSlug })
    if (!updatedProject || updatedProject.slug !== publishSlug) {
      throw new Error('Could not normalize project slug for DNS-safe publishing')
    }
    project = { ...project, slug: publishSlug }
  }

  // 2. Bundle into LoadedApp format
  // Use DB data first; fall back to in-memory store data if DB has no blocks
  // (chat-generated apps keep blocks in memory until auto-save runs)
  let bundle = toLoadedApp(project)
  const dbHasBlocks = Object.keys(bundle.blocks ?? {}).length > 0
  bundle = mergeStoreProjectForPublishBundle(bundle, storeProject, dbHasBlocks)
  bundle = attachRenderPackPublishPayload(bundle)
  const renderPackPublishPayload = readRecord(bundle as unknown as Record<string, unknown>)?.renderPackPublishPayload ?? null
  const nextVersion = project.version + 1
  onProgress?.('checking')
  const publishReadinessGate = evaluateStudioPublishReadiness(
    bundle,
    readStudioProjectPlan(project.settings),
    projectId,
  )

  if (!publishReadinessGate.ok) {
    throw Object.assign(new Error('Publish readiness gate rejected'), {
      gateResponse: {
        ok: false,
        rejected: true,
        grade: 'reject',
        score: 0,
        bddPassRate: 0,
        reasons: publishReadinessGate.reasons,
        failedBlocks: [],
        crossBlockIssues: [],
        failedBddScenarios: [],
        publishReadinessGate,
      } satisfies QualityGateApiResponse,
    })
  }

  // 2b. Quality gate check
  // Skip DB-based quality gate when blocks come from in-memory store (DB has no blocks yet).
  // The bundle itself has blocks, so a basic completeness check suffices.
  let gateResponse: QualityGateApiResponse
  const bundleBlockCount = Object.keys(bundle.blocks ?? {}).length
  if (!dbHasBlocks && bundleBlockCount > 0) {
    // In-memory blocks: pass with basic score (all blocks present = 100)
    gateResponse = { ok: true, grade: 'pass', score: 100, bddPassRate: 100, publishReadinessGate }
  } else {
    gateResponse = await checkQualityGate(projectId, nextVersion)
    if (gateResponse.rejected) {
      throw Object.assign(new Error('Quality gate rejected'), { gateResponse })
    }
    gateResponse = { ...gateResponse, publishReadinessGate }
  }

  onProgress?.('uploading')

  // 3. Upload to the active platform publish endpoint.
  const publishUrl = platformApiUrl('/api/project/publish')
  const publishJwt = await requireUserJwt()

  const publishRes = await fetch(publishUrl, {
    method: 'POST',
    headers: platformHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${publishJwt}` }),
    body: JSON.stringify({
      slug: publishSlug,
      schema: bundle,
      renderPack: renderPackPublishPayload,
      userId,
      version: nextVersion,
    }),
  })

  if (!publishRes.ok) {
    const errBody = await publishRes.json().catch(() => ({})) as Record<string, string>
    throw new Error(errBody.error ?? `Publish failed: ${publishRes.status}`)
  }

  const publishData = await publishRes.json() as { url: string; thumbnailUrl?: string; r2Path?: string }

  onProgress?.('registering')

  // 4. Record project version + status through the owner-scoped Worker API.
  await workerRequest(`/api/projects/${encodeURIComponent(projectId)}/publish-registration`, {
    method: 'POST',
    body: JSON.stringify({
      version: nextVersion,
      r2_key: publishData.r2Path ?? `apps/${publishSlug}/v/${nextVersion}/schema.json`,
      thumbnail: publishData.thumbnailUrl,
    }),
  })

  onProgress?.('live')

  return {
    url: publishData.url,
    version: nextVersion,
    thumbnailUrl: publishData.thumbnailUrl,
    gateResponse,
  }
}

/** Fetch version history for a project slug from CF Worker */
export async function fetchVersionHistory(slug: string): Promise<VersionEntry[]> {
  const url = platformApiUrl(`/api/project/versions?slug=${encodeURIComponent(slug)}`)

  const res = await fetch(url, {
    headers: platformHeaders(),
  })
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({})) as Record<string, string>
    throw new Error(errBody.error ?? `Failed to fetch versions: ${res.status}`)
  }
  const data = await res.json() as { versions: VersionEntry[]; slug: string }
  return data.versions
}

/** Rollback to a specific version via CF Worker */
export async function rollbackVersion(slug: string, version: number): Promise<{ activeVersion: number }> {
  const url = platformApiUrl('/api/project/rollback')

  const res = await fetch(url, {
    method: 'POST',
    headers: platformHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ slug, version }),
  })

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({})) as Record<string, string>
    throw new Error(errBody.error ?? `Rollback failed: ${res.status}`)
  }

  const data = await res.json() as { ok: boolean; slug: string; activeVersion: number }
  return { activeVersion: data.activeVersion }
}

// ---------------------------------------------------------------------------
// Deployed App Management (Cloudflare Worker API)
// ---------------------------------------------------------------------------

export interface DeployedAppInfo {
  id: string
  name: string
  slug: string
  description: string | null
  status: 'live' | 'draft' | 'archived'
  tier: string | null
  version: number
  liveVersion: number | null
  subdomain: string | null
  thumbnail: string | null
  updatedAt: string
}

export async function fetchDeployedApps(): Promise<DeployedAppInfo[]> {
  const url = platformApiUrl('/api/apps')
  let jwt: string
  try {
    jwt = await requireUserJwt()
  } catch (err) {
    if (err instanceof Error && err.message === 'Not authenticated') return []
    throw err
  }
  const res = await fetch(url, {
    headers: platformHeaders({ Authorization: `Bearer ${jwt}` }),
  })
  if (!res.ok) throw new Error(`Failed to fetch apps: ${res.status}`)
  const data = await res.json() as { apps: DeployedAppInfo[] }
  const apps = data.apps ?? []
  return apps.filter(app => {
    const isLegacyTime = new Date(app.updatedAt).getTime() <= new Date('2026-05-25T16:45:00.000Z').getTime()
    const isKeepable = app.name.toLowerCase().includes('zenflow') || app.name.toLowerCase().includes('yoga')
    if (isLegacyTime && !isKeepable) {
      return false
    }
    return true
  })
}

export async function updateAppSettings(slug: string, newSlug: string): Promise<void> {
  const url = platformApiUrl('/api/apps/settings')
  const jwt = await requireUserJwt()
  const res = await fetch(url, {
    method: 'PATCH',
    headers: platformHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` }),
    body: JSON.stringify({ slug, newSlug }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ?? `Settings update failed: ${res.status}`)
  }
}

export async function deleteDeployedApp(slug: string): Promise<void> {
  const url = platformApiUrl('/api/apps/delete')
  const jwt = await requireUserJwt()
  const res = await fetch(url, {
    method: 'DELETE',
    headers: platformHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` }),
    body: JSON.stringify({ slug }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ?? `Delete failed: ${res.status}`)
  }
}

// ---------------------------------------------------------------------------
// App Analytics (Cloudflare Worker API)
// ---------------------------------------------------------------------------

export interface AnalyticsMetrics {
  page_views: number
  events: number
  errors: number
}

export interface TopPage {
  path: string
  count: number
  percentage: number
}

export interface DailyBreakdown {
  date: string
  page_views: number
  events: number
  errors: number
}

export interface AppAnalyticsData {
  slug: string
  range: string
  metrics: AnalyticsMetrics
  top_pages: TopPage[]
  daily_breakdown: DailyBreakdown[]
}

export async function fetchAppAnalytics(slug: string, range: '7d' | '30d' = '7d'): Promise<AppAnalyticsData> {
  const url = platformApiUrl(`/api/apps/analytics?slug=${encodeURIComponent(slug)}&range=${range}`)
  const res = await fetch(url, {
    headers: platformHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to fetch analytics: ${res.status}`)
  return res.json() as Promise<AppAnalyticsData>
}

// ---------------------------------------------------------------------------
// Deployed App Tier Upgrade (Cloudflare Worker API)
// ---------------------------------------------------------------------------

export interface UpgradedAppPayload {
  blocks: Array<{ name: string; type: string; [key: string]: unknown }>
  flows?: Array<{ id: string; name: string; [key: string]: unknown }>
  tier?: string
  manifest?: Record<string, unknown>
  metadata?: Record<string, unknown>
  generationEvidence?: Record<string, unknown>
  renderPackPublishPayload?: Record<string, unknown>
  renderPack?: Record<string, unknown>
  renderPackBytes?: RenderPackBytesPayload
  rawSchema?: Record<string, unknown>
}

function normalizeDeployedAppBlocks(source: unknown): UpgradedAppPayload['blocks'] {
  if (Array.isArray(source)) return source as UpgradedAppPayload['blocks']
  return Object.entries(readRecord(source) ?? {}).map(([name, b]) => {
    const block = readRecord(b) ?? {}
    return {
      name,
      type: readString(block.blockType) ?? readString(readRecord(block.ui)?.blockType) ?? name,
      ...block,
    }
  })
}

function normalizeDeployedAppFlows(source: unknown): UpgradedAppPayload['flows'] {
  if (Array.isArray(source)) return source as UpgradedAppPayload['flows']
  const flowRecord = readRecord(source)
  if (!flowRecord) return []
  return Object.entries(flowRecord).map(([id, f]) => {
    const flow = readRecord(f) ?? {}
    return {
      id,
      name: readString(flow.name) ?? id,
      ...flow,
    }
  })
}

function firstRecordValue(...values: unknown[]): Record<string, unknown> | undefined {
  for (const value of values) {
    const record = readRecord(value)
    if (record) return record
  }
  return undefined
}

/** Fetch deployed app JSON from the app-serve endpoint (same as mobile cache uses) */
export async function fetchDeployedAppJson(slug: string): Promise<UpgradedAppPayload> {
  const url = platformApiUrl(`/api/app-serve?slug=${encodeURIComponent(slug)}`)

  const res = await fetch(url, {
    headers: platformHeaders(),
  })
  if (!res.ok) throw new Error(`Could not load app data (${res.status})`)
  const data = await res.json() as Record<string, unknown>
  const wrapper = readRecord(data) ?? {}
  const schema = firstRecordValue(wrapper.schema, wrapper.app, wrapper.payload, data) ?? {}
  const renderPackPublishPayload = firstRecordValue(
    schema.renderPackPublishPayload,
    schema.renderPackPackaging,
    wrapper.renderPack,
    schema.renderPack,
  )
  const renderPackBytes = decodeRenderPackPublishPayload({
    ...schema,
    renderPack: wrapper.renderPack ?? schema.renderPack,
  })

  return {
    blocks: normalizeDeployedAppBlocks(schema.blocks ?? wrapper.blocks),
    flows: normalizeDeployedAppFlows(schema.flows ?? wrapper.flows),
    tier: readString(schema.tier) ?? readString(wrapper.tier) ?? 'B',
    manifest: firstRecordValue(schema.manifest, wrapper.manifest),
    metadata: firstRecordValue(schema.metadata, wrapper.metadata),
    generationEvidence: firstRecordValue(
      readRecord(schema.manifest)?.generationEvidence,
      schema.generationEvidence,
      wrapper.generationEvidence,
    ),
    renderPackPublishPayload,
    renderPack: firstRecordValue(wrapper.renderPack, schema.renderPack),
    renderPackBytes,
    rawSchema: schema,
  }
}

/** Persist upgraded app tier + blocks back to the deployed app via CF Worker */
export async function updateDeployedAppTier(slug: string, data: UpgradedAppPayload): Promise<void> {
  const url = platformApiUrl('/api/apps/upgrade')
  const jwt = await requireUserJwt()

  const res = await fetch(url, {
    method: 'POST',
    headers: platformHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` }),
    body: JSON.stringify({
      slug,
      tier: data.tier,
      blocks: data.blocks,
      flows: data.flows,
      manifest: data.manifest,
      metadata: data.metadata,
      generationEvidence: data.generationEvidence,
      renderPackPublishPayload: data.renderPackPublishPayload,
      renderPack: data.renderPack ?? data.renderPackPublishPayload,
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ?? `Tier upgrade persist failed: ${res.status}`)
  }
}

/** Archive project (soft delete) */
export async function archiveProject(projectId: string): Promise<void> {
  const updated = await updateProject(projectId, { status: 'archived' })
  if (!updated) throw new Error('Project archive failed')
}

/** Duplicate project */
export async function duplicateProject(projectId: string, newName: string): Promise<string> {
  const { app } = await loadProject(projectId)
  return createProject(newName, app)
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Generate URL-safe slug from name + random suffix for uniqueness */
function toSlug(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40)
  const suffix = Math.random().toString(36).slice(2, 8)
  return `${base}-${suffix}`
}

/**
 * Defensively parses `metadata.instantiatedFrom` (persisted by
 * `persistProjectTemplateLineage`) into `ProjectMeta['instantiatedFrom']`.
 * Mirrors `readInstantiatedFrom` in
 * `features/templates/content-form/project-lineage.ts` exactly — same
 * required-string-fields contract, same malformed -> omit behavior —
 * duplicated rather than imported to avoid a project-api <-> project-lineage
 * import cycle (that module already imports from this one).
 */
function readInstantiatedFromLineage(value: unknown): ProjectMeta['instantiatedFrom'] {
  const record = readRecord(value)
  if (!record) return undefined
  const { templateId, version, structureHash, divergedAt } = record
  if (typeof templateId !== 'string' || typeof version !== 'string' || typeof structureHash !== 'string') return undefined
  return {
    templateId,
    version,
    structureHash,
    ...(typeof divergedAt === 'string' ? { divergedAt } : {}),
  }
}

function toProjectMeta(row: StudioProject): ProjectMeta {
  const settings = readRecord(row.settings) ?? {}
  const settingsMetadata = readRecord(settings.cogoApplicationMetadata)
  const metadata = settingsMetadata ?? {}
  const manifest = readRecord(row.manifest) ?? {}
  const manifestReadiness = readManifestDatasourceReadinessLocations(manifest)
  // The project DECLARES its scope under metadata.terminologyScope; never
  // synthesized or defaulted here — absent when the project declares none.
  const terminologyScope = readString(metadata.terminologyScope)
  const instantiatedFrom = readInstantiatedFromLineage(metadata.instantiatedFrom)
  // G1 — durability marker (defensive read; malformed markers are omitted).
  const generation = readProjectGenerationMarker(settings)
  const projectRevision = projectServerRevisionRef(row.id, row.artifact_revision)
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    status: row.status,
    app_target: row.app_target,
    thumbnail: row.thumbnail,
    version: row.version,
    ...(projectRevision
      ? {
          artifact_revision: row.artifact_revision as number,
          projectRevision,
        }
      : {}),
    updated_at: row.updated_at,
    design_system_preset_id: row.design_system_preset_id,
    ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    datasourceReadinessManifestSidecar:
      manifestReadiness.direct.present
        ? manifestReadiness.direct.value
        : manifestReadiness.metadata.value ?? null,
    datasourceReadinessManifestLocations: manifestReadiness,
    ...(terminologyScope ? { terminologyScope } : {}),
    ...(instantiatedFrom ? { instantiatedFrom } : {}),
    ...(generation ? { generation } : {}),
    studioProjectPlan: readStudioProjectPlan(row.settings),
  }
}

function toStudioProjectExportBundle(row: StudioProjectFull): StudioProjectExportBundle {
  return {
    schemaVersion: 'cogo.studio.project-export.v1',
    exportedAt: new Date().toISOString(),
    project: {
      id: row.id,
      name: row.name,
      slug: row.slug,
      description: row.description,
      app_target: row.app_target,
      version: row.version,
    },
    manifest: row.manifest ?? {},
    tokens: row.tokens ?? {},
    settings: row.settings ?? {},
    studio_blocks: sortStudioBlocks(row.studio_blocks ?? []).map(toBundleBlockRow),
  }
}

function normalizeImportedDatasourceReadinessMetadata(
  metadataValue: unknown,
  manifest: Record<string, unknown>,
): Record<string, unknown> {
  const importedMetadata = readRecord(metadataValue) ?? {}
  const importedQuarantine = readDatasourceReadinessImportQuarantine(importedMetadata)
  const rawImportedQuarantine = importedMetadata.datasourceReadinessImportQuarantine
  const malformedImportedQuarantine =
    rawImportedQuarantine != null && !importedQuarantine
  const {
    datasourceReadinessImportQuarantine: _importedQuarantine,
    ...safeImportedMetadata
  } = importedMetadata
  const importedSidecar = readProjectDatasourceReadinessSidecar({
    manifest,
    metadata: safeImportedMetadata,
  })
  const manifestReadiness = readManifestDatasourceReadinessLocations(manifest)
  const rawSidecars = [
    safeImportedMetadata.datasourceReadinessSidecar,
    manifestReadiness.direct.value,
    manifestReadiness.metadata.value,
  ]
  const parsedRawSidecars = rawSidecars
    .map(parseStudioDatasourceReadinessSidecar)
    .filter((sidecar): sidecar is StudioDatasourceReadinessSidecar => sidecar !== null)
  const malformedImportedSidecar = rawSidecars.some(
    (value) => value != null && !parseStudioDatasourceReadinessSidecar(value),
  )
  const rawSidecarProjectIdClaim = rawSidecars
    .map((value) => readString(readRecord(readRecord(value)?.projectBinding)?.projectId))
    .find((value): value is string => Boolean(value))
  const projectId =
    importedSidecar?.projectBinding.correlationStatus === 'linked'
      ? importedSidecar.projectBinding.projectId
      : parsedRawSidecars.find(
          (sidecar) => sidecar.projectBinding.correlationStatus === 'linked',
        )?.projectBinding.projectId ?? null
  const declaredUnlinkedImportedSidecar = Boolean(
    (
      importedSidecar?.projectBinding.dependencyDeclared &&
      importedSidecar.projectBinding.correlationStatus === 'unlinked'
    ) || parsedRawSidecars.some(
      (sidecar) =>
        sidecar.projectBinding.dependencyDeclared &&
        sidecar.projectBinding.correlationStatus === 'unlinked',
    ),
  )
  const quarantineSourceProjectId =
    projectId ??
    rawSidecarProjectIdClaim ??
    importedQuarantine?.sourceProjectIdClaim ??
    readString(readRecord(rawImportedQuarantine)?.sourceProjectIdClaim) ??
    readString(safeImportedMetadata.studioProjectId) ??
    readString(manifest.id) ??
    readString(manifest.appId) ??
    'unknown-source-project'

  return {
    ...safeImportedMetadata,
    ...(projectId || declaredUnlinkedImportedSidecar || importedQuarantine || malformedImportedQuarantine || malformedImportedSidecar
      ? {
          datasourceReadinessImportQuarantine: {
            schemaVersion: 'studio-datasource-readiness-import-quarantine.v1',
            state: 'quarantined',
            reasonCode: 'imported-sidecar-requires-destination-correlation',
            sourceProjectIdClaim: quarantineSourceProjectId,
          },
        }
      : {}),
  }
}

function normalizeProjectBundleForImport(
  bundle: unknown,
  opts: { name?: string; description?: string },
): {
  name: string
  description: string
  appTarget: StudioProject['app_target']
  manifest: unknown
  tokens: unknown
  settings: Record<string, unknown>
  blocks: StudioProjectBundleBlock[]
  sourceKind: StudioProjectImportResult['sourceKind']
} {
  const record = readRecord(bundle)
  if (!record) throw new Error('Invalid Coogo project bundle: root must be an object')

  if (Array.isArray(record.studio_blocks)) {
    const project = readRecord(record.project)
    const manifest = readRecord(record.manifest) ?? readRecord(record.app) ?? {}
    const name =
      opts.name ??
      readString(project?.name) ??
      readString(manifest.name) ??
      'Imported Coogo Project'
    const importedSettings = readRecord(record.settings) ?? {}
    const {
      generationAuthority: importedGenerationAuthority,
      generationAuthorityQuarantine: _untrustedQuarantine,
      ...safeImportedSettings
    } = importedSettings
    const {
      cogoApplicationMetadata: importedMetadataSource,
      ...settingsWithoutImportedMetadata
    } = safeImportedSettings
    const nextImportedMetadata = normalizeImportedDatasourceReadinessMetadata(
      importedMetadataSource,
      manifest,
    )
    const settings = {
      ...settingsWithoutImportedMetadata,
      ...(Object.keys(nextImportedMetadata).length > 0
        ? { cogoApplicationMetadata: nextImportedMetadata }
        : {}),
      importedVia: 'cogo-studio-code-surface',
      importedBundleSchema: readString(record.schemaVersion) ?? 'worker-full',
      ...(importedGenerationAuthority !== undefined
        ? {
            generationAuthorityQuarantine: {
              schemaVersion:
                'cogo.studio-generation-authority-quarantine.v1',
              state: 'quarantined',
              reasonCode:
                'imported-authority-requires-destination-rebind',
              sourceProjectIdClaim:
                readString(project?.id) ?? 'unknown-source-project',
            },
          }
        : {}),
    }
    return {
      name,
      description: opts.description ?? readString(project?.description) ?? 'Imported from a Coogo Studio project bundle.',
      appTarget: normalizeAppTarget(project?.app_target),
      manifest,
      tokens: readRecord(record.tokens) ?? {},
      settings,
      blocks: normalizeBundleBlockRows(record.studio_blocks),
      sourceKind: 'studio-export',
    }
  }

  if (readRecord(record.blocks)) {
    const manifest = readRecord(record.manifest) ?? readRecord(record.app) ?? {}
    const ontology = readRecord(record.ontology)
    const isSipassMobileApp =
      readString(manifest.appId) === 'sipass-app' ||
      readString(ontology?.appId) === 'sipass-app'
    const name =
      opts.name ??
      readString(manifest.name) ??
      readString(record.name) ??
      'Imported Coogo App'
    const importedMetadata = normalizeImportedDatasourceReadinessMetadata(
      record.metadata,
      manifest,
    )
    const settings = {
      importedVia: 'cogo-studio-code-surface',
      importedBundleSchema:
        readString(record.schemaVersion) ??
        readString(record.packageVersion) ??
        'cogo-app',
      sourceRoot: readString(manifest.sourceRoot) ?? null,
      sourceGeneratedAt: readString(manifest.generatedAt) ?? null,
      ...(isSipassMobileApp ? { sourceConversion: { defaultDevice: 'mobile', mobileOnly: true } } : {}),
      ...(record.ontology !== undefined ? { cogoApplicationOntology: record.ontology } : {}),
      ...(Object.keys(importedMetadata).length > 0
        ? { cogoApplicationMetadata: importedMetadata }
        : {}),
    }
    return {
      name,
      description: opts.description ?? 'Imported from a Coogo application bundle.',
      appTarget: isSipassMobileApp ? 'mobile' : 'web',
      manifest,
      tokens: readRecord(record.tokens) ?? {},
      settings,
      blocks: blocksRecordToBundleRows(readRecord(record.blocks) ?? {}),
      sourceKind: 'cogo-app-bundle',
    }
  }

  throw new Error('Invalid Coogo project bundle: expected studio_blocks array or blocks object')
}

function normalizeAppTarget(value: unknown): StudioProject['app_target'] {
  return value === 'mobile' || value === 'both' ? value : 'web'
}

function toBundleBlockRow(row: Record<string, any>, index = 0): StudioProjectBundleBlock {
  return {
    block_name: readString(row.block_name) ?? `block-${index + 1}`,
    ui: readRecord(row.ui) ?? {},
    flow: normalizeStudioFlowContainer(row.flow),
    variables: readRecord(row.variables) ?? {},
    events: readRecord(row.events) ?? {},
    datasource: readRecord(row.datasource) ?? {},
    manifest: readRecord(row.manifest) ?? {},
    semantic: readRecord(row.semantic),
    description: readString(row.description) ?? null,
    sort_order: typeof row.sort_order === 'number' && Number.isFinite(row.sort_order) ? row.sort_order : index,
  }
}

function normalizeBundleBlockRows(value: unknown): StudioProjectBundleBlock[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((row): row is Record<string, any> => Boolean(readRecord(row)))
    .map((row, index) => toBundleBlockRow(row, index))
    .sort((a, b) => a.sort_order - b.sort_order || a.block_name.localeCompare(b.block_name))
}

function blocksRecordToBundleRows(blocks: Record<string, unknown>): StudioProjectBundleBlock[] {
  return Object.entries(blocks).map(([blockName, rawBlock], index) => {
    const block = readRecord(rawBlock) ?? {}
    const packageBlock = readRecord(block.block)
    return {
      block_name: blockName,
      ui: readRecord(block.ui) ?? {},
      flow: normalizeStudioFlowContainer(block.flow),
      variables: readRecord(block.variables) ?? {},
      events: readRecord(block.events) ?? {},
      datasource: readRecord(block.datasource) ?? {},
      manifest:
        readRecord(block.manifest) ??
        readRecord(block.meta) ??
        packageBlock ??
        { name: blockName },
      semantic: readRecord(block.semantic),
      description:
        readString(block.description) ??
        readString(packageBlock?.description) ??
        null,
      sort_order: index,
    }
  })
}

function normalizeStudioFlowContainer(value: unknown): Record<string, unknown> {
  const flows = normalizeStudioFlowMap(value)
  return Object.keys(flows).length > 0 ? { flows } : {}
}

function normalizeStudioFlowMap(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) {
    const flows: Record<string, unknown> = {}
    value
      .filter((flow): flow is Record<string, unknown> => Boolean(readRecord(flow)))
      .forEach((flow, index) => {
        const name = readString(flow.name) ?? readString(flow.id) ?? `flow-${index + 1}`
        putImportedFlow(flows, readString(flow.id) ?? name, {
          ...flow,
          name,
          steps: Array.isArray(flow.steps) ? flow.steps : [],
        })
      })
    return flows
  }

  const record = readRecord(value)
  if (!record) return {}
  if (readRecord(record.flows) || Array.isArray(record.flows)) return normalizeStudioFlowMap(record.flows)

  const flows: Record<string, unknown> = {}
  for (const [key, flowValue] of Object.entries(record)) {
    const flow = readRecord(flowValue)
    if (!flow) continue
    const name = readString(flow.name) ?? readString(flow.id) ?? key
    putImportedFlow(flows, key, {
      ...flow,
      name,
      steps: Array.isArray(flow.steps) ? flow.steps : [],
    })
  }
  return flows
}

function putImportedFlow(
  flows: Record<string, unknown>,
  preferredKey: string,
  flow: Record<string, unknown>,
): void {
  const baseKey = preferredKey.trim() || `flow-${Object.keys(flows).length + 1}`
  let key = baseKey
  let suffix = 2
  while (key in flows) {
    key = `${baseKey}__${suffix}`
    suffix += 1
  }
  flows[key] = flow
}

function summarizeBundleRows(
  manifest: unknown,
  rows: StudioProjectBundleBlock[],
): StudioProjectImportResult['summary'] {
  const manifestRecord = readRecord(manifest) ?? {}
  const pages = Array.isArray(manifestRecord.pages) ? manifestRecord.pages.length : 0
  return {
    pages,
    blocks: rows.length,
    datasourceReferences: rows.reduce((sum, row) => sum + Object.keys(readRecord(row.datasource) ?? {}).length, 0),
    webMcpDatasourceReferences: rows.reduce((sum, row) => {
      const datasource = readRecord(row.datasource) ?? {}
      return sum + Object.values(datasource).filter((entry) => readRecord(entry)?.kind === 'webmcp').length
    }, 0),
  }
}

function buildBlockRows(
  blocks: Record<string, unknown>,
  flows?: Record<string, unknown>,
): Array<Record<string, unknown>> {
  const rows = Object.entries(blocks).map(([blockName, block], i) => {
    const b = block as any
    const canonicalUi = readRecord(b.ui) ?? {}
    const inputFlow = readRecord(b.flow)
    if (
      inputFlow &&
      Object.prototype.hasOwnProperty.call(inputFlow, PROJECT_FLOW_PARTITION_KEY)
    ) {
      // Only this persistence adapter may mint the project-flow partition.
      // Reject an impersonating marker on every input block even when no
      // application-level `flows` argument was supplied; otherwise a later
      // block could smuggle project authority into a subsequent readback.
      throw new Error(`Block flow uses reserved persistence key ${PROJECT_FLOW_PARTITION_KEY}`)
    }
    let flow = b.flow ?? {}
    if (flow && typeof flow === 'object' && !flow.flows) {
      const hasFlowDefs = Object.values(flow).some(
        (v: any) => v && typeof v === 'object' && ('steps' in v || 'trigger' in v),
      )
      if (hasFlowDefs) {
        flow = { flows: flow }
      }
    }
    return {
      block_name: blockName,
      // The v2 BlockDef owns these fields under `ui`. Flat fields are a
      // LoadedApp compatibility facade only, so a nested value must win when
      // both shapes are present. Persist exactly the three canonical ui-column
      // fields and derive the flat aliases from them again on readback.
      ui: {
        schemaVersion: canonicalUi.schemaVersion ?? b.schemaVersion ?? SCHEMA_VERSION,
        blockType: canonicalUi.blockType ?? b.blockType ?? blockName,
        root: canonicalUi.root ?? b.root,
      },
      flow,
      variables: b.state ?? b.variables ?? {},
      events: b.events ?? {},
      datasource: b.data ?? b.datasource ?? {},
      manifest: b.meta ?? b.manifest ?? {},
      sort_order: i,
    }
  })

  if (flows !== undefined && rows.length > 0) {
    const firstBlockFlow = readRecord(rows[0].flow) ?? {}
    rows[0].flow = {
      ...firstBlockFlow,
      [PROJECT_FLOW_PARTITION_KEY]: {
        schemaVersion: PROJECT_FLOW_PARTITION_SCHEMA_VERSION,
        flows,
      },
    }
  }

  return rows
}

/**
 * `studio_blocks.flow` reaches this reader in two persisted shapes, and only
 * one of them is a record:
 *
 *   - keyed record — `{ [flowName]: def }` or `{ flows: { [flowName]: def } }`,
 *     what the Studio editor's own saves write.
 *   - JSON array — `[ { id, name, trigger, steps, ... }, ... ]`, what the app
 *     converter writes and what `GET /api/projects/:id/full` serves straight
 *     back (measured on SIPASS.co, 2026-08-24: 42/42 blocks carried a flow
 *     array, 1,950 flow items in total).
 *
 * Both readers below funnel the column through `readRecord`, which answers
 * `null` for an array — so the array form collapsed to `{}` before the flow
 * merge ever ran and the project loaded with ZERO flows while every reader
 * reported success. Its siblings survive the identical trip only because they
 * are stored as records: `variables` goes through `normalizeBlockState` (also
 * array-blind, but never fed an array) and `datasource` is passed through
 * un-converted, so neither ever meets `readRecord`.
 *
 * The array form is reconciled to the keyed record HERE, at the single point
 * where the column enters the reader, so the partition read and the block
 * facade projection cannot drift apart. The record form is returned untouched.
 */
function flowArrayToRecord(items: unknown[]): Record<string, unknown> {
  const flows: Record<string, unknown> = {}
  items.forEach((item, index) => {
    const flow = readRecord(item)
    if (!flow) return
    // Key by the DECLARED name: `toLoadedApp`'s merge, flow-ownership routing
    // and event bindings all address a flow by name, so the array index must
    // never become the key (it did for the `{ flows: [...] }` shape below).
    const key = readString(flow.name) ?? readString(flow.id) ?? `flow-${index + 1}`
    putImportedFlow(flows, key, flow)
  })
  return flows
}

function normalizeStoredBlockFlow(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) return flowArrayToRecord(value)
  const record = readRecord(value)
  if (!record) return {}
  // `buildBlockRows` wraps a non-`flows` flow value as `{ flows: <value> }`, so
  // persisting an array-shaped block yields `{ flows: [...] }` on the next read.
  if (Array.isArray(record.flows)) {
    return { ...record, flows: flowArrayToRecord(record.flows) }
  }
  return record
}

/**
 * Flow-like entries the RAW stored column carries, counted before any
 * normalization or merge can drop one. This is the "non-empty source" half of
 * the silent-empty guard below: it is read off `b.flow` exactly as the server
 * sent it, so a reader that stops understanding a shape cannot also quietly
 * revise the count that would have exposed it.
 */
function countStoredBlockFlowItems(value: unknown): number {
  if (Array.isArray(value)) return value.length
  const record = readRecord(value)
  if (!record) return 0
  if (Array.isArray(record.flows)) return record.flows.length
  const map = readRecord(record.flows) ?? record
  // `_`-prefixed keys are metadata (`_diagram`, the reserved partition), never
  // executable flows — the merge skips them, so they are not "source" either.
  return Object.keys(map).filter((key) => !key.startsWith('_')).length
}

/** Names the stored shape in a warning, so a future mismatch is diagnosable. */
function describeStoredFlowShape(value: unknown): string {
  if (Array.isArray(value)) return `array(${value.length})`
  const record = readRecord(value)
  if (!record) return typeof value
  if (Array.isArray(record.flows)) return `{ flows: array(${record.flows.length}) }`
  if (readRecord(record.flows)) return `{ flows: record }`
  return 'record'
}

function readProjectFlowPartition(value: unknown): Record<string, unknown> | undefined {
  const flow = normalizeStoredBlockFlow(value)
  if (!Object.prototype.hasOwnProperty.call(flow, PROJECT_FLOW_PARTITION_KEY)) return undefined
  const partition = readRecord(flow[PROJECT_FLOW_PARTITION_KEY]) ?? {}
  if (
    partition.schemaVersion !== PROJECT_FLOW_PARTITION_SCHEMA_VERSION ||
    !readRecord(partition.flows)
  ) {
    // A malformed reserved partition must not silently replace the legacy
    // block-owned flow projection. Treat it as absent; receipt-bound readback
    // will still fail its exact hash comparison.
    return undefined
  }
  return partition.flows as Record<string, unknown>
}

function stripProjectFlowPartition(value: unknown): Record<string, unknown> {
  const flow = normalizeStoredBlockFlow(value)
  if (!Object.prototype.hasOwnProperty.call(flow, PROJECT_FLOW_PARTITION_KEY)) return flow
  return Object.fromEntries(
    Object.entries(flow).filter(([key]) => key !== PROJECT_FLOW_PARTITION_KEY),
  )
}

function normalizeBlockState(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}

  const state = value as Record<string, unknown>
  if ('state' in state || 'computed' in state || 'context' in state) {
    return state
  }

  if ('localState' in state && state.localState && typeof state.localState === 'object' && !Array.isArray(state.localState)) {
    return { state: state.localState }
  }

  return { state }
}

/**
 * Silent-empty guard for `studio_blocks.variables`, the sibling column that
 * survived the flow defect (#2494) only by luck of its stored shape.
 *
 * `normalizeBlockState` is array-blind in exactly the way `readRecord` was:
 * `Array.isArray(value)` → `{}`. Unlike `flow`, an array here is a shape NO
 * writer produces, so it is NOT normalized — it is reported:
 *
 *   - no writer CONSTRUCTS one. The source converter that does emit the flow
 *     array (`@cogo/convert` — `source-projector.ts` writes
 *     `flow: block.flow` as a `SourceActionFlow[]`) writes this column as a
 *     record from `buildBlockVariables`: `{ state: { [name]: {...} } }`. The
 *     generation lane's `rebuildFinalGeneratedStateAndMeta` / `compileV2`
 *     both return `Record<string, unknown>`; the import lanes coerce
 *     (`readRecord(block.variables) ?? {}`); canonical templates and the
 *     Studio store write `{ localState: {...} }` / `{ state: {...} }`; v4
 *     `variables.cogo.schema.json` declares an object. Measured over the
 *     in-repo corpus (2026-08-24): 210/210 app-bundle blocks and 23/23
 *     exported `studio_blocks` rows store a record — zero arrays — against 81
 *     array-shaped `flow` columns in the same scan.
 *   - but an array is STORABLE, which is why silence is not safe. Both row
 *     writers pass the column through un-converted (`variables: b.state ??
 *     b.variables ?? {}` — client `buildBlockRows` above and its pinned port
 *     `buildStudioBlockRows` in the worker), the block PUT/PATCH routes
 *     allowlist the key without checking its JSON type, and the column is a
 *     bare `JSONB DEFAULT '{}'` with no CHECK constraint. That is the exact
 *     pass-through that let `flow` arrive here as an array.
 *
 * So a non-record here means the reader has met an input it cannot read. It
 * must say so with the field name and the shape it found rather than fold it
 * into an empty default: normalizing a shape no writer declares would be
 * guessing, and staying silent is the failure that hid #2494 for a whole
 * customer demo.
 */
function countStoredBlockVariableItems(value: unknown): number {
  if (Array.isArray(value)) return value.length
  const record = readRecord(value)
  if (!record) return 0
  // The declaration map lives under `localState` (v4 authoring) or `state`
  // (v3 / already-normalized) when either is present as a record; otherwise
  // the column itself IS the map.
  const inner = readRecord(record.localState) ?? readRecord(record.state)
  return Object.keys(inner ?? record).length
}

/** Mirror of the count above, applied to what `normalizeBlockState` produced. */
function countLoadedBlockVariableItems(state: Record<string, unknown>): number {
  const inner = readRecord(state.state)
  return Object.keys(inner ?? state).length
}

/** Names the stored shape in a warning, so a future mismatch is diagnosable. */
function describeStoredVariableShape(value: unknown): string {
  if (Array.isArray(value)) return `array(${value.length})`
  if (value === null) return 'null'
  const record = readRecord(value)
  if (!record) return typeof value
  if (Array.isArray(record.localState)) return `{ localState: array(${record.localState.length}) }`
  if (Array.isArray(record.state)) return `{ state: array(${record.state.length}) }`
  return 'record'
}

/**
 * `studio_blocks.ui` is the third array-blind reader in this load path, and
 * the quietest of the three: `readRecord(b.ui) ?? {}` answers `{}` for an
 * array, so `root` is `undefined` and the block renders EMPTY with nothing
 * missing from any count — no `flowCount 0` to notice.
 *
 * Like `variables`, an array is a shape nothing writes DELIBERATELY: both row
 * writers build this column as an object literal
 * `{ schemaVersion, blockType, root }` (`buildBlockRows` above and
 * `buildStudioBlockRows` in the worker, a pinned port of it), the canonical
 * template route hard-rejects a non-record (`canonical-block-ui-invalid`), the
 * import lanes coerce with `readRecord(row.ui) ?? {}`, and 259/259 in-repo
 * app-bundle blocks store a record — every one of them carrying a `root`.
 *
 * It is nevertheless REACHABLE, which is why the answer is loudness and not
 * silence: the collaboration route's simple-edit apply
 * (cogo-platform `routes/project-collaboration.ts`) takes an untyped
 * `SimpleEditBody.block`, and its patch visitor preserves array-ness
 * (`if (Array.isArray(value)) return value.map(visit)`) before PATCHing the
 * result straight into this column. An array that arrives that way is a
 * damaged envelope, not an alternative encoding — nothing in it says which
 * member is the root — so there is no normalization to write, only a reader
 * that must not swallow an input it cannot read.
 */
function describeStoredUiShape(value: unknown): string {
  if (Array.isArray(value)) return `array(${value.length})`
  if (value === null) return 'null'
  return typeof value
}

/**
 * `manifest.pages` is required to be an ARRAY by the reader below; every
 * other shape falls through to the legacy `studio_pages` projection and, when
 * that is empty, the project loads with `pages: []` and no word said.
 *
 * A non-array is again a shape nothing writes — of the writers that emit this
 * field, EVERY one emits an array: the worker's `buildManifest`
 * (`pages: pagesWithLayout` off the spec's page array), the source
 * converter's `app-package.ts`, `createProject`'s default
 * `[{ id: 'home', … }]`, the auto-save re-save of the loaded manifest, the
 * seed/verify scripts. `app.cogo.schema.json` declares `pages` as
 * `{"type": "array", "minItems": 1}`, the canonical-template route
 * hard-rejects a non-array (`canonical-template-invalid`), and 19/19 in-repo
 * manifests carrying `pages` store an array.
 *
 * It stays reachable because `PATCH /api/projects/:id` writes `manifest`
 * byte-for-byte (only `settings` gets a shape check) and `importProjectBundle`
 * coerces the manifest's top level but never inspects `pages` inside it. And
 * a record here is not merely lossy downstream — `build-mode-canvas` does
 * `(manifest.pages ?? []).some(…)`, which THROWS on one. So this reader's
 * silent `[]` is what turns a reachable bad write into an app with no screens
 * and no explanation.
 *
 * An ABSENT `pages` is the legitimate legacy case the fallback exists for,
 * and an explicitly persisted empty array is an intentional empty — neither
 * is reported.
 */
function describeStoredPagesShape(value: unknown): string {
  if (Array.isArray(value)) return `array(${value.length})`
  if (value === null) return 'null'
  const record = readRecord(value)
  if (record) return `record(${Object.keys(record).length} key(s))`
  return typeof value
}

/**
 * Page entries the RAW row carries, counted before the branch below can drop
 * one — the same discipline as `countStoredBlockFlowItems`: the count that
 * would expose a reader defect is never computed by the reader whose defect
 * it would expose. An array `manifest.pages` is authoritative on its own (the
 * fallback is deliberately not consulted), so `studio_pages` joins the count
 * only when the manifest partition is absent or unreadable.
 */
function countStoredProjectPageEntries(manifestPages: unknown, studioPages: unknown): number {
  if (Array.isArray(manifestPages)) return manifestPages.length
  const legacy = Array.isArray(studioPages) ? studioPages.length : 0
  if (manifestPages === undefined || manifestPages === null) return legacy
  const record = readRecord(manifestPages)
  return (record ? Object.keys(record).length : 1) + legacy
}

/**
 * Deterministic studio_blocks order. PostgREST embedded-row order is
 * unspecified and can change after row UPDATEs (auto-save PATCHes blocks
 * constantly), but "first declarer" flow ownership — the reload merge in
 * toLoadedApp, the auto-save owner routing (flow-ownership.ts iterating
 * project.blocks), and blockIdMap — all key off block position. Sort by the
 * explicit sort_order column (same convention as studio_pages), then
 * created_at, then id so ownership cannot flip between loads/sessions.
 */
function sortStudioBlocks(rows: Array<Record<string, any>>): Array<Record<string, any>> {
  return rows.slice().sort((a, b) =>
    ((a.sort_order ?? 0) - (b.sort_order ?? 0))
    || String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
    || String(a.id ?? '').localeCompare(String(b.id ?? '')),
  )
}

// Exported for tests: the flow merge below must stay consistent with the
// auto-save owner routing (flow-ownership.ts) for save→reload round-trips.
export function toLoadedApp(row: any): LoadedApp {
  const blocks: Record<string, any> = {}
  const allFlows: Record<string, any> = {}
  let persistedProjectFlows: Record<string, unknown> | undefined
  const settings = readRecord(row.settings) ?? {}
  const sourceOntology = settings.cogoApplicationOntology
  const sourceMetadata =
    readRecord(settings.cogoApplicationMetadata)
    ?? readRecord(readRecord(row.manifest)?.metadata)
    ?? {}
  const sourceApplicationSupplement = settings.cogoApplicationSupplement
  const studioProjectId = readString(row.id)
  const ontologyReleaseSelection = readProjectOntologyReleaseSelection(row)
  // flowName → block that first declared it (the served copy's block)
  const flowDeclarers: Record<string, string> = {}
  const recoverableFlows: Record<string, { ownerBlock: string; shadowBlock: string; steps: any[] }> = {}
  let flowDiagram: Record<string, any> | undefined
  // Total flow items the SERVER sent across all blocks (raw column count).
  let storedFlowItems = 0

  // Map of block UUID -> block_name
  const blockNameMap: Record<string, string> = {}

  const sortedBlocks = sortStudioBlocks(row.studio_blocks ?? [])
  for (const [blockIndex, b] of sortedBlocks.entries()) {
    blockNameMap[b.id] = b.block_name
    const ui = readRecord(b.ui) ?? {}
    if (b.ui !== undefined && b.ui !== null && !readRecord(b.ui)) {
      // Silent-empty guard for the quietest of the three readers: nothing is
      // missing from a count here, the screen is simply blank.
      console.warn(
        `[toLoadedApp] block "${b.block_name}" stores a \`ui\` column in a shape this ` +
        `reader cannot read (${describeStoredUiShape(b.ui)}) — the persisted form is the ` +
        `{ schemaVersion, blockType, root } record both row writers build. The block will ` +
        `render EMPTY (no ui.root); the ui is still on the server.`,
      )
    }
    const schemaVersion = ui.schemaVersion ?? SCHEMA_VERSION
    const blockType = ui.blockType ?? b.block_name
    // The project partition has exactly one authority location: the first
    // deterministic studio_blocks row. Markers on every other row are stripped
    // from the block facade below but never decoded, so a compromised/later
    // block cannot replace application-level flows.
    if (blockIndex === 0) {
      persistedProjectFlows = readProjectFlowPartition(b.flow)
    }
    const blockFlow = stripProjectFlowPartition(b.flow)
    const blockSupplement = receiptBoundBlockSupplement(
      sourceApplicationSupplement,
      b.block_name,
    )
    // Map DB fields → hybrid format supporting both access patterns:
    //   block.ui.root   (BlockDef — template-loader, selection-slice, use-usage-index)
    //   block.root       (CogoBlockDef — auto-save-bridge, studio-bridge, cross-reference)
    blocks[b.block_name] = {
      ...blockSupplement.block,
      // BlockDef fields (nested)
      meta: b.manifest ?? { name: b.block_name, type: 'ui', version: '1.0.0', description: '', stateContract: { localState: {}, inputSymbols: [], outputSymbols: [] } },
      ui: { ...blockSupplement.ui, schemaVersion, blockType, root: ui.root },
      events: b.events ?? {},
      flow: blockFlow,
      state: normalizeBlockState(b.variables),
      data: b.datasource ?? {},
      // CogoBlockDef fields (flat — backward compat)
      schemaVersion,
      blockType,
      root: ui.root,
      variables: normalizeBlockState(b.variables),
      datasource: b.datasource ?? {},
      manifest: b.manifest ?? {},
    }

    // Silent-empty guard for the sibling column: what the SERVER sent for this
    // block's variables, counted off the raw column, against what the loaded
    // block actually exposes. Both numbers come off values this reader did not
    // normalize, so a reader that stops understanding a shape cannot also
    // quietly revise the count that would expose it.
    const storedVariableItems = countStoredBlockVariableItems(b.variables)
    if (
      storedVariableItems > 0 &&
      countLoadedBlockVariableItems(normalizeBlockState(b.variables)) === 0
    ) {
      console.warn(
        `[toLoadedApp] block "${b.block_name}" stores ${storedVariableItems} variable ` +
        `declaration(s) but the loader could read NONE of them — the stored shape is one ` +
        `this reader does not understand (${describeStoredVariableShape(b.variables)}). ` +
        `Those variables are absent from the loaded block; the project has NOT lost them ` +
        `on the server.`,
      )
    }

    // Collect flows from each block's flow field
    // Supports both { flows: { name: def } } (wrapped) and { name: def } (flat/unwrapped)
    const flowData = blockFlow
    // Silent-empty guard: what the SERVER sent for this block, counted off the
    // raw column. A block that shipped flow items but contributes none is the
    // exact failure that hid this defect (`success: true` with `data: {}`), so
    // it has to be said out loud rather than folded into a zero.
    const storedItems = countStoredBlockFlowItems(b.flow)
    storedFlowItems += storedItems
    let readableItems = 0
    if (flowData) {
      const flowMap = flowData.flows ?? flowData
      for (const [flowName, flowDef] of Object.entries(flowMap)) {
        if (flowName.startsWith('_')) continue // skip _diagram etc.
        if (flowDef && typeof flowDef === 'object' && ('steps' in (flowDef as object) || 'trigger' in (flowDef as object))) {
          readableItems += 1
          // Ownership-aware merge: the FIRST block declaring a flow name owns
          // it — the same rule the auto-save bridge uses to route flow saves
          // (flow-ownership.ts) and the Behavior surface uses to dedupe its
          // flow list. The previous later-block-wins merge let a stale
          // same-named copy on a later block silently clobber the owner's
          // saved edits on reload (F58).
          if (!(flowName in allFlows)) {
            allFlows[flowName] = flowDef
            flowDeclarers[flowName] = b.block_name
          } else {
            // Detection for projects corrupted by the pre-fix pipeline (F50):
            // it wrote a gutted copy of every flow to the first block, so the
            // served first-declarer copy can be empty while the intact copy
            // sits shadowed on a later block. Warn instead of auto-recovering
            // (operator decision — heuristics could mask real empty flows);
            // the intact copy stays in the DB for manual recovery.
            const servedSteps = (allFlows[flowName] as { steps?: unknown[] }).steps
            const shadowedSteps = (flowDef as { steps?: unknown[] }).steps
            if (
              (!Array.isArray(servedSteps) || servedSteps.length === 0) &&
              Array.isArray(shadowedSteps) && shadowedSteps.length > 0
            ) {
              console.warn(
                `[toLoadedApp] flow "${flowName}" is served from block "${flowDeclarers[flowName]}" with EMPTY steps, ` +
                `but a shadowed copy on block "${b.block_name}" has ${shadowedSteps.length} step(s). ` +
                `The served copy may be a corrupted artifact of the pre-fix auto-save pipeline (F50); ` +
                `the flow editor offers an explicit Recover affordance.`,
              )
              // Recorded for the EXPLICIT recovery affordance (FlowStepPanel).
              // Serving stays first-declarer (no auto-recovery heuristics —
              // a deliberately emptied flow must not silently resurrect);
              // first detection wins if several shadows exist.
              if (!(flowName in recoverableFlows)) {
                recoverableFlows[flowName] = {
                  ownerBlock: flowDeclarers[flowName],
                  shadowBlock: b.block_name,
                  steps: shadowedSteps,
                }
              }
            }
          }
        }
      }
      if (flowData._diagram) {
        flowDiagram = { ...(flowDiagram ?? {}), ...flowData._diagram }
      }
    }
    if (storedItems > 0 && readableItems === 0) {
      console.warn(
        `[toLoadedApp] block "${b.block_name}" stores ${storedItems} flow item(s) but the ` +
        `loader could read NONE of them — the stored shape is one this reader does not ` +
        `understand (${describeStoredFlowShape(b.flow)}). Those flows are absent from the ` +
        `loaded project; the project has NOT lost them on the server.`,
      )
    }
  }

  // Current Studio page editing and whole-project generation persist the page
  // partition in manifest.pages. studio_pages is a legacy projection that can
  // remain populated after a manifest replacement, so it must not override an
  // explicitly persisted manifest array (including an intentional empty one).
  // Fall back to studio_pages only for legacy projects with no manifest pages
  // partition at all.
  let pages = []
  const storedManifestPages = readRecord(row.manifest)?.pages
  const storedPageEntries = countStoredProjectPageEntries(storedManifestPages, row.studio_pages)
  if (Array.isArray(storedManifestPages)) {
    pages = storedManifestPages.map((p: any) => ({
      ...p,
      blocks: p.blocks ?? [], // Guarantee blocks array is present (no undefined)
    }))
  } else if (row.studio_pages && row.studio_pages.length > 0) {
    pages = row.studio_pages
      .slice()
      .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((p: any) => ({
        id: p.page_name,
        route: p.route,
        blocks: (p.block_ids ?? []).map((id: string) => blockNameMap[id]).filter(Boolean),
        layout: (typeof p.layout === 'string' && p.layout) ? p.layout : 'flow',
        blockLayout: p.block_layout ?? {},
      }))
  }
  if (
    storedManifestPages !== undefined &&
    storedManifestPages !== null &&
    !Array.isArray(storedManifestPages)
  ) {
    console.warn(
      `[toLoadedApp] project "${row.id}" stores manifest.pages in a shape this reader ` +
      `cannot read (${describeStoredPagesShape(storedManifestPages)}) — the persisted form ` +
      `is an array of page records (app.cogo.schema.json: pages is type "array"). The ` +
      `manifest page partition was NOT applied; the loader fell back to the legacy ` +
      `studio_pages projection (${Array.isArray(row.studio_pages) ? row.studio_pages.length : 0} row(s)).`,
    )
  }

  const canonicalProjectName =
    typeof row.manifest?.name === 'string' && row.manifest.name.trim().length > 0
      ? row.manifest.name
      : typeof row.name === 'string' && row.name.trim().length > 0
        ? row.name
        : undefined
  const manifest = {
    ...(row.manifest ?? {}),
    ...(canonicalProjectName ? { name: canonicalProjectName } : {}),
    pages,
  }

  // Project-level silent-empty guard. A project whose blocks shipped flow items
  // must never load with an empty flow map and no word said about it — that is
  // precisely how a whole application's 1,950 ActionFlows reached a customer
  // demo as `flowCount 0` with `getFlows()` answering `success: true`.
  // Same guard for the page list. A project that shipped page entries and
  // loads with none has lost its whole navigable surface; `pages: []` reaches
  // the preview, the page rail and the build canvas as "this app has no
  // screens", which is indistinguishable from a genuinely empty project.
  if (storedPageEntries > 0 && pages.length === 0) {
    console.warn(
      `[toLoadedApp] project "${canonicalProjectName ?? row.id}" loaded with ZERO pages while the row carries ` +
      `${storedPageEntries} page entr(y/ies). This is a reader defect, not an empty ` +
      `project — the pages are still on the server.`,
    )
  }

  const projectFlows = persistedProjectFlows ?? allFlows
  if (storedFlowItems > 0 && Object.keys(projectFlows).length === 0) {
    console.warn(
      `[toLoadedApp] project "${canonicalProjectName ?? row.id}" loaded with ZERO flows while ` +
      `its blocks store ${storedFlowItems} flow item(s). This is a reader defect, not an ` +
      `empty project — the flows are still on the server.`,
    )
  }

  return {
    manifest,
    blocks,
    data: {},
    ...receiptBoundApplicationSupplement(sourceApplicationSupplement),
    // New whole-project writes persist this partition independently from
    // block-local declarations. Legacy rows have no marker and retain the
    // established first-declarer reconstruction.
    flows: projectFlows,
    tokens: row.tokens,
    flowDiagram,
    ...(ontologyReleaseSelection ? { ontologyReleaseSelection } : {}),
    ...(sourceOntology !== undefined ? { ontology: sourceOntology } : {}),
    ...(sourceOntology !== undefined || Object.keys(sourceMetadata).length > 0 || studioProjectId
      ? {
          metadata: {
            ...sourceMetadata,
            ...(studioProjectId ? { studioProjectId } : {}),
            ...(sourceOntology !== undefined ? { cogoApplicationOntology: sourceOntology } : {}),
          },
        }
      : {}),
    ...(Object.keys(recoverableFlows).length > 0 ? { recoverableFlows } : {}),
  }
}
